// Knowledge Base Condition Data
// This file contains all the educational content for each condition
// Extracted from ConditionDetail.js for maintainability

export 
const conditionData = {
  'pandas-pans': {
    name: 'PANDAS/PANS',
    color: 'bg-primary',
    lightColor: 'bg-primary/10',
    textColor: 'text-primary',
    description: 'Pediatric Autoimmune Neuropsychiatric Disorders Associated with Streptococcal Infections (PANDAS) and Pediatric Acute-onset Neuropsychiatric Syndrome (PANS)',
    about: {
      overview: `PANDAS (Pediatric Autoimmune Neuropsychiatric Disorders Associated with Streptococcal Infections) and PANS (Pediatric Acute-onset Neuropsychiatric Syndrome) are conditions where the immune system mistakenly attacks parts of the brain, leading to sudden and dramatic changes in behavior, cognition, and emotional regulation.

**The Science Behind It:**

PANDAS and PANS are examples of autoimmune encephalitis - inflammation of the brain caused by the body's own immune system. When a child is exposed to certain infections (particularly Group A Streptococcus in PANDAS), the immune system produces antibodies to fight the infection. In susceptible individuals, these antibodies cross-react with brain tissue through a process called molecular mimicry.

**Molecular Mimicry Explained:**

The surface proteins on certain bacteria (like strep) share structural similarities with proteins found in the basal ganglia - a region of the brain responsible for movement, behavior, and emotion regulation. When antibodies are created to attack the bacteria, they also mistakenly attack these brain tissues because they "look" similar. This is like friendly fire in the immune system.

**The Basal Ganglia:**

The basal ganglia is a group of structures deep in the brain that control:
• Motor control and movement coordination
• Procedural learning and habit formation
• Emotion regulation
• Impulse control and decision making
• Cognitive processing

When inflammation occurs in this area, it disrupts these critical functions, leading to the dramatic behavioral and neurological symptoms seen in PANDAS/PANS.

**PANDAS vs PANS:**

PANDAS:
• Specifically triggered by Group A Streptococcal (GAS) infections
• Temporal relationship between strep infection and symptom onset must be documented
• Often involves anti-streptococcal antibodies (ASO, Anti-DNase B)
• May have repeated flares with subsequent strep exposures

PANS:
• Broader diagnostic criteria
• Can be triggered by various infections (mycoplasma, influenza, mononucleosis, Lyme disease)
• Can be triggered by environmental factors, metabolic issues, or unknown causes
• Does not require documented infection, just acute onset of symptoms

**Key Diagnostic Criteria for PANDAS/PANS:**

1. Abrupt, dramatic onset of OCD or severely restricted food intake
2. Concurrent presence of at least 2 additional neuropsychiatric symptoms:
   - Anxiety (particularly separation anxiety)
   - Emotional lability or depression
   - Irritability, aggression, or oppositional behaviors
   - Behavioral regression
   - Deterioration in school performance
   - Sensory or motor abnormalities
   - Somatic signs (sleep disturbance, urinary frequency, etc.)

**The Autoimmune Process:**

1. Infection occurs - Body is exposed to triggering pathogen
2. Immune response - B-cells produce antibodies against the pathogen
3. Cross-reactivity - Some antibodies also recognize brain tissue
4. Brain inflammation - Antibodies cross blood-brain barrier and attack neurons
5. Symptom onset - Inflammation disrupts normal brain function
6. Cytokine storm - Pro-inflammatory chemicals amplify the response
7. Ongoing inflammation - Without treatment, inflammation can persist

**Why Does This Happen?**

Genetic susceptibility plays a significant role. Children with PANDAS/PANS often have:
• Family history of autoimmune conditions
• Genetic variations in immune regulation genes
• Altered blood-brain barrier permeability
• Differences in immune system regulation
• Higher baseline inflammation

**It's NOT:**

❌ A behavioral problem or parenting issue
❌ A mental health disorder caused by trauma
❌ Something the child can control or "snap out of"
❌ A progressive degenerative condition (with proper treatment, children can recover)
❌ Contagious (though the triggering infections may be)

**The Good News:**

With proper diagnosis and treatment, many children show significant improvement or full recovery. Early intervention is key. The brain has remarkable healing capacity when inflammation is reduced and proper support is provided.`,
      keyPoints: [
        'Autoimmune condition affecting the brain (specifically the basal ganglia)',
        'Characterized by sudden, dramatic onset of symptoms',
        'PANDAS is specifically triggered by streptococcal infections',
        'PANS can be triggered by various infections or environmental factors',
        'Symptoms often wax and wane in a relapsing-remitting pattern',
        'Early diagnosis and treatment are crucial for best outcomes'
      ],
      prevalence: 'Estimated 1 in 200 children may be affected, though many go undiagnosed',
      ageOfOnset: 'Typically between ages 3-12, though can occur at any age'
    },
    symptoms: {
      overview: `**Primary Symptoms (Must Have at Least One):**`,
      primary: [
        { 
          name: 'Obsessive-Compulsive Disorder (OCD)', 
          description: 'The OCD seen in PANDAS/PANS is often severe and sudden. Unlike typical childhood OCD which develops gradually, PANS OCD appears "overnight" and can be debilitating.',
          details: [
            'Contamination fears - extreme fear of germs, refusing to touch things, excessive handwashing until hands bleed',
            'Intrusive thoughts - disturbing thoughts about harm, violence, or inappropriate content',
            'Counting/arranging rituals - needing things to be "just right," specific numbers, symmetry',
            'Checking compulsions - repeatedly checking locks, appliances, homework',
            'Confessing compulsions - need to tell parents every thought or action',
            'Reassurance seeking - constantly asking "am I okay?" "did I do it right?"',
            'Scrupulosity - excessive religious or moral worries'
          ]
        },
        { 
          name: 'Restricted Eating', 
          description: 'This goes beyond picky eating and can lead to dangerous weight loss:',
          details: [
            'Fear of swallowing or choking (phagophobia)',
            'Fear of vomiting (emetophobia)',
            'Extreme texture sensitivities appearing suddenly',
            'Refusing all but 2-3 "safe" foods',
            'Fear of food contamination',
            'Eating rituals (certain order, specific utensils only)',
            'May progress to anorexia-like presentation'
          ]
        }
      ],
      secondaryOverview: `**Secondary Symptoms (Must Have 2+ Concurrent):**`,
      secondary: [
        { 
          name: 'Anxiety Manifestations', 
          description: '',
          details: [
            'Separation anxiety - unable to leave parent\'s side, panic when separated',
            'Generalized anxiety - constant worry about everything',
            'Panic attacks - heart racing, can\'t breathe, feeling of impending doom',
            'Social anxiety - sudden refusal to see friends, avoidance of social situations',
            'Specific phobias appearing suddenly (dark, animals, storms)'
          ]
        },
        { 
          name: 'Emotional Dysregulation', 
          description: '',
          details: [
            'Rapid mood swings - happy to rage within seconds',
            'Inconsolable crying episodes',
            'Extreme irritability - "walking on eggshells"',
            'Emotional storms - meltdowns lasting hours',
            'Personality changes - "this isn\'t my child"',
            'Loss of joy in previously loved activities',
            'Flat affect or emotional numbness'
          ]
        },
        { 
          name: 'Behavioral Changes', 
          description: '',
          details: [
            'Oppositional defiance - refusing all requests, arguing constantly',
            'Aggression - physical violence toward self or others (very out of character)',
            'Regression - baby talk, wanting bottles, loss of independence',
            'Clinginess - cannot be alone even for bathroom',
            'Sleep disturbances - night terrors, insomnia, sleeping with parents',
            'Hyperactivity or ADHD-like symptoms appearing suddenly'
          ]
        },
        { 
          name: 'Cognitive Changes', 
          description: '',
          details: [
            'Brain fog - difficulty thinking clearly',
            'Memory problems - forgetting recently learned information',
            'Executive function decline - inability to organize, plan, or sequence tasks',
            'Processing speed slows - taking much longer to complete work',
            'Handwriting deterioration - may become illegible',
            'Math skills decline - calculation errors',
            'Reading comprehension drops'
          ]
        },
        { 
          name: 'Sensory Processing Issues', 
          description: '',
          details: [
            'Tactile sensitivities - tags in clothes unbearable, textures intolerable',
            'Auditory sensitivity - covering ears at normal sounds',
            'Visual sensitivity - bothered by lights',
            'Olfactory changes - smell sensitivities or hallucinations',
            'Feeling textures that aren\'t there'
          ]
        },
        { 
          name: 'Motor/Movement Changes', 
          description: '',
          details: [
            'Tics (motor) - eye blinking, facial grimacing, head jerking, shoulder shrugging',
            'Tics (vocal) - throat clearing, sniffing, grunting, words or phrases',
            'Handwriting changes - sudden deterioration in fine motor skills',
            'Clumsiness - bumping into things, dropping items',
            'Choreiform movements - brief, irregular, jerky movements',
            'Decline in athletic ability'
          ]
        },
        { 
          name: 'Somatic/Physical Symptoms', 
          description: '',
          details: [
            'Urinary urgency/frequency - sudden need to urinate constantly, sometimes bedwetting',
            'Dilated pupils',
            'Sleep disturbances - difficulty falling asleep, frequent waking, nightmares/night terrors',
            'Joint pain (may mimic growing pains)',
            'Headaches',
            'Stomachaches (especially morning)',
            'Temperature dysregulation'
          ]
        }
      ],
      hallmarkPattern: {
        title: 'The Hallmark Pattern',
        description: 'What distinguishes PANDAS/PANS from other conditions is:',
        points: [
          'Sudden onset - Symptoms appear dramatically, often within 24-72 hours',
          'Severity - Symptoms are intense and debilitating',
          'Multiple domains - Affects behavior, cognition, and physical functioning simultaneously',
          'Out of character - Behaviors are completely unlike the child\'s baseline',
          'Relapsing-remitting - Symptoms may wax and wane with infections',
          'Interference with function - Child cannot attend school, leave house, or function normally'
        ]
      },
      ageOfOnset: {
        title: 'Age of Onset',
        points: [
          'Most commonly presents between ages 3-13',
          'Peak onset is around age 7-8',
          'Can occur in adolescence but less common',
          'Very rare in adults (though adults may have undiagnosed PANS from childhood)'
        ]
      },
      importantNote: 'Not every child will have every symptom. The presentation is highly variable. Some children have primarily OCD, others primarily anxiety and behavioral regression. The key is the acute onset and multiple neuropsychiatric symptoms appearing together.'
    },
    triggers: {
      tabLabel: 'Triggers/Flare Ups',
      isExtended: true,
      whatIsFlare: {
        title: 'What is a Flare-Up?',
        description: 'A flare-up (also called exacerbation or relapse) is when symptoms suddenly worsen or return after a period of improvement. This occurs when the immune system is reactivated, causing renewed inflammation in the brain.'
      },
      flareCycle: {
        title: 'The Flare Cycle',
        steps: [
          'Trigger exposure - Child encounters infection, stressor, or other trigger',
          'Immune activation - Body mounts immune response',
          'Antibody production - Antibodies are created (including cross-reactive ones)',
          'Brain inflammation - Antibodies attack basal ganglia',
          'Symptom escalation - Behaviors and symptoms worsen over 24-72 hours',
          'Peak symptoms - Most severe symptoms (may last days to weeks)',
          'Plateau - Symptoms remain elevated',
          'Gradual improvement - As infection resolves and inflammation decreases (if treated)'
        ]
      },
      commonTriggers: [
        {
          category: 'Infectious Triggers',
          items: [
            { name: 'Group A Streptococcus (GAS)', details: ['Most documented PANDAS trigger', 'Can occur without sore throat', 'Carrier state can cause ongoing flares', 'Siblings/family members can pass strep asymptomatically'] },
            { name: 'Mycoplasma pneumoniae', details: ['Common "walking pneumonia" bacteria', 'Often causes respiratory symptoms', 'Can be chronic/persistent infection', 'May require extended antibiotic treatment'] },
            { name: 'Viral infections', details: ['Influenza (flu)', 'Epstein-Barr virus (mononucleosis)', 'Common cold viruses', 'COVID-19', 'Coxsackie virus (hand-foot-mouth)'] },
            { name: 'Other bacterial infections', details: ['Urinary tract infections (UTI)', 'Lyme disease', 'Sinus infections'] },
            { name: 'Candida/yeast overgrowth', details: ['May occur after antibiotics'] }
          ]
        },
        {
          category: 'Environmental Triggers',
          items: [
            { name: 'Allergens', details: ['Seasonal pollen', 'Dust mites', 'Mold exposure', 'Pet dander', 'Food allergies'] },
            { name: 'Chemical exposures', details: ['Strong fragrances/perfumes', 'Cleaning products', 'Air pollution', 'Pesticides'] },
            { name: 'Mold exposure', details: ['Can cause immune activation and inflammation'] }
          ]
        },
        {
          category: 'Metabolic/Physiological Triggers',
          items: [
            { name: 'Sleep deprivation', details: ['Even one night of poor sleep can trigger'] },
            { name: 'Stress (physical or emotional)', details: ['Major life changes', 'School stress', 'Family stress', 'Medical procedures'] },
            { name: 'Hormonal changes', details: ['Puberty onset', 'Menstrual cycle (in adolescent girls)'] },
            { name: 'Nutritional deficiencies', details: ['Vitamin D deficiency', 'B vitamin deficiencies', 'Mineral deficiencies'] }
          ]
        },
        {
          category: 'Food-Related Triggers',
          items: [
            { name: 'Histamine-rich foods', details: ['In those with histamine intolerance'] },
            { name: 'Food additives', details: ['Artificial colors/dyes (especially Red 40, Yellow 5)', 'MSG', 'Preservatives'] },
            { name: 'High-sugar diet', details: ['Promotes inflammation'] },
            { name: 'Gluten (in sensitive individuals)', details: ['Can increase gut permeability'] },
            { name: 'Dairy (in sensitive individuals)', details: ['Casein can be inflammatory'] }
          ]
        }
      ],
      recognizingFlare: {
        title: 'Recognizing an Oncoming Flare',
        earlyWarning: {
          title: 'Early Warning Signs (12-48 hours before full flare)',
          signs: [
            'Subtle increase in anxiety or clinginess',
            'Disrupted sleep or nightmares',
            'Increased sensory sensitivities',
            'Mild increase in tics or repetitive behaviors',
            'Complaints of "not feeling right"',
            'Decreased frustration tolerance',
            'Appetite changes',
            'Physical complaints (headache, stomachache)',
            'Dilated pupils',
            'More emotional than usual'
          ]
        },
        fullFlare: {
          title: 'Full Flare Symptoms',
          signs: [
            'Dramatic return or worsening of OCD/anxiety',
            'Severe behavioral regression',
            'Emotional meltdowns',
            'Physical aggression (if part of child\'s pattern)',
            'Inability to function at school',
            'Severe separation anxiety',
            'Sleep completely disrupted',
            'Urinary frequency returns'
          ]
        }
      },
      flareTypes: {
        title: 'Types of Flares',
        types: [
          { name: 'Acute Flare', description: 'Sudden, severe symptom worsening. Usually triggered by new infection. May require immediate medical intervention. Can last days to weeks.' },
          { name: 'Mini-Flare', description: 'Brief worsening (24-72 hours). Less severe than full flare. May resolve on its own. Often related to minor illness or stress.' },
          { name: 'Slow-Burn Flare', description: 'Gradual increase in symptoms over weeks. May be due to chronic infection. Persistent inflammation. Requires investigation for underlying cause.' },
          { name: 'Waxing and Waning Pattern', description: 'Symptoms fluctuate day to day. "Good days" and "bad days". Often related to subclinical infections or inflammation.' }
        ]
      },
      whatToDo: {
        title: 'What to Do During a Flare',
        immediate: {
          title: 'Immediate Steps',
          steps: [
            'Document everything - timing, symptoms, potential triggers',
            'Check for infection: Strep test (throat and rapid), Consider urinalysis, Assess for viral symptoms, Check for mycoplasma if respiratory symptoms',
            'Contact your PANDAS/PANS specialist',
            'Consider symptom management: Anti-inflammatory measures, Increase immune support, Ensure adequate sleep, Reduce stress and demands'
          ]
        },
        medical: {
          title: 'Medical Interventions (Discuss with Doctor)',
          items: [
            'Antibiotics if infection confirmed',
            'Anti-inflammatory medications (NSAIDs)',
            'Steroids for severe flares (controversial, use with caution)',
            'IVIG or plasmapheresis for severe, refractory cases',
            'Temporary increase in psychiatric medications if needed'
          ]
        },
        home: {
          title: 'Home Management',
          items: [
            'Create calm, low-demand environment',
            'Maintain routines when possible',
            'Offer comfort and reassurance',
            'Don\'t punish symptoms',
            'Keep child home from school if needed',
            'Ensure adequate hydration and nutrition',
            'Use previously successful coping strategies',
            'Limit screen time (can worsen symptoms)'
          ]
        }
      },
      prevention: {
        title: 'Preventing Future Flares',
        strategies: [
          { name: 'Infection prevention', items: ['Good handwashing', 'Avoid sick contacts when possible', 'Prophylactic antibiotics (for some patients)', 'Consider tonsillectomy if recurrent strep'] },
          { name: 'Immune support', items: ['Adequate sleep (9-11 hours for children)', 'Anti-inflammatory diet', 'Regular exercise', 'Stress management', 'Appropriate supplementation'] },
          { name: 'Reduce inflammation', items: ['Omega-3 fatty acids', 'Curcumin/turmeric', 'Anti-inflammatory foods', 'Avoid trigger foods'] },
          { name: 'Address gut health', items: ['Probiotics', 'Heal leaky gut', 'Address food sensitivities'] },
          { name: 'Environmental controls', items: ['HEPA air filters', 'Mold remediation if needed', 'Reduce chemical exposures', 'Manage allergies'] }
        ]
      },
      longTermPattern: {
        title: 'Long-Term Pattern',
        points: [
          'Flares often decrease in frequency and severity over time with treatment',
          'Some children "outgrow" susceptibility as immune system matures',
          'Others need ongoing immune support and infection prevention',
          'Identifying and avoiding individual triggers is key',
          'Each child\'s pattern is unique'
        ]
      },
      flareLog: {
        title: 'Keep a Flare Log',
        description: 'This information is invaluable for identifying patterns and working with your medical team.',
        trackItems: [
          'Date symptoms began',
          'Severity (1-10 scale)',
          'Specific symptoms',
          'Potential triggers/exposures in previous 2 weeks',
          'What helped/didn\'t help',
          'How long flare lasted',
          'When symptoms returned to baseline'
        ]
      }
    },
    supplements: {
      isExtendedPandas: true,
      intro: 'These supplements support immune function, reduce inflammation, and aid recovery. Always consult with your healthcare provider before starting new supplements.',
      items: [
        {
          name: 'Omega-3 Fatty Acids (Fish Oil)',
          dosage: '1000-2000 mg EPA+DHA daily',
          evidence: 'Strong evidence',
          benefits: [
            'Powerful anti-inflammatory effects',
            'Reduces neuroinflammation in the brain',
            'Supports brain health and development',
            'May reduce behavioral symptoms',
            'Improves mood regulation',
            'Supports immune system balance'
          ],
          note: 'Use high-quality fish oil tested for purity and heavy metals. Nordic Naturals and Carlson brands are reliable. Take with meals. Higher EPA content often better for inflammation.'
        },
        {
          name: 'Vitamin D3',
          dosage: '2000-5000 IU daily (test levels, aim for 50-80 ng/mL)',
          evidence: 'Strong evidence',
          benefits: [
            'Essential for immune regulation',
            'Most PANDAS/PANS children are deficient',
            'Reduces autoimmune responses',
            'Anti-inflammatory properties',
            'Supports brain health',
            'May reduce infection frequency'
          ],
          note: 'Test levels every 3 months. Take with vitamin K2 for proper calcium metabolism. Take with fat-containing meal for best absorption. Deficiency strongly associated with autoimmune conditions.'
        },
        {
          name: 'Probiotics (specific strains)',
          dosage: '25-50 billion CFU daily, multi-strain',
          evidence: 'Good evidence',
          benefits: [
            'Support gut-brain-immune connection',
            'Reduce intestinal inflammation',
            'Support microbiome recovery after antibiotics',
            'May reduce autoimmune responses',
            'Support neurotransmitter production',
            'Strengthen gut barrier'
          ],
          note: 'Take 2 hours away from antibiotics. Continue for months after antibiotics stop. Look for strains: Lactobacillus rhamnosus, L. plantarum, Bifidobacterium species. Avoid histamine-producing strains if histamine intolerance present.'
        },
        {
          name: 'N-Acetylcysteine (NAC)',
          dosage: '600-1200 mg daily',
          evidence: 'Moderate to strong evidence',
          benefits: [
            'Powerful antioxidant',
            'Reduces oxidative stress in brain',
            'May help with OCD symptoms specifically',
            'Supports glutathione production (master antioxidant)',
            'Anti-inflammatory',
            'May help break down biofilms'
          ],
          note: 'Take on empty stomach for best absorption. Start with lower dose and increase gradually. Can cause nausea if dose too high. Well-studied for OCD - some research shows benefit.'
        },
        {
          name: 'Curcumin (Turmeric)',
          dosage: '500-1000 mg daily',
          evidence: 'Strong evidence',
          benefits: [
            'Potent anti-inflammatory',
            'Reduces neuroinflammation',
            'Crosses blood-brain barrier',
            'Antioxidant properties',
            'May reduce autoantibody formation',
            'Supports overall healing'
          ],
          note: 'MUST include piperine (black pepper extract) or use liposomal form for absorption. Take with fatty meal. Meriva or BCM-95 forms have better bioavailability.'
        },
        {
          name: 'Magnesium',
          dosage: '200-400 mg daily (age-dependent)',
          evidence: 'Moderate evidence',
          benefits: [
            'Calming effect on nervous system',
            'Reduces anxiety',
            'Improves sleep quality',
            'Anti-inflammatory',
            'Supports over 300 enzymatic processes',
            'Often deficient in children',
            'May reduce tics'
          ],
          note: 'Use magnesium glycinate for best absorption and calming effects. Give 30-60 minutes before bed. Avoid magnesium oxide (poorly absorbed, causes diarrhea).'
        },
        {
          name: 'Zinc',
          dosage: '15-30 mg daily',
          evidence: 'Good evidence',
          benefits: [
            'Critical for immune function',
            'Anti-inflammatory',
            'Supports healing',
            'Antiviral properties',
            'Often deficient in children with PANDAS/PANS',
            'Supports neurotransmitter function'
          ],
          note: 'Take with food to avoid nausea. Use zinc picolinate or glycinate for best absorption. Balance with copper (10:1 zinc to copper ratio). Test levels if possible.'
        },
        {
          name: 'B-Complex Vitamins (Methylated)',
          dosage: 'Age-appropriate B-complex',
          evidence: 'Moderate evidence',
          benefits: [
            'Energy production',
            'Support nervous system',
            'Methylation support (important for detoxification)',
            'B6 helps with neurotransmitter production',
            'B12 supports nerve health',
            'Often depleted during illness'
          ],
          note: 'Use methylated forms (methylfolate, methylcobalamin) especially if MTHFR gene variants. Take in morning (B vitamins can be energizing).'
        },
        {
          name: 'Vitamin C',
          dosage: '500-1000 mg twice daily',
          evidence: 'Moderate evidence',
          benefits: [
            'Immune system support',
            'Powerful antioxidant',
            'Anti-inflammatory',
            'Supports healing and tissue repair',
            'May reduce infection duration',
            'Supports stress response'
          ],
          note: 'Use buffered form (calcium or magnesium ascorbate) to prevent stomach upset. Split doses throughout day for better absorption. Higher doses may cause loose stools.'
        },
        {
          name: 'Quercetin',
          dosage: '250-500 mg twice daily',
          evidence: 'Emerging evidence',
          benefits: [
            'Natural antihistamine',
            'Mast cell stabilizer',
            'Anti-inflammatory',
            'Antiviral properties',
            'May reduce allergic responses',
            'Antioxidant'
          ],
          note: 'Take with vitamin C and bromelain for enhanced absorption. Particularly helpful if histamine intolerance or MCAS present. Take between meals for anti-inflammatory effect.'
        },
        {
          name: 'L-Glutamine',
          dosage: '3-5 grams daily',
          evidence: 'Good evidence',
          benefits: [
            'Heals leaky gut',
            'Reduces intestinal permeability',
            'Supports gut lining where immune cells reside',
            'May reduce autoimmune responses',
            'Supports immune function',
            'Brain fuel'
          ],
          note: 'Take on empty stomach (morning and/or bedtime). Powder form often most economical. Critical for addressing gut permeability which contributes to autoimmunity.'
        },
        {
          name: 'Melatonin',
          dosage: '0.5-3 mg at bedtime (start low)',
          evidence: 'Good evidence for sleep',
          benefits: [
            'Improves sleep quality',
            'Powerful antioxidant',
            'Anti-inflammatory',
            'Immune modulation',
            'Neuroprotective',
            'Safe for children'
          ],
          note: 'Start with lowest dose. Give 30-60 minutes before bedtime. Use in conjunction with good sleep hygiene. Some children need higher doses but start low.'
        },
        {
          name: 'Saccharomyces boulardii',
          dosage: '250-500 mg daily',
          evidence: 'Good evidence',
          benefits: [
            'Beneficial yeast (probiotic)',
            'Prevents antibiotic-associated diarrhea',
            'Supports gut health during antibiotics',
            'Anti-inflammatory in gut',
            'Can take WITH antibiotics (unlike bacterial probiotics)',
            'Helps prevent C. diff infections'
          ],
          note: 'Safe to take at same time as antibiotics. Particularly helpful during long-term antibiotic treatment. Florastor is common brand.'
        },
        {
          name: 'Vitamin B6 (P5P form)',
          dosage: '25-50 mg daily',
          evidence: 'Moderate evidence',
          benefits: [
            'Supports neurotransmitter production',
            'Needed for serotonin and dopamine synthesis',
            'May help with mood regulation',
            'Anti-inflammatory',
            'Supports immune function'
          ],
          note: 'Use P5P (pyridoxal-5-phosphate) - active form. Often included in B-complex. Important for brain chemistry.'
        },
        {
          name: 'Resveratrol',
          dosage: '50-200 mg daily',
          evidence: 'Emerging evidence',
          benefits: [
            'Powerful antioxidant',
            'Anti-inflammatory',
            'Neuroprotective',
            'May reduce neuroinflammation',
            'Supports mitochondrial function'
          ],
          note: 'Found in grape skins and Japanese knotweed. Take with fats for absorption. Emerging research in neuroimmune conditions.'
        }
      ],
      guidelines: [
        'Start one supplement at a time (wait 3-5 days between additions)',
        'Start with lower doses and increase gradually',
        'Core supplements for most: Omega-3, Vitamin D, Probiotic, Magnesium',
        'Keep list of all supplements and share with all healthcare providers',
        'Quality matters - use third-party tested brands (ConsumerLab, USP, NSF)',
        'Supplements support but don\'t replace appropriate medical treatment',
        'Be patient - many supplements take weeks to show full benefit'
      ]
    },
    lifestyle: {
      isExtendedPandas: true,
      title: 'Lifestyle Management for PANDAS/PANS',
      intro: 'Lifestyle factors significantly impact PANDAS/PANS symptoms and recovery. Focus on reducing inflammation, supporting immune function, and creating a healing environment.',
      categories: [
        {
          icon: '🌙',
          name: 'Sleep Optimization',
          importance: 'CRITICAL',
          importanceNote: '',
          items: [
            '9-11 hours per night for children: Non-negotiable. Sleep is when immune system repairs and fights infection.',
            'Consistent schedule: Same bedtime and wake time every day, even weekends.',
            'Sleep environment: Cool (65-68°F), dark, quiet. Blackout curtains if needed.',
            'No screens 1-2 hours before bed: Blue light suppresses melatonin and worsens PANDAS symptoms.',
            'Calming bedtime routine: Bath, reading, quiet time. Predictable and relaxing.',
            'Melatonin if needed: 0.5-3mg 30-60 minutes before bed (consult doctor).',
            'Address sleep disorders: Screen for sleep apnea, restless leg if sleep quality poor despite adequate hours.',
            'Magnesium before bed: Supports sleep and calms nervous system.',
            'Poor sleep = worse symptoms the next day. Protect sleep at all costs.'
          ]
        },
        {
          icon: '🧘',
          name: 'Stress Reduction',
          importance: 'HIGH',
          importanceNote: '',
          items: [
            'Lower academic pressure during flares: School can wait. Focus on healing first.',
            'Reduce extracurricular demands: Don\'t overschedule. Rest is productive.',
            'Deep breathing exercises: 4-7-8 technique, box breathing. Teaches nervous system regulation.',
            'Mindfulness/meditation: Age-appropriate apps: Headspace Kids, Calm Kids, Smiling Mind.',
            'Gentle yoga or stretching: Reduces stress hormones, increases body awareness.',
            'Time in nature: Even 20 minutes outdoors reduces cortisol and supports healing.',
            'Creative outlets: Art, music, journaling. Non-verbal stress release.',
            'Family therapy: PANDAS/PANS affects whole family. Everyone needs support.',
            'Parent stress management: Your stress affects child. Prioritize your own regulation.',
            'Limit scary content: News, violent shows/games increase anxiety and inflammation.'
          ]
        },
        {
          icon: '🛡️',
          name: 'Infection Prevention',
          importance: 'HIGH',
          importanceNote: '',
          items: [
            'Handwashing: Frequent, thorough (20 seconds with soap). Before meals, after bathroom, when home from school.',
            'Avoid sick contacts: Keep child home from school during known outbreaks. Protect them.',
            'Don\'t share: Drinks, utensils, towels, lip balm with anyone.',
            'Prompt treatment: Any signs of infection need immediate attention and strep testing.',
            'Prophylactic antibiotics: Some children benefit. Discuss with PANDAS specialist.',
            'Consider tonsillectomy: If recurrent strep despite antibiotics. Discuss risks/benefits with ENT familiar with PANDAS.',
            'Treat family members: If anyone has strep, all family members should be tested/treated.',
            'Address carrier state: Some family members carry strep without symptoms. May need treatment.',
            'Dental hygiene: Good oral hygiene prevents oral infections. See dentist regularly.',
            'Boost immune system: Proper nutrition, sleep, supplements, reduce stress.'
          ]
        },
        {
          icon: '🦠',
          name: 'Gut Health Focus',
          importance: 'HIGH',
          importanceNote: '',
          items: [
            'Daily probiotics: Especially during and after antibiotics. 70-80% of immune system in gut.',
            'Prebiotic foods: Feed good bacteria. Asparagus, garlic, onions, bananas, oats.',
            'Bone broth: Heals gut lining. Rich in collagen, glutamine, minerals.',
            'L-glutamine supplement: Repairs leaky gut which contributes to autoimmunity.',
            'Avoid gut irritants: Processed foods, excess sugar, artificial additives damage gut.',
            'Address food sensitivities: Common culprits: gluten, dairy. Consider elimination trial.',
            'Fermented foods (if tolerated): Yogurt, kefir, sauerkraut support microbiome. Watch for histamine sensitivity.',
            'Adequate fiber: From vegetables and fruits. Supports beneficial bacteria.',
            'Limit antibiotics to necessary: But treat infections appropriately. Balance is key.',
            'Test for SIBO/parasites if chronic gut issues: Dysbiosis perpetuates inflammation.'
          ]
        },
        {
          icon: '🏠',
          name: 'Environmental Controls',
          importance: 'MODERATE',
          importanceNote: '',
          items: [
            'HEPA air filters: In child\'s bedroom at minimum. Remove airborne allergens and pollutants.',
            'Address mold: Major immune trigger. If you suspect mold, test and remediate professionally.',
            'Reduce chemicals: Fragrance-free laundry detergent, cleaning products, personal care.',
            'No air fresheners/candles: Artificial fragrances trigger inflammation.',
            'Control humidity: 30-50% prevents mold and dust mites. Dehumidifier if needed.',
            'Wash bedding weekly: Hot water kills dust mites. Allergen-proof mattress/pillow covers.',
            'Remove carpets if possible: Harbor allergens, dust, mold. Hard floors easier to keep clean.',
            'Non-toxic cleaning: Vinegar, baking soda, hydrogen peroxide for most cleaning needs.',
            'Indoor plants: Can improve air quality (spider plants, snake plants) but watch for mold in soil.',
            'Shoes off at door: Don\'t track outdoor pollutants and allergens through house.'
          ]
        },
        {
          icon: '🏃',
          name: 'Physical Activity',
          importance: 'MODERATE',
          importanceNote: '',
          items: [
            'Gentle, regular movement: Walking, swimming, bike riding. Not intense sports during acute phase.',
            'Outdoor activity preferred: Nature exposure + movement = double benefit.',
            'Avoid overexertion during flares: Can worsen symptoms and delay recovery.',
            'Listen to body: If child says tired, believe them. Rest when needed.',
            'Yoga or tai chi: Combines movement with breathwork and mindfulness.',
            'Dance, play: Movement should be joyful, not forced.',
            'Gradually increase: As symptoms improve, slowly return to normal activity levels.',
            'Avoid competitive sports during acute phase: Pressure and physical demands can trigger flares.',
            'Movement is medicine: But timing and intensity matter. Right dose at right time.',
            'Swimming beneficial: Low-impact, full-body. Chlorine sensitivity in some - rinse well after.'
          ]
        },
        {
          icon: '📱',
          name: 'Screen Time Management',
          importance: 'MODERATE',
          importanceNote: '',
          items: [
            'Limit overall screen time: Excessive screen time worsens PANDAS symptoms.',
            'Absolutely no screens before bed: Blue light disrupts sleep, worsens mood and behavior.',
            'Blue light blocking glasses: If screens unavoidable in evening.',
            'Monitor content: Avoid violent, scary, overstimulating content. Increases anxiety.',
            'Use parental controls: Limit access to inappropriate content.',
            'Screen-free meals: Family connection time.',
            'Replace with other activities: Reading, crafts, outdoor play, board games.',
            'Model healthy habits: Parents\' screen use affects children\'s.',
            'Symptoms often worse with screens: Many parents report clear connection.',
            'Social media especially problematic: For adolescents, increases anxiety and comparison.'
          ]
        },
        {
          icon: '🏫',
          name: 'School Accommodations',
          importance: 'HIGH DURING FLARES',
          importanceNote: '',
          items: [
            '504 Plan or IEP: Legal protections for accommodations. Essential for most PANDAS kids.',
            'Educate teachers: About PANDAS/PANS. Behaviors are medical, not willful.',
            'Reduced schedule: Half days, modified workload during flares.',
            'Homebound instruction: If too ill to attend school. Temporary.',
            'Testing accommodations: Extra time, separate room, breaks.',
            'Allow breaks: For bathroom (urinary frequency), anxiety, sensory overload.',
            'Safe person/place: Designate adult child can go to, quiet space for regulation.',
            'No punishment for symptoms: Tics, compulsions, outbursts are not choices.',
            'Gradual return after absence: Don\'t rush full-time return.',
            'Communicate regularly: Between parents and school team. Weekly check-ins during acute phase.'
          ]
        },
        {
          icon: '👥',
          name: 'Social Connection',
          importance: 'MODERATE',
          importanceNote: '',
          items: [
            'Maintain friendships: As able. Video calls during isolation periods.',
            'Small groups better than large: Overstimulation triggers symptoms.',
            'Educate close friends: Age-appropriate explanation if child comfortable.',
            'Don\'t force social situations: During severe symptoms. Child needs space to heal.',
            'Support groups for child: Connecting with other PANDAS kids can be validating.',
            'Family time important: Don\'t just focus on illness. Create positive memories.',
            'Siblings need attention too: Individual time with each child.',
            'Reintegration takes time: Social skills may have regressed. Be patient.',
            'Quality over quantity: Few good friends better than many superficial ones.',
            'Outdoor social activities: Lower infection risk than indoor gatherings.'
          ]
        },
        {
          icon: '📊',
          name: 'Tracking and Documentation',
          importance: 'MODERATE',
          importanceNote: '',
          items: [
            'Symptom diary: Daily ratings of mood, anxiety, tics, OCD, sleep, etc. Use tracker in this app!',
            'Photo/video documentation: Especially tics and behaviors. Shows doctors the reality.',
            'Track triggers: Foods, exposures, stressors. Identify patterns.',
            'Medication log: All medications, supplements, doses, timing. Track effectiveness.',
            'Infection log: Every illness, exposure, strep test. Look for flare correlations.',
            'School performance: Track grades, behavior reports. Shows impact of illness.',
            'Growth chart: Height, weight. PANDAS can affect growth.',
            'Lab results: Keep copies of all lab work. Track trends over time.',
            'Share with medical team: Data helps doctors make better treatment decisions.',
            'Patterns emerge over time: What you track, you can treat.'
          ]
        }
      ]
    },
    diet: {
      isExtendedPandas: true,
      title: 'Anti-Inflammatory Diet for PANDAS/PANS',
      intro: 'Since PANDAS/PANS involves brain inflammation, an anti-inflammatory diet can significantly impact symptoms and recovery. Focus on whole, nutrient-dense foods that reduce inflammation and support immune function.',
      include: {
        icon: '✅',
        title: 'Foods to Include (Eat Plenty)',
        categories: [
          {
            name: 'Vegetables (7-9 servings daily)',
            items: [
              'Leafy greens: Spinach, kale, collards, Swiss chard, arugula, romaine',
              'Cruciferous: Broccoli, cauliflower, Brussels sprouts, cabbage, bok choy',
              'Colorful veggies: Bell peppers (all colors), carrots, beets, sweet potatoes, squash',
              'Anti-inflammatory superstars: Garlic, onions, ginger, turmeric',
              'Others: Celery, cucumber, zucchini, asparagus, green beans, mushrooms'
            ]
          },
          {
            name: 'Fruits (2-4 servings daily)',
            items: [
              'Berries (highest antioxidants): Blueberries, blackberries, raspberries, strawberries',
              'Other fruits: Apples, pears, cherries, pineapple, papaya, kiwi, melons',
              'Citrus: Oranges, grapefruit (unless histamine sensitivity)',
              'Note: Moderate fruit intake during flares (sugar feeds inflammation). Emphasize berries and low-glycemic options.'
            ]
          },
          {
            name: 'Proteins (at every meal)',
            items: [
              'Wild-caught fish (omega-3s): Salmon, sardines, mackerel, anchovies, herring - 2-3x/week',
              'Organic poultry: Chicken, turkey (pasture-raised preferred)',
              'Grass-fed beef: In moderation (high in omega-3 vs grain-fed)',
              'Eggs: Pasture-raised (if tolerated - great source of choline, protein)',
              'Legumes: Lentils, chickpeas, black beans (if tolerated - good fiber + protein)',
              'Bone broth: Homemade or quality store-bought (healing for gut, immune system)'
            ]
          },
          {
            name: 'Healthy Fats (essential for brain)',
            items: [
              'Extra virgin olive oil: Primary cooking/dressing oil (high in oleocanthal - anti-inflammatory)',
              'Avocados and avocado oil: Rich in monounsaturated fats',
              'Coconut oil: Medium-chain triglycerides, antimicrobial',
              'Nuts (if tolerated): Walnuts (omega-3), almonds, pecans, macadamias. Watch for histamine sensitivity.',
              'Seeds: Chia, flax, hemp, pumpkin, sunflower',
              'Grass-fed ghee or butter: If dairy tolerated (ghee usually better tolerated)'
            ]
          },
          {
            name: 'Whole Grains (if tolerated)',
            items: [
              'Gluten-free options: Quinoa, rice (brown and white), oats (certified GF), millet, buckwheat',
              'Many PANDAS children do better avoiding gluten. Consider 4-6 week elimination trial.'
            ]
          },
          {
            name: 'Anti-Inflammatory Herbs & Spices',
            items: [
              'Turmeric: Most powerful anti-inflammatory. Use liberally. Add black pepper for absorption.',
              'Ginger: Anti-inflammatory, immune-boosting, digestive aid',
              'Garlic: Antimicrobial, immune-supporting',
              'Cinnamon: Blood sugar regulation, antimicrobial',
              'Others: Oregano, basil, rosemary, thyme, sage, parsley, cilantro'
            ]
          },
          {
            name: 'Beverages',
            items: [
              'Water: Plenty throughout day (half body weight in ounces)',
              'Bone broth: Sip as beverage, use as soup base',
              'Green tea: Antioxidants, L-theanine (calming). Limit caffeine for children.',
              'Herbal teas: Chamomile, ginger, peppermint, rooibos',
              'Golden milk: Turmeric latte with coconut milk'
            ]
          }
        ]
      },
      avoid: {
        icon: '🔴',
        title: 'Foods to Avoid (Pro-Inflammatory)',
        categories: [
          {
            name: 'Sugar & Sweeteners',
            reason: 'Sugar impairs immune function, feeds harmful bacteria, promotes inflammation, worsens mood/behavior',
            items: [
              'Refined white and brown sugar',
              'High fructose corn syrup',
              'Candy, cookies, cakes, pastries',
              'Sugary drinks (soda, juice with added sugar, sports drinks)',
              'Artificial sweeteners (aspartame, sucralose - may be neurotoxic)'
            ]
          },
          {
            name: 'Processed Foods',
            reason: 'Contain inflammatory oils, additives, preservatives, lack nutrients',
            items: [
              'Fast food and fried foods',
              'Packaged snacks (chips, crackers, cookies)',
              'Processed meats (hot dogs, deli meat, sausage, bacon)',
              'Frozen dinners and convenience foods',
              'Anything with long ingredient list or unpronounceable chemicals'
            ]
          },
          {
            name: 'Refined Grains',
            reason: 'Spike blood sugar, lack nutrients, increase inflammation',
            items: [
              'White bread and white pasta',
              'White rice',
              'Pastries, donuts, muffins',
              'Most breakfast cereals',
              'Crackers, pretzels'
            ]
          },
          {
            name: 'Inflammatory Oils',
            reason: 'High in omega-6, promote inflammation, oxidize easily',
            items: [
              'Vegetable oil, canola oil',
              'Soybean oil, corn oil',
              'Margarine and shortening',
              'Anything fried in these oils'
            ]
          },
          {
            name: 'Dairy (consider elimination)',
            reason: 'Casein can be inflammatory, increases mucus, common sensitivity in PANDAS',
            items: [
              'Milk, cheese (especially processed cheese)',
              'Ice cream, yogurt (unless fermented and tolerated)',
              'Note: Some tolerate grass-fed ghee or butter',
              'Consider 4-6 week elimination trial'
            ]
          },
          {
            name: 'Gluten (consider elimination)',
            reason: 'Increases intestinal permeability (leaky gut), promotes inflammation in sensitive individuals',
            items: [
              'Wheat, barley, rye',
              'Bread, pasta, baked goods (conventional)',
              'Hidden in sauces, condiments, processed foods',
              'Many PANDAS children improve significantly off gluten',
              'Try strict elimination for 4-6 weeks, then reintroduce and observe'
            ]
          },
          {
            name: 'Food Additives',
            reason: 'Can trigger immune responses, behavioral symptoms, inflammation',
            items: [
              'Artificial colors/dyes (Red 40, Yellow 5, Yellow 6 especially problematic)',
              'MSG (monosodium glutamate)',
              'Preservatives (BHT, BHA, sodium benzoate, nitrates)',
              'Artificial flavors'
            ]
          }
        ]
      },
      sampleDay: {
        title: 'Sample Day of Anti-Inflammatory Eating',
        meals: [
          { name: 'Breakfast', description: 'Scrambled eggs with sautéed spinach and mushrooms, topped with avocado. Side of mixed berries. Green tea or herbal tea.' },
          { name: 'Snack', description: 'Apple slices with almond butter, handful of walnuts' },
          { name: 'Lunch', description: 'Large salad with mixed greens, grilled chicken, colorful vegetables (bell peppers, carrots, cucumber), pumpkin seeds, olive oil and lemon dressing. Side of quinoa or sweet potato.' },
          { name: 'Snack', description: 'Carrot and celery sticks with hummus, or bone broth' },
          { name: 'Dinner', description: 'Baked wild salmon with turmeric and ginger, roasted broccoli and cauliflower drizzled with olive oil, roasted sweet potato. Side salad.' },
          { name: 'Evening', description: 'Golden milk (turmeric latte) or chamomile tea' }
        ]
      },
      tips: {
        title: 'Practical Implementation Tips',
        items: [
          'Start gradually: Don\'t overhaul everything at once. Add good foods first, then remove bad ones.',
          '80/20 rule: Aim for 80% whole foods, 20% flexibility. Perfection not required.',
          'Meal prep: Cook in batches on weekends to make weekdays easier.',
          'Get family on board: Easier when everyone eats similarly. This diet is healthy for whole family.',
          'Read ALL labels: Hidden sugars, oils, and additives everywhere.',
          'Keep healthy snacks accessible: Pre-cut veggies, fruit, nuts visible and ready.',
          'Hide trigger foods: Out of sight, out of mind. Better yet, don\'t buy them.',
          'Food is medicine: This is treatment, not deprivation. Reframe mindset.',
          'Track food-symptom connection: Keep log. You\'ll see patterns.',
          'Consider working with nutritionist: Familiar with PANDAS/PANS and elimination diets.',
          'Be patient: Dietary changes take 2-4 weeks to show full benefit.'
        ]
      }
    },
    autoimmune: {
      hasTab: true,
      understanding: {
        title: 'Understanding Autoimmunity',
        description: 'Autoimmunity occurs when the immune system loses the ability to distinguish between "self" and "non-self," leading it to attack the body\'s own healthy tissues. In PANDAS/PANS, this attack is directed at the brain, specifically the basal ganglia.'
      },
      normalFunction: {
        title: 'Normal Immune Function',
        steps: [
          'Pathogen recognition - Immune cells identify foreign invaders',
          'Antibody production - B-cells create specific antibodies',
          'Targeted attack - Antibodies bind only to foreign pathogens',
          'Memory formation - Immune system "remembers" pathogen for faster response next time',
          'Tolerance - Immune system does NOT attack body\'s own cells'
        ]
      },
      whatGoesWrong: {
        title: 'What Goes Wrong in PANDAS/PANS',
        molecularMimicry: {
          title: 'Molecular Mimicry',
          points: [
            'Certain bacterial proteins closely resemble brain tissue proteins',
            'The immune system creates antibodies against bacterial epitopes (specific protein sequences)',
            'These antibodies accidentally recognize similar sequences on neuronal cells',
            'Antibodies cross blood-brain barrier and bind to neurons',
            'This binding triggers inflammation and cell dysfunction'
          ]
        },
        bbbDysfunction: {
          title: 'Blood-Brain Barrier Dysfunction',
          description: 'The blood-brain barrier (BBB) normally protects the brain from immune attacks. In PANDAS/PANS:',
          points: [
            'Inflammation increases BBB permeability',
            'Antibodies and inflammatory cytokines can cross into brain',
            'Some children may have inherently "leaky" BBB',
            'Infections can temporarily compromise BBB integrity',
            'This allows immune system access to brain tissue'
          ]
        }
      },
      keyAntibodies: {
        title: 'Key Antibodies in PANDAS/PANS',
        antiNeuronal: {
          title: 'Anti-Neuronal Antibodies',
          items: [
            { name: 'Anti-dopamine receptor antibodies', description: 'Target D1 and D2 receptors in basal ganglia' },
            { name: 'Anti-lysoganglioside-GM1', description: 'Associated with tic disorders' },
            { name: 'Anti-tubulin', description: 'Affects neuronal structure and function' },
            { name: 'Anti-neuronal cell surface antibodies', description: 'Various targets being researched' }
          ]
        },
        inflammatoryMarkers: {
          title: 'Inflammatory Markers',
          items: [
            { name: 'Cytokines: IL-6, IL-17, TNF-alpha', description: 'Elevated during flares' },
            { name: 'CRP (C-Reactive Protein)', description: 'General inflammation marker' },
            { name: 'ESR (Erythrocyte Sedimentation Rate)', description: 'Another inflammation indicator' },
            { name: 'Anti-streptolysin O (ASO)', description: 'Indicates recent strep exposure' },
            { name: 'Anti-DNase B', description: 'Another strep antibody, can stay elevated longer' }
          ]
        }
      },
      inflammatoryCascade: {
        title: 'The Inflammatory Cascade',
        steps: [
          'Initial trigger - Infection or other immune stimulus',
          'Cytokine release - Pro-inflammatory signals amplify response',
          'Immune cell activation - T-cells, B-cells, microglia activated',
          'BBB disruption - Inflammatory factors increase permeability',
          'Antibody infiltration - Cross-reactive antibodies enter brain',
          'Microglial activation - Brain\'s immune cells become overactive',
          'Neuroinflammation - Direct damage to neurons and synapses',
          'Neurotransmitter disruption - Dopamine, serotonin, glutamate imbalanced',
          'Symptom manifestation - Behavioral and neurological changes'
        ]
      },
      whySomeChildren: {
        title: 'Why Some Children and Not Others?',
        geneticFactors: {
          title: 'Genetic Factors',
          points: [
            'HLA genes - Certain HLA types increase susceptibility (particularly HLA-DRB1)',
            'Immune regulation genes - Variations in genes controlling immune response',
            'BBB genes - Genetic differences affecting barrier integrity',
            'Cytokine genes - Variations affecting inflammatory response',
            'Family history of autoimmune disease increases risk'
          ]
        },
        epigeneticFactors: {
          title: 'Epigenetic Factors',
          points: [
            'Environmental exposures can modify gene expression',
            'Stress can alter immune regulation',
            'Nutrition affects immune function',
            'Gut microbiome influences immune system development'
          ]
        }
      },
      th1Th2Balance: {
        title: 'The Th1/Th2 Balance',
        description: 'The immune system has two main branches:',
        branches: [
          { name: 'Th1 response', description: 'Cell-mediated immunity, fights intracellular pathogens' },
          { name: 'Th2 response', description: 'Antibody-mediated immunity, fights extracellular pathogens' }
        ],
        imbalance: [
          'Th2 dominance (excessive antibody production)',
          'Th1 suppression (reduced cellular immunity)',
          'This imbalance promotes autoantibody formation'
        ]
      },
      relatedConditions: {
        title: 'Related Autoimmune Conditions',
        childrenRates: {
          title: 'Children with PANDAS/PANS have higher rates of:',
          conditions: [
            'Autoimmune thyroiditis (Hashimoto\'s)',
            'Celiac disease',
            'Type 1 diabetes (in family)',
            'Rheumatic fever',
            'Sydenham\'s chorea',
            'Autoimmune encephalitis',
            'Multiple sclerosis (rare in children but may be related)'
          ]
        },
        familyMembers: {
          title: 'Family members often have:',
          conditions: [
            'Autoimmune thyroid disease',
            'Rheumatoid arthritis',
            'Lupus',
            'Multiple sclerosis',
            'Other autoimmune conditions'
          ]
        }
      },
      gutBrainImmune: {
        title: 'The Gut-Brain-Immune Connection',
        gutAsHub: {
          title: 'Gut as Immune Hub',
          points: [
            '70-80% of immune system resides in gut',
            'Gut microbiome trains immune system',
            'Dysbiosis (imbalanced gut bacteria) promotes inflammation',
            '"Leaky gut" allows bacterial fragments into bloodstream',
            'This can trigger systemic inflammation and BBB disruption'
          ]
        },
        gutBrainAxis: {
          title: 'Gut-Brain Axis',
          points: [
            'Gut microbiome produces neurotransmitters',
            'Vagus nerve connects gut to brain',
            'Gut inflammation can directly affect brain function',
            'Addressing gut health is crucial in PANDAS/PANS treatment'
          ]
        }
      },
      treatmentApproaches: {
        title: 'Treatment Approaches',
        immunomodulatory: {
          title: 'Immunomodulatory Treatments',
          treatments: [
            {
              name: 'IVIG (Intravenous Immunoglobulin)',
              points: [
                'Pools antibodies from thousands of donors',
                'Helps regulate immune system',
                'Can reduce autoantibody levels',
                'Most effective when given during acute flare',
                'May require multiple treatments',
                'Can be expensive and require prior authorization'
              ]
            },
            {
              name: 'Plasmapheresis/Plasma Exchange',
              points: [
                'Filters blood to remove antibodies',
                'Reserved for severe, refractory cases',
                'Requires specialized center',
                'Can provide dramatic improvement',
                'Effects may be temporary without maintenance therapy'
              ]
            },
            {
              name: 'Rituximab (for severe cases)',
              points: [
                'Depletes B-cells that produce antibodies',
                'Reserved for very severe, treatment-resistant cases',
                'Requires careful monitoring',
                'Used off-label in children'
              ]
            }
          ]
        },
        antiInflammatory: {
          title: 'Anti-Inflammatory Approaches',
          treatments: [
            {
              name: 'NSAIDs (Ibuprofen, Naproxen)',
              points: [
                'Reduce inflammation',
                'May help during flares',
                'Long-term use requires monitoring'
              ]
            },
            {
              name: 'Corticosteroids',
              points: [
                'Powerful anti-inflammatory',
                'Can provide rapid improvement',
                'Significant side effects with prolonged use',
                'Use controversial in PANDAS/PANS community',
                'Short bursts may be helpful for acute flares'
              ]
            },
            {
              name: 'Natural anti-inflammatories',
              points: [
                'Omega-3 fatty acids (fish oil)',
                'Curcumin',
                'Resveratrol',
                'NAC (N-acetylcysteine)',
                'Quercetin'
              ]
            }
          ]
        },
        infectionTreatment: {
          title: 'Infection Treatment',
          treatments: [
            {
              name: 'Antibiotics',
              points: [
                'Treat active infections',
                'Some children benefit from prophylactic antibiotics',
                'Long-term use requires monitoring',
                'Azithromycin has anti-inflammatory properties'
              ]
            },
            {
              name: 'Antivirals (if viral trigger suspected)',
              points: [
                'Valacyclovir for EBV, HSV',
                'Used in select cases'
              ]
            }
          ]
        },
        immuneSupport: {
          title: 'Immune Support',
          points: [
            'Vitamin D (many PANDAS/PANS children are deficient)',
            'Probiotics (support gut-immune connection)',
            'Zinc, selenium, magnesium',
            'Adequate sleep and stress management',
            'Anti-inflammatory diet'
          ]
        }
      },
      remissionProcess: {
        title: 'The Remission Process',
        description: 'With proper treatment:',
        steps: [
          'Infection is eradicated',
          'Inflammation decreases',
          'Antibody levels decline',
          'BBB integrity improves',
          'Microglial activation normalizes',
          'Neurotransmitter balance restored',
          'Symptoms gradually improve',
          'Brain function recovers'
        ]
      },
      longTermOutlook: {
        title: 'Long-Term Outlook',
        points: [
          'Many children achieve full remission',
          'Some have residual symptoms requiring ongoing support',
          'Early diagnosis and treatment improves outcomes',
          'Immune system may mature and become less reactive',
          'Continued infection prevention is key',
          'Some children transition to adult care for ongoing immune support'
        ]
      },
      earlyIntervention: {
        title: 'The Importance of Early Intervention',
        points: [
          'The longer inflammation persists, the more difficult to treat',
          'Chronic inflammation can lead to neuronal damage',
          'Early treatment prevents this progression',
          'Aggressive early treatment often leads to better outcomes',
          'Don\'t wait for "perfect" antibody tests - clinical diagnosis is valid'
        ]
      }
    },
    recovery: {
      hasTab: true,
      intro: {
        title: 'The Path to Recovery',
        description: 'Recovery from PANDAS/PANS is possible, but it looks different for every child. Some recover fully and quickly, while others have a longer journey with ups and downs. Understanding what recovery means and what helps can provide hope during difficult times.'
      },
      whatRecoveryLooksLike: {
        title: 'What Does Recovery Look Like?',
        types: [
          {
            name: 'Full Remission',
            color: 'bg-green-50',
            textColor: 'text-green-800',
            points: [
              'Child returns to pre-PANDAS/PANS baseline',
              'No residual symptoms',
              'Can handle normal childhood stress',
              'Functions well at school and socially',
              'No longer experiencing flares',
              'May still need preventive measures'
            ]
          },
          {
            name: 'Partial Recovery',
            color: 'bg-blue-50',
            textColor: 'text-blue-800',
            points: [
              'Significant improvement but some residual symptoms',
              'Can function well most of the time',
              'Occasional mild flares with infections',
              'May need ongoing support (therapy, medications)',
              'Quality of life greatly improved'
            ]
          },
          {
            name: 'Ongoing Management',
            color: 'bg-amber-50',
            textColor: 'text-amber-800',
            points: [
              'Symptoms controlled but requires treatment',
              'Regular medical monitoring',
              'Preventive antibiotics or immune support',
              'Lifestyle modifications',
              'Can still live full, happy life'
            ]
          }
        ]
      },
      factorsInfluencing: {
        title: 'Factors That Influence Recovery',
        positive: {
          title: 'Positive Factors',
          points: [
            'Early diagnosis and treatment',
            'Appropriate antibiotics/immune therapy',
            'Strong support system',
            'Good medical team',
            'Addressing co-infections',
            'Anti-inflammatory lifestyle',
            'Consistent treatment approach',
            'Parent education and advocacy'
          ]
        },
        challenging: {
          title: 'Challenging Factors',
          points: [
            'Delayed diagnosis (years of untreated inflammation)',
            'Chronic untreated infections',
            'Multiple co-infections',
            'Complicating factors (Lyme, mold, MCAS)',
            'Limited access to knowledgeable doctors',
            'Financial barriers to treatment',
            'Ongoing exposure to triggers'
          ]
        }
      },
      healingTimeline: {
        title: 'The Healing Timeline',
        phases: [
          {
            period: 'First 3-6 months',
            points: [
              'Focus on stopping active infection',
              'Reduce acute inflammation',
              'Stabilize symptoms',
              'Begin addressing gut health',
              'Implement dietary changes',
              'Progress may be gradual'
            ]
          },
          {
            period: '6-12 months',
            points: [
              'Symptoms should be improving',
              'Flares may still occur but less severe',
              'Work on underlying immune dysfunction',
              'Address nutritional deficiencies',
              'Continue anti-inflammatory approach',
              'May see significant improvement'
            ]
          },
          {
            period: '1-2 years',
            points: [
              'Continued progress',
              'Longer periods between flares',
              'Better stress tolerance',
              'Improved functioning',
              'May be able to reduce medications',
              'Working on building resilience'
            ]
          },
          {
            period: '2+ years',
            points: [
              'Many children in remission or near-remission',
              'Flares rare or absent',
              'Good quality of life',
              'May need ongoing preventive measures',
              'Some children completely "graduate" from treatment'
            ]
          }
        ],
        note: 'This timeline is general. Some children improve much faster, others take longer. Progress isn\'t always linear - there may be setbacks along the way.'
      },
      whatHelpsRecovery: {
        title: 'What Helps Recovery',
        categories: [
          {
            name: 'Medical Treatment',
            color: 'bg-purple-50',
            textColor: 'text-purple-800',
            points: [
              'Appropriate antibiotics for infections',
              'Anti-inflammatory medications when needed',
              'Immune modulation (IVIG, plasmapheresis) for severe cases',
              'Treating co-infections (mycoplasma, Lyme, viruses)',
              'Addressing gut health'
            ]
          },
          {
            name: 'Nutritional Support',
            color: 'bg-green-50',
            textColor: 'text-green-800',
            points: [
              'Anti-inflammatory diet',
              'Addressing food sensitivities',
              'Adequate protein and healthy fats',
              'Nutrient-dense whole foods',
              'Proper supplementation for deficiencies'
            ]
          },
          {
            name: 'Lifestyle Factors',
            color: 'bg-blue-50',
            textColor: 'text-blue-800',
            points: [
              'Adequate sleep (cannot be overstated)',
              'Stress reduction',
              'Gentle exercise (as tolerated)',
              'Time in nature',
              'Limiting screen time',
              'Consistent routines'
            ]
          },
          {
            name: 'Therapeutic Support',
            color: 'bg-teal-50',
            textColor: 'text-teal-800',
            points: [
              'Exposure-Response Prevention (ERP) therapy for OCD',
              'Cognitive Behavioral Therapy (CBT)',
              'Family therapy',
              'Occupational therapy for sensory issues',
              'Physical therapy if needed',
              'School accommodations (504 plan or IEP)'
            ]
          },
          {
            name: 'Emotional/Spiritual Support',
            color: 'bg-pink-50',
            textColor: 'text-pink-800',
            points: [
              'Connection with other PANDAS/PANS families',
              'Faith/spiritual practices if meaningful',
              'Counseling for parents',
              'Maintaining hope',
              'Celebrating small victories',
              'Finding joy in midst of struggle'
            ]
          }
        ]
      },
      buildingResilience: {
        title: 'Building Resilience',
        description: 'As children improve, focus on:',
        points: [
          'Gradually increasing independence',
          'Reconnecting with friends',
          'Returning to activities they love',
          'Building confidence',
          'Processing the trauma of illness',
          'Creating new positive experiences',
          'Not letting PANDAS/PANS define them'
        ]
      },
      forParents: {
        title: 'For Parents - Taking Care of Yourself',
        intro: 'You cannot pour from an empty cup. Your wellbeing matters.',
        selfCare: {
          title: 'Self-Care Essentials',
          points: [
            'Take breaks when possible',
            'Connect with support groups',
            'Individual therapy or counseling',
            'Maintain relationships outside of PANDAS/PANS',
            'Do activities you enjoy',
            'Exercise and eat well',
            'Sleep when you can',
            'Set boundaries with extended family',
            'It\'s okay to ask for help',
            'Release guilt and shame'
          ]
        },
        emotionalToll: {
          title: 'Managing the Emotional Toll',
          sections: [
            {
              name: 'Grief is Normal',
              points: [
                'Grief for the child you knew before illness',
                'Grief for lost time and experiences',
                'Grief for the easy childhood you imagined',
                'These feelings are valid and normal',
                'Allow yourself to feel them'
              ]
            },
            {
              name: 'Dealing with Others',
              points: [
                'Not everyone will understand',
                'You don\'t owe anyone explanations',
                'Protect your energy',
                'Surround yourself with believers',
                'Educate those who are open',
                'Set boundaries with those who aren\'t'
              ]
            }
          ]
        },
        trauma: {
          title: 'Trauma and PTSD',
          description: 'Both children and parents can develop trauma from:',
          causes: [
            'The sudden onset and severity of symptoms',
            'Medical procedures (blood draws, IVs)',
            'Hospitalizations',
            'Watching your child suffer',
            'Fear of relapse',
            'Uncertainty about future'
          ],
          addressing: {
            title: 'Addressing this trauma:',
            points: [
              'Therapy (EMDR can be helpful)',
              'Somatic experiencing',
              'Processing the experience',
              'Creating sense of safety',
              'Building new positive memories'
            ]
          }
        }
      },
      siblingImpact: {
        title: 'Sibling Impact',
        description: 'Don\'t forget siblings who:',
        impacts: [
          'May feel neglected',
          'Worry about their brother/sister',
          'Feel scared or confused',
          'Need individual attention',
          'May resent the focus on sick sibling',
          'Need their own support'
        ],
        strategies: {
          title: 'Strategies',
          points: [
            'One-on-one time with each child',
            'Age-appropriate explanations',
            'Family therapy',
            'Individual therapy for siblings if needed',
            'Maintain their routines when possible',
            'Acknowledge their feelings'
          ]
        }
      },
      schoolAndSocial: {
        title: 'School and Social Life',
        school: {
          title: 'School Considerations',
          points: [
            '504 plan or IEP may be needed',
            'Homebound instruction during severe flares',
            'Modified schedule or workload',
            'Testing accommodations',
            'Understanding teachers make huge difference',
            'Gradual return after absence'
          ]
        },
        social: {
          title: 'Social Challenges',
          points: [
            'Friends may not understand',
            'Child may be embarrassed',
            'Social skills may have regressed',
            'Reintegration can be difficult',
            'May need support rebuilding friendships'
          ]
        }
      },
      successStories: {
        title: 'Success Stories',
        description: 'Many children and families have walked this path before you:',
        stories: [
          'Children who couldn\'t leave the house now traveling',
          'Children who couldn\'t eat now enjoying food',
          'Children who missed years of school now graduating',
          'Families who thought they\'d never recover now thriving',
          'The acute crisis does not last forever'
        ]
      },
      notAlone: {
        title: 'You Are Not Alone',
        description: 'Thousands of families are on this journey:',
        resources: [
          'Support groups (online and in-person)',
          'Facebook communities',
          'PANDAS Network conferences',
          'Local meetups',
          'Online forums',
          'Parent mentors who understand'
        ]
      },
      messagesOfHope: {
        title: 'Messages of Hope',
        messages: [
          '"Your child is still in there. The behaviors are not who they are. They will come back to you."',
          '"Progress isn\'t linear. Bad days don\'t erase the progress you\'ve made."',
          '"You are exactly the parent your child needs. Your advocacy matters."',
          '"This is temporary. It feels permanent, but it\'s not."',
          '"Small improvements add up. Celebrate every single one."',
          '"You\'re doing better than you think you are."'
        ]
      },
      lightAtEnd: {
        title: 'The Light at the End',
        paragraphs: [
          'Recovery is possible. Children do get better. Families do heal. Life can return to normal, or a new, beautiful normal. The journey is hard, but you\'re strong enough to walk it. One day at a time, one moment at a time, you will get through this.',
          'And when you do, you\'ll have wisdom, resilience, and strength you never knew you had. Your child will know they were loved through the darkest time. That matters more than anything.',
          'Keep going. There is hope. 💚'
        ]
      }
    }
  },
  'mycoplasma-pneumonia': {
    name: 'Mycoplasma Pneumoniae',
    color: 'bg-accent',
    lightColor: 'bg-accent/10',
    textColor: 'text-accent',
    description: 'A common bacterial infection that can trigger neuropsychiatric symptoms in susceptible children',
    about: {
      isExtended: true,
      whatIs: {
        title: 'What is Mycoplasma Pneumoniae?',
        description: 'Mycoplasma pneumoniae is a unique bacterial pathogen that causes "atypical" or "walking pneumonia." It\'s called "atypical" because it often causes milder respiratory symptoms than typical pneumonia, and people can sometimes continue daily activities despite being infected (hence "walking" pneumonia).'
      },
      whyItMatters: {
        title: 'Why It Matters for PANS',
        description: 'Mycoplasma pneumoniae is one of the most common infectious triggers for PANS (Pediatric Acute-onset Neuropsychiatric Syndrome). Studies show it\'s second only to streptococcus as a documented PANS trigger. The bacteria can trigger an autoimmune response that affects the brain, leading to sudden onset of OCD, anxiety, behavioral changes, and other neuropsychiatric symptoms.'
      },
      uniqueCharacteristics: {
        title: 'Unique Characteristics',
        points: [
          { name: 'No cell wall', description: 'Unlike most bacteria, mycoplasma lacks a cell wall, making it resistant to many common antibiotics (like penicillin and amoxicillin)' },
          { name: 'Small and stealthy', description: 'One of the smallest self-replicating organisms, it can evade the immune system' },
          { name: 'Intracellular', description: 'Can live inside cells, making it harder for antibiotics and immune system to reach' },
          { name: 'Slow-growing', description: 'Replicates slowly (every 6+ hours vs 20 minutes for typical bacteria)' },
          { name: 'Can become chronic', description: 'May persist for months or years if not properly treated' }
        ]
      },
      howItTriggersPans: {
        title: 'How It Triggers PANS',
        steps: [
          'Infection: Child is exposed to mycoplasma (often from other children at school or daycare)',
          'Immune response: Body creates antibodies to fight the infection',
          'Molecular mimicry: Mycoplasma proteins resemble brain tissue proteins',
          'Cross-reactivity: Antibodies attack both bacteria AND brain tissue (basal ganglia)',
          'Inflammation: Brain inflammation leads to sudden neuropsychiatric symptoms'
        ]
      },
      testingChallenges: {
        title: 'Testing Challenges',
        description: 'Diagnosing mycoplasma infection can be tricky:',
        challenges: [
          { name: 'IgM antibodies', description: 'Indicate recent/active infection but may be negative early in infection or if chronic' },
          { name: 'IgG antibodies', description: 'Indicate past exposure but can stay elevated for months/years' },
          { name: 'PCR testing', description: 'Most accurate but may be negative if bacteria present in lungs but blood sample taken' },
          { name: 'Clinical diagnosis', description: 'Often diagnosed based on symptoms and response to appropriate antibiotics' }
        ]
      },
      prevalence: {
        title: 'Prevalence',
        points: [
          'Accounts for 15-20% of all community-acquired pneumonia',
          'Most common in school-age children (5-15 years) - the same age as PANS onset',
          'Outbreaks common in schools and close-contact settings',
          'Can occur year-round but peaks in late summer and fall',
          'Incubation period 1-4 weeks (long time between exposure and symptoms)'
        ]
      }
    },
    symptoms: {
      isExtendedMycoplasma: true,
      intro: 'Mycoplasma can present with respiratory symptoms, neuropsychiatric symptoms (PANS), or both. Many children have PANS symptoms with minimal or no respiratory symptoms.',
      respiratory: {
        title: 'Respiratory Symptoms',
        items: [
          { name: 'Persistent dry cough (can last weeks to months)', highlight: 'hallmark symptom' },
          { name: 'Low-grade fever (usually under 102°F)' },
          { name: 'Sore throat' },
          { name: 'Headache' },
          { name: 'Fatigue and malaise (feeling unwell)' },
          { name: 'Chest pain or discomfort' },
          { name: 'Wheezing or difficulty breathing (less common)' },
          { name: 'Ear pain or ear infections' }
        ],
        note: 'Some children with mycoplasma-triggered PANS have NO respiratory symptoms or only mild cold symptoms'
      },
      neuropsychiatric: {
        title: 'PANS Neuropsychiatric Symptoms',
        items: [
          { name: 'Sudden onset OCD', description: 'contamination fears, counting, arranging, intrusive thoughts' },
          { name: 'Food restriction', description: 'refusal to eat, fear of choking/swallowing' },
          { name: 'Separation anxiety', description: 'cannot leave parent\'s side' },
          { name: 'Anxiety and panic attacks' },
          { name: 'Mood changes', description: 'irritability, rage, depression' },
          { name: 'Behavioral regression', description: 'baby talk, loss of independence' },
          { name: 'Tics', description: 'motor or vocal' },
          { name: 'Cognitive decline', description: 'brain fog, memory issues, difficulty concentrating' },
          { name: 'Sleep disturbances', description: 'insomnia, nightmares, night terrors' },
          { name: 'Urinary frequency', description: 'sudden need to urinate often' },
          { name: 'Sensory sensitivities', description: 'to touch, sound, light' }
        ]
      },
      other: {
        title: 'Other Associated Symptoms',
        items: [
          { name: 'Rash (rare, can look like target lesions)' },
          { name: 'Joint pain' },
          { name: 'Gastrointestinal symptoms', description: 'nausea, vomiting, diarrhea' },
          { name: 'Eye redness or conjunctivitis' }
        ]
      },
      importantNote: 'In PANS, neuropsychiatric symptoms often appear AFTER respiratory symptoms resolve, or with no respiratory symptoms at all. This delayed presentation can make diagnosis challenging.'
    },
    triggers: {
      isExtendedMycoplasma: true,
      howItSpreads: {
        title: 'How Mycoplasma Spreads',
        items: [
          { name: 'Respiratory droplets', description: 'Coughing, sneezing, close contact' },
          { name: 'Incubation period', description: '1-4 weeks (long time before symptoms appear)' },
          { name: 'Contagious period', description: 'Can spread for weeks, even during treatment' },
          { name: 'Close contact settings', description: 'Schools, daycares, dormitories, families' }
        ]
      },
      riskFactors: {
        title: 'Factors That Increase Risk of Infection',
        items: [
          'School-age children (5-15) - peak age group',
          'Close-contact environments - schools, camps, sports teams',
          'Fall and late summer - seasonal peaks',
          'Weakened immune system - stress, poor sleep, poor nutrition',
          'Asthma or reactive airway disease - may have more severe symptoms',
          'Family members infected - spreads easily within households'
        ]
      },
      pansRiskFactors: {
        title: 'Factors That Trigger PANS Response',
        intro: 'Not everyone who gets mycoplasma develops PANS. These factors increase risk of autoimmune response:',
        items: [
          'Genetic susceptibility - family history of autoimmune disease',
          'Previous PANS episodes - once sensitized, more likely to flare',
          'Leaky blood-brain barrier - allows antibodies to reach brain',
          'Gut dysbiosis - imbalanced microbiome promotes inflammation',
          'Chronic inflammation - baseline inflammatory state',
          'Immune dysregulation - Th1/Th2 imbalance',
          'Nutritional deficiencies - especially vitamin D, zinc',
          'Stress - physical or emotional stress weakens immune regulation',
          'Co-infections - other infections occurring simultaneously'
        ]
      },
      reactivation: {
        title: 'Factors That Can Cause Reactivation or Persistence',
        items: [
          'Inadequate antibiotic treatment - too short duration or wrong antibiotic',
          'Antibiotic resistance - increasing in mycoplasma',
          'Biofilm formation - bacteria create protective coating',
          'Intracellular hiding - bacteria live inside cells where antibiotics struggle to reach',
          'Re-exposure - getting infected again from contacts',
          'Weakened immune system - unable to fully clear infection'
        ]
      }
    },
    supplements: {
      isExtendedMycoplasma: true,
      intro: 'These supplements have research or clinical evidence supporting their use alongside conventional treatment. Always consult with your healthcare provider before starting supplements.',
      items: [
        {
          name: 'Vitamin D3',
          dosage: '2000-4000 IU daily (test levels, aim for 50-80 ng/mL)',
          evidence: 'Strong evidence',
          benefits: [
            'Essential for immune function',
            'Reduces risk of respiratory infections',
            'Anti-inflammatory properties',
            'Many PANS children are deficient',
            'Helps regulate immune response'
          ],
          note: 'Take with vitamin K2 and fat for absorption. Test levels every 3 months.'
        },
        {
          name: 'Zinc',
          dosage: '15-30 mg daily (age-dependent)',
          evidence: 'Strong evidence',
          benefits: [
            'Critical for immune cell function',
            'Antiviral and antibacterial properties',
            'Reduces duration of respiratory infections',
            'Supports healing',
            'Often deficient in children'
          ],
          note: 'Take with food. Balance with copper (10:1 zinc to copper ratio). Use zinc picolinate or glycinate for better absorption.'
        },
        {
          name: 'Probiotics',
          dosage: 'Multi-strain, 25-50 billion CFU daily',
          evidence: 'Good evidence',
          benefits: [
            'Support gut-immune connection',
            'Reduce antibiotic side effects',
            'Help prevent secondary infections',
            'Anti-inflammatory effects',
            'Support microbiome recovery after antibiotics'
          ],
          note: 'Take away from antibiotics (2 hours before/after). Continue for months after antibiotics stop. Look for Lactobacillus and Bifidobacterium strains.'
        },
        {
          name: 'Omega-3 Fatty Acids (Fish Oil)',
          dosage: '1000-2000 mg EPA+DHA daily',
          evidence: 'Strong evidence',
          benefits: [
            'Powerful anti-inflammatory',
            'Supports brain health',
            'Reduces neuroinflammation',
            'Improves behavior and mood',
            'Supports immune regulation'
          ],
          note: 'Use high-quality, tested for purity. Take with meals. Look for high EPA formulas.'
        },
        {
          name: 'Vitamin C',
          dosage: '500-1000 mg daily (split doses)',
          evidence: 'Moderate evidence',
          benefits: [
            'Immune system support',
            'Antioxidant protection',
            'Supports healing',
            'May reduce infection duration',
            'Helps with stress response'
          ],
          note: 'Use buffered form to avoid stomach upset. Bowel tolerance dosing possible for acute infections.'
        },
        {
          name: 'N-Acetylcysteine (NAC)',
          dosage: '600-1200 mg daily',
          evidence: 'Good evidence',
          benefits: [
            'Breaks up mucus in lungs',
            'Powerful antioxidant',
            'Supports glutathione production',
            'May help with OCD symptoms',
            'Anti-inflammatory',
            'Helps break down biofilms'
          ],
          note: 'Take on empty stomach. Start low and increase gradually. Can cause nausea if dose too high.'
        },
        {
          name: 'Quercetin',
          dosage: '250-500 mg twice daily',
          evidence: 'Emerging evidence',
          benefits: [
            'Natural antibiotic properties',
            'Anti-inflammatory',
            'Antiviral effects',
            'Mast cell stabilizer',
            'May enhance antibiotic effectiveness'
          ],
          note: 'Take with vitamin C for better absorption. Can cause mild stomach upset.'
        },
        {
          name: 'Curcumin (Turmeric)',
          dosage: '500-1000 mg daily (with black pepper/piperine)',
          evidence: 'Strong evidence',
          benefits: [
            'Potent anti-inflammatory',
            'Reduces neuroinflammation',
            'Antioxidant properties',
            'Supports immune function',
            'May reduce autoantibody formation'
          ],
          note: 'Must include piperine (black pepper extract) or take with fats for absorption. Liposomal forms absorb best.'
        },
        {
          name: 'Elderberry',
          dosage: 'Follow product instructions (age-appropriate)',
          evidence: 'Moderate evidence',
          benefits: [
            'Antiviral and antibacterial',
            'Immune support',
            'May reduce respiratory symptoms',
            'Antioxidant properties'
          ],
          note: 'Best taken at first sign of illness. Some debate about use in autoimmune conditions - discuss with doctor.'
        },
        {
          name: 'Magnesium',
          dosage: '200-400 mg daily (age-dependent)',
          evidence: 'Moderate evidence',
          benefits: [
            'Supports immune function',
            'Reduces anxiety',
            'Improves sleep',
            'Anti-inflammatory',
            'Often deficient in children',
            'Supports over 300 enzymatic processes'
          ],
          note: 'Use magnesium glycinate for best absorption and calming effects. Give before bed.'
        },
        {
          name: 'B-Complex Vitamins',
          dosage: 'Age-appropriate B-complex',
          evidence: 'Moderate evidence',
          benefits: [
            'Energy production',
            'Immune system support',
            'Brain function',
            'Stress response',
            'Often depleted during illness'
          ],
          note: 'Use methylated forms (methylfolate, methylcobalamin) for better absorption.'
        }
      ],
      reminders: [
        'Supplements support but don\'t replace antibiotics for active infection',
        'Always inform your doctor of all supplements your child takes',
        'Start one supplement at a time to monitor for reactions',
        'Use high-quality, third-party tested brands',
        'Dosing should be appropriate for your child\'s age and weight',
        'Some supplements can interact with medications'
      ]
    },
    lifestyle: {
      isExtendedMycoplasma: true,
      title: 'Lifestyle Changes to Support Recovery',
      categories: [
        {
          name: 'Sleep Optimization',
          icon: '🌙',
          importance: 'CRITICAL',
          importanceNote: 'Sleep is when the immune system repairs and fights infection',
          items: [
            'Aim for 9-11 hours per night for school-age children',
            'Consistent bedtime and wake time (even weekends)',
            'Dark, cool room (65-68°F)',
            'No screens 1-2 hours before bed (blue light suppresses melatonin)',
            'Calming bedtime routine',
            'Consider magnesium glycinate 30-60 min before bed',
            'White noise machine if helpful',
            'Address sleep disorders (sleep apnea, restless leg) if present'
          ]
        },
        {
          name: 'Stress Reduction',
          icon: '🧘',
          importance: 'HIGH',
          importanceNote: 'Stress weakens immune function and increases inflammation',
          items: [
            'Reduce academic pressure during illness - focus on healing first',
            'Lower expectations temporarily - this is medical, not behavioral',
            'Gentle yoga or stretching',
            'Deep breathing exercises (4-7-8 technique)',
            'Mindfulness or meditation (age-appropriate)',
            'Time in nature daily if possible',
            'Creative outlets (art, music, play)',
            'Limit news/scary content',
            'Family therapy if needed to reduce household stress',
            'Consider homeschooling or reduced schedule during acute phase'
          ]
        },
        {
          name: 'Infection Prevention',
          icon: '🛡️',
          importance: 'HIGH',
          importanceNote: 'Prevent reinfection and new triggers',
          items: [
            'Frequent handwashing (20 seconds, soap and water)',
            'Avoid sharing drinks, utensils, towels',
            'Keep child home from school when sick',
            'Avoid sick contacts when possible',
            'Boost immune system with good nutrition and sleep',
            'Consider prophylactic antibiotics if recurrent infections (discuss with doctor)',
            'Address any chronic infections (sinus, dental)',
            'Probiotics to support immune system',
            'Keep up with recommended vaccines (discuss timing with PANS doctor)',
            'Treat family members if they have symptoms'
          ]
        },
        {
          name: 'Environmental Controls',
          icon: '🏠',
          importance: 'MODERATE',
          importanceNote: 'Reduce additional immune triggers',
          items: [
            'HEPA air filters in child\'s bedroom',
            'Regular dusting and vacuuming (HEPA filter vacuum)',
            'Reduce chemical exposures (fragrance-free products)',
            'Address mold if present (major immune trigger)',
            'Keep humidity 30-50% (prevents mold and dust mites)',
            'Wash bedding weekly in hot water',
            'Remove carpets if possible (harbor allergens and dust)',
            'Use non-toxic cleaning products',
            'Avoid air fresheners and scented candles',
            'Consider allergen-proof mattress and pillow covers'
          ]
        },
        {
          name: 'Physical Activity',
          icon: '🏃',
          importance: 'MODERATE',
          importanceNote: 'Support immune function but don\'t overdo',
          items: [
            'Gentle movement as tolerated (walking, light play)',
            'Avoid intense exercise during acute infection (can worsen symptoms)',
            'Listen to body - rest when needed',
            'Outdoor activity in nature (mood and immune benefits)',
            'Yoga or gentle stretching',
            'Gradually increase activity as child improves',
            'Don\'t push through fatigue',
            'Swimming can be good if tolerated (not during acute phase)',
            'Avoid competitive sports during flares (stress can worsen symptoms)'
          ]
        },
        {
          name: 'Gut Health Focus',
          icon: '🦠',
          importance: 'HIGH',
          importanceNote: '70-80% of immune system in gut',
          items: [
            'Probiotics daily (especially during and after antibiotics)',
            'Prebiotic foods (feed good bacteria)',
            'Bone broth for gut healing',
            'Avoid foods that damage gut lining (see diet tab)',
            'Fermented foods if tolerated (yogurt, kefir, sauerkraut)',
            'L-glutamine for gut repair',
            'Address food sensitivities',
            'Adequate fiber from vegetables and fruits',
            'Stay hydrated',
            'Avoid unnecessary antibiotics (but treat infections appropriately)'
          ]
        },
        {
          name: 'Hydration',
          icon: '💧',
          importance: 'HIGH',
          importanceNote: 'Essential for healing and flushing toxins',
          items: [
            'Plenty of water throughout day',
            'Herbal teas (chamomile, ginger)',
            'Bone broth (hydrating and healing)',
            'Avoid excessive juice (sugar can impair immune function)',
            'Avoid sodas and artificial drinks',
            'Add lemon to water (vitamin C)',
            'Electrolytes if needed (coconut water, natural electrolyte drinks)',
            'Monitor urine color (should be pale yellow)'
          ]
        },
        {
          name: 'Screen Time Management',
          icon: '📱',
          importance: 'MODERATE',
          importanceNote: 'Can worsen symptoms and affect sleep',
          items: [
            'Limit screen time (especially during flares)',
            'No screens 1-2 hours before bed',
            'Blue light blocking glasses if needed',
            'Encourage other activities (reading, crafts, outdoor play)',
            'Monitor content (avoid scary or stimulating content)',
            'Use parental controls',
            'Model healthy screen habits',
            'Replace screen time with connection time'
          ]
        },
        {
          name: 'Social Connection',
          icon: '👥',
          importance: 'MODERATE',
          importanceNote: 'Balance connection with infection risk',
          items: [
            'Maintain friendships as able (video calls during isolation)',
            'Small group activities better than large gatherings',
            'Outdoor activities safer than indoor',
            'Educate close friends about condition (if child comfortable)',
            'Don\'t force social situations during acute flares',
            'Family time and bonding important',
            'Support groups for child if age-appropriate',
            'Gradual reintegration as symptoms improve'
          ]
        },
        {
          name: 'Medication Compliance',
          icon: '💊',
          importance: 'CRITICAL',
          importanceNote: 'Incomplete treatment leads to persistence',
          items: [
            'Take full course of antibiotics (often 3-6 weeks for mycoplasma)',
            'Don\'t stop early even if feeling better',
            'Take at same time each day',
            'Use pill organizers or phone reminders',
            'Take with or without food as directed',
            'Probiotics 2 hours away from antibiotics',
            'Track doses to ensure none missed',
            'Report side effects to doctor (don\'t just stop)',
            'Discuss extended treatment if symptoms persist'
          ]
        }
      ]
    },
    diet: {
      isExtendedMycoplasma: true,
      title: 'Anti-Inflammatory Diet for Mycoplasma Recovery',
      intro: 'Nutrition plays a crucial role in immune function and recovery. Focus on anti-inflammatory, nutrient-dense whole foods that support healing and reduce inflammation.',
      include: {
        title: 'Foods to Include (Eat Plenty)',
        icon: '✅',
        categories: [
          {
            name: 'Vegetables (7-9 servings daily)',
            items: [
              'Leafy greens: spinach, kale, arugula, Swiss chard',
              'Cruciferous: broccoli, cauliflower, Brussels sprouts, cabbage',
              'Colorful veggies: bell peppers (all colors), carrots, beets, sweet potatoes',
              'Alliums: garlic, onions, leeks (antimicrobial properties)',
              'Others: celery, cucumber, zucchini, asparagus, mushrooms'
            ]
          },
          {
            name: 'Fruits (2-4 servings daily, emphasize berries)',
            items: [
              'Berries: blueberries, strawberries, raspberries, blackberries (highest antioxidants)',
              'Citrus: oranges, grapefruit, lemons, limes (vitamin C)',
              'Others: apples, pears, cherries, pineapple, kiwi, papaya',
              'Pomegranate (anti-inflammatory)',
              'Limit high-sugar fruits during active infection'
            ]
          },
          {
            name: 'Proteins (at every meal)',
            items: [
              'Wild-caught fish: salmon, sardines, mackerel, anchovies (omega-3s)',
              'Organic, pasture-raised chicken and turkey',
              'Grass-fed beef (moderate amounts)',
              'Eggs (if tolerated - great for choline and protein)',
              'Legumes: lentils, chickpeas, black beans (if tolerated)',
              'Bone broth (healing for gut and immune system)'
            ]
          },
          {
            name: 'Healthy Fats (essential for healing)',
            items: [
              'Extra virgin olive oil (anti-inflammatory)',
              'Avocados and avocado oil',
              'Coconut oil (antimicrobial properties)',
              'Nuts: walnuts (omega-3), almonds, pecans, macadamias',
              'Seeds: chia, flax, pumpkin, sunflower',
              'Fatty fish (listed above)',
              'Grass-fed ghee or butter (if dairy tolerated)'
            ]
          },
          {
            name: 'Herbs & Spices (medicinal properties)',
            items: [
              'Turmeric (curcumin - powerful anti-inflammatory)',
              'Ginger (anti-inflammatory, immune support)',
              'Garlic (antimicrobial, immune-boosting)',
              'Cinnamon (blood sugar regulation, antimicrobial)',
              'Oregano (antimicrobial)',
              'Basil, rosemary, thyme, sage (antioxidants)',
              'Black pepper (enhances turmeric absorption)'
            ]
          },
          {
            name: 'Beverages',
            items: [
              'Water (plenty throughout day)',
              'Bone broth',
              'Green tea (antioxidants, L-theanine)',
              'Herbal teas: ginger, chamomile, peppermint, rooibos',
              'Golden milk (turmeric latte)'
            ]
          },
          {
            name: 'Fermented Foods (gut health)',
            items: [
              'Plain yogurt (if dairy tolerated - look for live cultures)',
              'Kefir (if dairy tolerated)',
              'Sauerkraut (unpasteurized)',
              'Kimchi',
              'Kombucha (low sugar)',
              'Miso (fermented soybean paste)'
            ]
          }
        ]
      },
      avoid: {
        title: 'Foods to Limit or Avoid (Pro-Inflammatory)',
        icon: '⚠️',
        categories: [
          {
            name: 'Sugar & Sweeteners',
            reason: 'Impairs immune function, feeds bad bacteria, promotes inflammation',
            items: [
              'Refined sugar (white sugar, brown sugar)',
              'High fructose corn syrup',
              'Candy and sweets',
              'Sugary drinks (soda, juice with added sugar, sports drinks)',
              'Artificial sweeteners (aspartame, sucralose)',
              'Limit natural sweeteners (honey, maple syrup) during active infection'
            ]
          },
          {
            name: 'Processed Foods',
            reason: 'Contain inflammatory oils, additives, and lack nutrients',
            items: [
              'Fast food',
              'Packaged snacks (chips, crackers, cookies)',
              'Processed meats (hot dogs, deli meat, sausage)',
              'Frozen dinners and convenience foods',
              'Anything with long ingredient list or unpronounceable ingredients'
            ]
          },
          {
            name: 'Refined Grains',
            reason: 'Spike blood sugar, lack nutrients, can increase inflammation',
            items: [
              'White bread and white pasta',
              'White rice',
              'Pastries, donuts, muffins',
              'Most cereals',
              'Crackers and pretzels'
            ]
          },
          {
            name: 'Inflammatory Oils',
            reason: 'High in omega-6, promote inflammation',
            items: [
              'Vegetable oil',
              'Canola oil',
              'Soybean oil',
              'Corn oil',
              'Margarine and shortening',
              'Fried foods'
            ]
          },
          {
            name: 'Dairy (if sensitive)',
            reason: 'Casein can be inflammatory for some, increases mucus production',
            items: [
              'Milk',
              'Cheese',
              'Ice cream',
              'Note: Some tolerate ghee, grass-fed butter, or fermented dairy (yogurt, kefir)',
              'Consider eliminating during acute phase, reintroduce later'
            ]
          },
          {
            name: 'Gluten (if sensitive)',
            reason: 'Can increase intestinal permeability (leaky gut), promotes inflammation in sensitive individuals',
            items: [
              'Wheat products',
              'Barley, rye',
              'Most breads, pasta, baked goods',
              'Many sauces and condiments',
              'Consider elimination trial for 4-6 weeks'
            ]
          },
          {
            name: 'Additives & Chemicals',
            reason: 'Can trigger immune responses and inflammation',
            items: [
              'Artificial colors and dyes (especially Red 40, Yellow 5, Yellow 6)',
              'MSG (monosodium glutamate)',
              'Preservatives (BHT, BHA, nitrates)',
              'Artificial flavors'
            ]
          },
          {
            name: 'Alcohol & Caffeine',
            reason: 'Dehydrating, can disrupt sleep and immune function',
            items: [
              'Alcohol (obviously for adults, not children)',
              'Excessive caffeine (energy drinks)',
              'Limit coffee and black tea',
              'Green tea okay in moderation (has benefits)'
            ]
          }
        ]
      },
      sampleDay: {
        title: 'Sample Day of Eating',
        meals: [
          { name: 'Breakfast', description: 'Scrambled eggs with sautéed spinach and mushrooms, topped with avocado. Side of berries. Green tea.' },
          { name: 'Snack', description: 'Apple slices with almond butter, handful of walnuts' },
          { name: 'Lunch', description: 'Large salad with mixed greens, grilled chicken, colorful veggies, olive oil and lemon dressing. Side of sweet potato' },
          { name: 'Snack', description: 'Carrots and cucumber with hummus, or bone broth' },
          { name: 'Dinner', description: 'Baked wild salmon with roasted broccoli and cauliflower, quinoa. Turmeric-ginger tea after dinner' },
          { name: 'Before bed', description: 'Chamomile tea with a small amount of raw honey (if not avoiding sugar completely)' }
        ]
      },
      tips: {
        title: 'Practical Tips',
        items: [
          'Make changes gradually - don\'t try to overhaul everything at once',
          'Focus on adding good foods first, then work on removing bad ones',
          'Meal prep on weekends to make healthy eating easier during week',
          'Keep healthy snacks visible and accessible',
          'Hide or don\'t buy trigger foods',
          'Get whole family on board - easier when everyone eats similarly',
          'Consider working with nutritionist familiar with PANS/PANDAS',
          'Track foods and symptoms to identify personal triggers',
          'Don\'t stress about perfection - 80/20 rule (80% whole foods, 20% flexibility)',
          'During food restriction phase of PANS, nutrition is even more critical'
        ]
      }
    }
  },
  'histamine-intolerance': {
    name: 'Histamine Intolerance',
    color: 'bg-secondary',
    lightColor: 'bg-secondary/30',
    textColor: 'text-secondary-foreground',
    description: 'A condition where the body cannot properly break down histamine, leading to a buildup that causes various symptoms',
    about: {
      isExtendedHistamine: true,
      whatIs: {
        title: 'What is Histamine Intolerance?',
        description: 'Histamine intolerance is a condition where the body has difficulty breaking down histamine, leading to an accumulation that causes allergic-like symptoms. It\'s not a true allergy, but rather an imbalance between histamine intake/production and the body\'s ability to metabolize it.'
      },
      whatIsHistamine: {
        title: 'What is Histamine?',
        intro: 'Histamine is a chemical compound that plays multiple roles in the body:',
        roles: [
          { name: 'Immune response', description: 'Released during allergic reactions to defend against foreign substances' },
          { name: 'Neurotransmitter', description: 'Regulates sleep-wake cycles, alertness, learning, and memory' },
          { name: 'Digestive aid', description: 'Triggers release of stomach acid for digestion' },
          { name: 'Blood vessel regulator', description: 'Causes vasodilation (widening of blood vessels)' }
        ]
      },
      pansProblem: {
        title: 'The Histamine Problem in PANDAS/PANS',
        intro: 'Many children with PANDAS/PANS also have histamine intolerance. This connection occurs because:',
        reasons: [
          'Mast cell activation: The immune dysfunction in PANS often triggers mast cells (which store histamine)',
          'Gut inflammation: Leaky gut (common in PANS) allows more histamine into bloodstream',
          'DAO deficiency: Inflammation reduces production of DAO enzyme that breaks down histamine',
          'Infections: Certain bacteria produce histamine or block its breakdown',
          'Autoimmunity: Chronic inflammation increases histamine release'
        ]
      },
      breakdown: {
        title: 'How Histamine Breakdown Works (or Doesn\'t)',
        intro: 'The body has two main enzymes to break down histamine:',
        enzymes: [
          { name: 'DAO (Diamine Oxidase)', description: 'Made in intestinal lining, breaks down histamine from food and gut bacteria. Reduced in leaky gut, intestinal inflammation, certain medications, and genetic variants.' },
          { name: 'HNMT (Histamine N-Methyltransferase)', description: 'Works inside cells to break down histamine. Can be reduced by genetic variants and certain medications.' }
        ]
      },
      bucketTheory: {
        title: 'The Histamine Bucket Theory',
        intro: 'Think of your body\'s histamine capacity as a bucket:',
        points: [
          'Histamine comes from food, gut bacteria, mast cells, and immune reactions',
          'When the bucket is full (histamine exceeds breakdown capacity), symptoms overflow',
          'Symptoms vary based on how much histamine overflows and which receptors are activated',
          'Goal is to reduce histamine input and increase breakdown capacity to lower bucket level'
        ]
      },
      receptors: {
        title: 'Histamine Receptors & Their Effects',
        intro: 'Histamine acts on four types of receptors (H1-H4), each causing different symptoms:',
        types: [
          { name: 'H1 receptors', effects: 'Allergic symptoms (itching, sneezing, hives), anxiety, insomnia, headaches' },
          { name: 'H2 receptors', effects: 'Digestive issues (stomach acid production), heart palpitations' },
          { name: 'H3 receptors', effects: 'Brain/nervous system (mood, cognition, sleep-wake cycle)' },
          { name: 'H4 receptors', effects: 'Immune system modulation, inflammation' }
        ]
      },
      causes: {
        title: 'Causes of Histamine Intolerance',
        items: [
          'DAO enzyme deficiency: Genetic or acquired (from gut inflammation)',
          'Leaky gut: Allows more histamine absorption',
          'SIBO/dysbiosis: Bad bacteria produce histamine',
          'Mast cell activation: Excessive histamine release from mast cells',
          'Medications: Some block DAO (NSAIDs, certain antibiotics)',
          'Nutrient deficiencies: B6, copper, vitamin C, zinc needed for DAO function',
          'Hormonal influences: Estrogen can decrease DAO activity'
        ]
      },
      testing: {
        title: 'Testing for Histamine Intolerance',
        intro: 'Diagnosis is challenging as testing is limited:',
        challenges: [
          { name: 'Blood histamine', note: 'Often normal even with intolerance (histamine peaks and clears quickly)' },
          { name: 'DAO levels', note: 'Can be measured but not widely available' },
          { name: 'Most reliable', note: 'Elimination diet trial (low-histamine diet for 2-4 weeks, then reintroduce)' },
          { name: 'Symptom tracking', note: 'Note reactions to high-histamine foods' }
        ]
      }
    },
    symptoms: {
      isExtendedHistamine: true,
      intro: 'Symptoms are diverse and can affect multiple body systems. Symptoms typically occur 15 minutes to several hours after consuming high-histamine foods or during histamine release from mast cells.',
      categories: [
        {
          name: 'Neurological/Psychological',
          icon: '🧠',
          items: [
            'Headaches or migraines (often severe)',
            'Dizziness or vertigo',
            'Brain fog or difficulty concentrating',
            'Anxiety or panic attacks',
            'Irritability or mood swings',
            'Insomnia or sleep disturbances',
            'Fatigue (especially after meals)',
            'Confusion or disorientation'
          ]
        },
        {
          name: 'Respiratory',
          icon: '🫁',
          items: [
            'Nasal congestion or runny nose',
            'Sneezing',
            'Post-nasal drip',
            'Asthma symptoms or wheezing',
            'Difficulty breathing or shortness of breath',
            'Throat tightness or clearing'
          ]
        },
        {
          name: 'Digestive',
          icon: '🤢',
          items: [
            'Abdominal pain or cramping',
            'Bloating and gas',
            'Diarrhea (often within hours of eating trigger foods)',
            'Nausea or vomiting',
            'Acid reflux or heartburn',
            'Constipation (less common)'
          ]
        },
        {
          name: 'Skin',
          icon: '🔴',
          items: [
            'Hives or rashes',
            'Itching (can be intense)',
            'Flushing (red face/neck/chest)',
            'Eczema flares',
            'Dry skin',
            'Swelling (lips, face, hands)'
          ]
        },
        {
          name: 'Cardiovascular',
          icon: '❤️',
          items: [
            'Heart palpitations or rapid heartbeat',
            'Low blood pressure (can cause dizziness)',
            'High blood pressure (less common)',
            'Chest tightness',
            'Arrhythmias'
          ]
        },
        {
          name: 'Other',
          icon: '⚡',
          items: [
            'Temperature regulation issues (feeling too hot/cold)',
            'Menstrual irregularities or worsening symptoms with cycle',
            'Urinary urgency or frequency',
            'Joint or muscle pain',
            'Eye redness or itching',
            'Sensitivity to smells'
          ]
        }
      ],
      importantNotes: [
        'Symptoms vary widely - you may have some but not others',
        'Symptom severity depends on histamine load (bucket theory)',
        'You may tolerate high-histamine foods sometimes but not others (depends on bucket level)',
        'Symptoms often worsen during PANS flares (increased inflammation → more histamine)',
        'Multiple symptoms occurring together after eating certain foods is key diagnostic clue'
      ]
    },
    triggers: {
      isExtendedHistamine: true,
      howItAccumulates: {
        title: 'How Histamine Accumulates',
        steps: [
          'Dietary histamine: Consuming foods high in histamine',
          'Histamine liberators: Foods that trigger mast cells to release histamine',
          'DAO blockers: Foods/substances that inhibit DAO enzyme',
          'Endogenous production: Body\'s own histamine from mast cells, bacteria',
          'Reduced breakdown: When enzymes can\'t keep up with histamine load'
        ]
      },
      factorsIncrease: {
        title: 'Factors That Increase Histamine',
        items: [
          'Infections: Bacterial, viral, parasitic - all trigger immune response and histamine',
          'Gut dysbiosis: Imbalanced gut bacteria produce excess histamine',
          'Leaky gut: Increased intestinal permeability allows more histamine into bloodstream',
          'Allergies: Environmental allergies trigger mast cell degranulation',
          'Stress: Physical or emotional stress triggers histamine release',
          'Hormones: Estrogen decreases DAO activity (symptoms worse with menstruation)',
          'Temperature extremes: Very hot or cold can trigger histamine release',
          'Exercise: Intense exercise can trigger histamine (though moderate exercise helps)',
          'Poor sleep: Sleep deprivation increases inflammation and histamine',
          'Mold exposure: Major trigger for mast cell activation'
        ]
      },
      medications: {
        title: 'Medications That Affect Histamine',
        intro: 'Medications that block DAO or increase histamine:',
        items: [
          'NSAIDs: Ibuprofen, naproxen (block DAO)',
          'Some antibiotics: Especially fluoroquinolones',
          'Muscle relaxants: Some can trigger histamine release',
          'Contrast dyes: Used in imaging',
          'Some local anesthetics'
        ],
        warning: 'Don\'t stop medications without consulting doctor - discuss alternatives if problematic'
      },
      environmental: {
        title: 'Environmental Triggers',
        items: [
          'Mold: Major mast cell activator',
          'Dust mites',
          'Pollen (seasonal allergies)',
          'Pet dander',
          'Fragrances and strong smells',
          'Chemicals: Cleaning products, air fresheners, perfumes'
        ]
      }
    },
    supplements: {
      isExtendedHistamine: true,
      intro: 'These supplements can help reduce histamine levels, support DAO enzyme function, stabilize mast cells, or reduce inflammation.',
      items: [
        {
          name: 'DAO (Diamine Oxidase) Enzyme',
          dosage: 'Take 1-2 capsules 15-20 minutes before meals',
          evidence: 'Strong clinical evidence',
          benefits: [
            'Directly supplements the enzyme that breaks down dietary histamine',
            'Helps digest high-histamine foods when accidental exposure occurs',
            'Can allow some dietary flexibility',
            'Most effective for food-related histamine symptoms',
            'Works in gut before histamine absorbed'
          ],
          note: 'Not a cure - addresses symptoms, not root cause. Expensive but very effective for food-triggered symptoms. Brands: Histamine Block, DAOsin, Umbrellux'
        },
        {
          name: 'Quercetin',
          dosage: '500-1000 mg twice daily',
          evidence: 'Strong evidence',
          benefits: [
            'Natural antihistamine',
            'Mast cell stabilizer (prevents histamine release)',
            'Anti-inflammatory',
            'Antioxidant properties',
            'Supports immune function',
            'May reduce allergic responses'
          ],
          note: 'Take with vitamin C and bromelain for enhanced absorption. Take between meals for best effect. May take 2-4 weeks for full benefit.'
        },
        {
          name: 'Vitamin C',
          dosage: '500-1000 mg two to three times daily',
          evidence: 'Strong evidence',
          benefits: [
            'Helps break down histamine',
            'Natural antihistamine effect',
            'Supports DAO enzyme function',
            'Mast cell stabilizer',
            'Antioxidant',
            'Immune support'
          ],
          note: 'Use buffered form (ascorbate) to prevent stomach upset. Split doses throughout day. Higher doses may cause loose stools - reduce if needed.'
        },
        {
          name: 'Vitamin B6 (P5P form)',
          dosage: '25-50 mg daily',
          evidence: 'Moderate evidence',
          benefits: [
            'Cofactor for DAO enzyme',
            'Helps body produce DAO',
            'Supports histamine breakdown',
            'Reduces hormonal effects on histamine (helps with menstrual flares)'
          ],
          note: 'Use P5P (pyridoxal-5-phosphate) - active form. Often deficient in histamine intolerance. Part of B-complex is fine too.'
        },
        {
          name: 'Copper',
          dosage: '1-2 mg daily (careful with dosing)',
          evidence: 'Moderate evidence',
          benefits: [
            'Essential cofactor for DAO enzyme',
            'DAO cannot function without adequate copper',
            'Many people deficient'
          ],
          note: 'Test levels first if possible. Too much copper problematic. Often included in quality multivitamins. Balance with zinc (10:1 zinc to copper).'
        },
        {
          name: 'Zinc',
          dosage: '15-30 mg daily',
          evidence: 'Good evidence',
          benefits: [
            'Supports DAO function',
            'Mast cell stabilizer',
            'Immune system support',
            'Anti-inflammatory',
            'Often deficient in children'
          ],
          note: 'Take with food. Use zinc picolinate or glycinate. Balance with copper. Many with histamine intolerance are zinc deficient.'
        },
        {
          name: 'Stinging Nettle (Urtica dioica)',
          dosage: '300-600 mg daily',
          evidence: 'Moderate evidence',
          benefits: [
            'Natural antihistamine',
            'Reduces allergic responses',
            'Anti-inflammatory',
            'Traditionally used for allergies',
            'May help with seasonal allergies'
          ],
          note: 'Freeze-dried form most effective. Can also drink as tea. Safe for long-term use.'
        },
        {
          name: 'Bromelain',
          dosage: '500-1000 mg between meals',
          evidence: 'Moderate evidence',
          benefits: [
            'Anti-inflammatory enzyme',
            'May help reduce histamine',
            'Supports digestion',
            'Enhances quercetin absorption',
            'Reduces inflammation'
          ],
          note: 'Derived from pineapple. Take on empty stomach for anti-inflammatory effect, with meals for digestive support. Often combined with quercetin.'
        },
        {
          name: 'Omega-3 Fatty Acids',
          dosage: '1000-2000 mg EPA+DHA daily',
          evidence: 'Strong evidence',
          benefits: [
            'Powerful anti-inflammatory',
            'Mast cell stabilizer',
            'Reduces allergic responses',
            'Supports brain health',
            'Improves cell membrane integrity'
          ],
          note: 'Use high-quality, tested for purity. Take with meals. Higher EPA formulas often better for inflammation.'
        },
        {
          name: 'Vitamin D3',
          dosage: '2000-4000 IU daily (test levels)',
          evidence: 'Strong evidence',
          benefits: [
            'Immune regulation',
            'Reduces mast cell activation',
            'Anti-inflammatory',
            'Supports DAO production',
            'Many with histamine issues deficient'
          ],
          note: 'Test levels - aim for 50-80 ng/mL. Take with K2 and fat. Deficiency common in PANS and histamine intolerance.'
        },
        {
          name: 'Probiotics (specific strains)',
          dosage: 'Multi-strain, 25-50 billion CFU daily',
          evidence: 'Moderate evidence',
          benefits: [
            'Support gut health',
            'Some strains break down histamine',
            'Reduce gut inflammation',
            'Support DAO production in gut lining'
          ],
          note: 'CRITICAL: Avoid histamine-producing strains (Lactobacillus casei, L. reuteri, L. bulgaricus). Choose: Bifidobacterium infantis, B. longum, B. lactis, Lactobacillus plantarum, L. rhamnosus. Seeking Health brand ProBiota HistaminX specifically designed for histamine intolerance.'
        },
        {
          name: 'L-Glutamine',
          dosage: '3-5 grams daily',
          evidence: 'Good evidence',
          benefits: [
            'Heals leaky gut',
            'Reduces intestinal permeability',
            'Supports gut lining where DAO produced',
            'Anti-inflammatory for gut'
          ],
          note: 'Take on empty stomach. Powder form often most economical. Key for addressing root cause (leaky gut).'
        },
        {
          name: 'Curcumin (Turmeric)',
          dosage: '500-1000 mg daily',
          evidence: 'Strong evidence',
          benefits: [
            'Potent anti-inflammatory',
            'Mast cell stabilizer',
            'Reduces histamine release',
            'Antioxidant',
            'Supports overall healing'
          ],
          note: 'Must include piperine (black pepper) or use liposomal form for absorption. Take with fats.'
        },
        {
          name: 'Magnesium',
          dosage: '200-400 mg daily',
          evidence: 'Moderate evidence',
          benefits: [
            'Mast cell stabilizer',
            'Calming effect',
            'Supports sleep',
            'Anti-inflammatory',
            'Often deficient'
          ],
          note: 'Use magnesium glycinate for best absorption and calming effects. Take before bed.'
        },
        {
          name: 'N-Acetylcysteine (NAC)',
          dosage: '600-1200 mg daily',
          evidence: 'Moderate evidence',
          benefits: [
            'Powerful antioxidant',
            'Reduces inflammation',
            'Supports glutathione production',
            'May help with OCD symptoms',
            'Thins mucus'
          ],
          note: 'Take on empty stomach. Start low and increase gradually.'
        }
      ],
      protocol: {
        title: 'Starting a Supplement Protocol',
        items: [
          'Start ONE supplement at a time (wait 3-5 days before adding another)',
          'Start with low doses and increase gradually',
          'Track symptoms to see what helps',
          'Work with knowledgeable practitioner',
          'Core supplements: Quercetin, Vitamin C, DAO enzyme (with meals), quality probiotic',
          'Supplements support but don\'t replace low-histamine diet initially',
          'Quality matters - use third-party tested brands'
        ]
      }
    },
    lifestyle: {
      isExtendedHistamine: true,
      title: 'Lifestyle Management for Histamine Intolerance',
      intro: 'Managing histamine intolerance requires a comprehensive approach addressing diet, stress, sleep, gut health, and environmental factors. The goal is to reduce histamine load and support the body\'s ability to break down histamine.',
      categories: [
        {
          icon: '🍽️',
          name: 'Food Freshness & Preparation',
          importance: 'CRITICAL',
          importanceNote: '',
          items: [
            'Eat food immediately after cooking: Histamine increases rapidly in cooked food. No leftovers allowed - this is non-negotiable.',
            'Buy fresh, use immediately: Meat and fish should be eaten within 24 hours of purchase or frozen immediately.',
            'Freeze properly: If not eating right away, freeze meat/fish in individual portions immediately. Don\'t let sit in fridge.',
            'Thaw carefully: Thaw in refrigerator, not on counter. Cook immediately after thawing.',
            'Avoid restaurants initially: You can\'t control freshness or ingredients. Cook at home where you control everything.',
            'Meal prep differently: Can\'t make big batches. Prep ingredients (wash veggies, portion meat) but cook fresh each meal.',
            'Use fresh herbs not dried: Dried herbs have higher histamine. Fresh is better.',
            'Check expiration dates: Even "fresh" food at end of shelf life has elevated histamine.',
            'Fast food is impossible: All pre-made, sitting under heat lamps. Histamine skyrockets.',
            'Batch cooking alternative: Cook and freeze immediately in single portions, then reheat once (not multiple times).'
          ]
        },
        {
          icon: '🌙',
          name: 'Sleep Optimization',
          importance: 'CRITICAL',
          importanceNote: '',
          items: [
            '9-11 hours nightly for children: Sleep deprivation dramatically increases histamine levels and worsens all symptoms.',
            'Consistent sleep schedule: Same bedtime and wake time every day. Body\'s histamine rhythm depends on circadian regulation.',
            'Cool bedroom (65-68°F): Heat triggers histamine release. Keep room cool.',
            'Dark room: Blackout curtains or eye mask. Light disrupts melatonin which regulates histamine.',
            'No screens 2 hours before bed: Blue light increases histamine and disrupts sleep hormones.',
            'Calming bedtime routine: Warm (not hot) bath with Epsom salts, reading, gentle stretching.',
            'Magnesium before bed: 200-400mg magnesium glycinate 30-60 min before sleep. Calms and stabilizes mast cells.',
            'Chamomile or rooibos tea: Natural calming, safe for histamine intolerance.',
            'Elevate head slightly: If reflux is issue (histamine increases stomach acid).',
            'Air purifier in bedroom: HEPA filter removes allergens that trigger histamine at night.'
          ]
        },
        {
          icon: '🧘',
          name: 'Stress Management',
          importance: 'HIGH',
          importanceNote: '',
          items: [
            'Stress triggers mast cells: Both emotional and physical stress cause mast cell degranulation and histamine release.',
            'Deep breathing daily: 4-7-8 technique, box breathing. Activates parasympathetic nervous system (calming).',
            'Meditation or mindfulness: Even 5-10 minutes daily. Apps: Headspace, Calm, Insight Timer.',
            'Gentle yoga: Reduces stress hormones, improves vagal tone. Avoid hot yoga (temperature trigger).',
            'Time in nature: 20+ minutes outdoors daily. Reduces cortisol, supports healing.',
            'Journaling: Process emotions, identify stress patterns.',
            'Therapy/counseling: Professional support for managing chronic illness stress.',
            'Lower expectations during healing: This is medical treatment time, not productivity time.',
            'Say no to non-essentials: Protect energy. Overcommitment = stress = histamine.',
            'Support groups: Connect with others who understand. Reduces isolation and validates experience.',
            'Creative outlets: Art, music, gentle hobbies. Stress release without words.'
          ]
        },
        {
          icon: '🦠',
          name: 'Gut Health Priority',
          importance: 'HIGH',
          importanceNote: '',
          items: [
            'DAO enzyme made in gut: Healing gut lining increases DAO production and histamine breakdown capacity.',
            'L-glutamine daily: 3-5 grams on empty stomach. Directly heals intestinal lining.',
            'Probiotics (right strains): Bifidobacterium and specific Lactobacillus strains. AVOID histamine-producing strains.',
            'Prebiotic foods: Feed good bacteria. Cooked and cooled potatoes, asparagus, garlic (if tolerated).',
            'Bone broth: Rich in collagen, glutamine, minerals. Heals gut lining. Drink daily if possible.',
            'Avoid gut irritants: Alcohol, NSAIDs, processed foods damage gut lining and reduce DAO.',
            'Address dysbiosis: Some bacteria produce histamine. Test for SIBO (small intestinal bacterial overgrowth) if chronic gut issues.',
            'Digestive enzymes: Support digestion, reduce gut inflammation. Take with meals.',
            'Chew food thoroughly: Better digestion = less gut stress = more DAO production.',
            'Don\'t eat when stressed: Stress shuts down digestion. Eat in calm state when possible.',
            'Identify food sensitivities: Gluten, dairy, eggs - common culprits that damage gut. Eliminate and test.'
          ]
        },
        {
          icon: '🏠',
          name: 'Environmental Controls',
          importance: 'MODERATE-HIGH',
          importanceNote: '',
          items: [
            'Mold is enemy #1: Mold exposure major mast cell trigger. If you suspect mold, test and remediate professionally.',
            'HEPA air filters: Bedroom at minimum, whole house if possible. Removes airborne allergens that trigger histamine.',
            'Dehumidifier: Keep humidity 30-50%. Prevents mold and dust mite growth.',
            'Dust mite control: Allergen-proof mattress and pillow covers, wash bedding weekly in hot water (130°F+).',
            'Remove carpets: Harbor allergens, dust, mold. Hard floors much easier to keep clean.',
            'Fragrance-free everything: Laundry detergent, soaps, shampoos, cleaning products. Fragrances trigger mast cells.',
            'No air fresheners, candles, perfumes: Chemical fragrances are huge triggers.',
            'Natural cleaning products: Vinegar (if tolerated), baking soda, hydrogen peroxide for most cleaning.',
            'Vacuum with HEPA filter: Regular vacuums blow allergens into air. HEPA filter traps them.',
            'Shoes off at door: Don\'t track outdoor allergens and pollutants through house.',
            'Shower before bed: Removes pollen and allergens from hair and skin before sleeping.'
          ]
        },
        {
          icon: '🌡️',
          name: 'Temperature Management',
          importance: 'MODERATE',
          importanceNote: '',
          items: [
            'Heat triggers histamine: Both external heat and internal overheating cause histamine release.',
            'Keep cool: Air conditioning helpful. Cool bedroom especially important.',
            'Avoid hot showers/baths: Lukewarm or warm only. Hot water triggers flushing and histamine.',
            'Dress in layers: Easy to adjust as needed. Natural fabrics (cotton, bamboo) breathe better.',
            'Avoid overheating during exercise: Exercise in cool environment, stay hydrated, don\'t push too hard.',
            'Cold triggers too: Sudden cold can also trigger histamine. Avoid temperature extremes either direction.',
            'Gradual temperature changes: Don\'t go from hot to cold quickly (like hot room to air conditioning).',
            'Cool compress for flares: During histamine reaction, cool compress on face/neck can help.',
            'Avoid saunas, hot tubs: Too much heat stress for histamine-intolerant bodies.',
            'Summer challenges: Harder to manage. Air conditioning, staying indoors during peak heat, cool foods.'
          ]
        },
        {
          icon: '🏃',
          name: 'Exercise & Movement',
          importance: 'MODERATE',
          importanceNote: '',
          items: [
            'Moderate exercise helps: Light to moderate exercise reduces inflammation. Intense exercise triggers histamine.',
            'Walking ideal: 20-30 minutes daily. Outdoor walking combines movement + nature benefits.',
            'Gentle yoga or stretching: Calming, strengthening, doesn\'t trigger histamine release.',
            'Swimming (if tolerated): Low-impact, full-body. Chlorine may bother some - rinse immediately after.',
            'Avoid HIIT, intense cardio: High-intensity exercise triggers histamine release. Save for when you\'re better.',
            'Listen to your body: If exercise makes symptoms worse, back off. You\'re not lazy - you\'re listening.',
            'Cool environment for exercise: Don\'t overheat. Exercise in air conditioning or cool weather.',
            'Stay hydrated: Dehydration worsens histamine symptoms.',
            'Morning or evening best: Avoid midday heat if exercising outdoors.',
            'Build gradually: As tolerance improves, slowly increase intensity. Don\'t rush.'
          ]
        },
        {
          icon: '📊',
          name: 'Symptom Tracking',
          importance: 'HIGH',
          importanceNote: '',
          items: [
            'Food-symptom diary essential: Track everything you eat and all symptoms. Patterns will emerge.',
            'Track timing: Note when symptoms occur relative to meals (15 min - 4 hours typical).',
            'Rate severity: Use 1-10 scale for each symptom. Helps see progress over time.',
            'Note environment: Where you were, temperature, stress level, sleep quality night before.',
            'Track menstrual cycle: For females - symptoms often worse during certain phases. Estrogen affects DAO.',
            'Photo documentation: Take photos of skin reactions, swelling. Shows patterns and severity.',
            'Use the tracker in this app: Log symptoms and triggers consistently.',
            'Review weekly: Look for patterns. What foods always cause problems? What helps?',
            'Share with providers: Data helps doctors understand and adjust treatment.',
            'Celebrate improvements: Track wins too. Notice when symptoms decrease.'
          ]
        },
        {
          icon: '💊',
          name: 'Medication Awareness',
          importance: 'MODERATE',
          importanceNote: '',
          items: [
            'NSAIDs block DAO: Ibuprofen, aspirin, naproxen reduce DAO enzyme activity. Avoid if possible.',
            'Alternative for pain: Acetaminophen (Tylenol) doesn\'t affect DAO. Use instead of NSAIDs.',
            'Some antibiotics problematic: Fluoroquinolones can increase histamine. Discuss with doctor if prescribed.',
            'Antihistamines may be needed: H1 blockers (Zyrtec, Claritin, Allegra) and/or H2 blockers (Pepcid/famotidine).',
            'Inform all doctors: About histamine intolerance before prescribing anything. Some meds trigger histamine.',
            'Contrast dyes: Used in CT/MRI can trigger severe reactions. Pre-medicate with antihistamines.',
            'Anesthesia: Some anesthetics trigger histamine. Inform anesthesiologist. May need pre-medication.',
            'Keep emergency antihistamine: Have Benadryl (diphenhydramine) on hand for acute reactions.',
            'Don\'t stop meds without doctor: But do discuss alternatives if medications worsening symptoms.'
          ]
        },
        {
          icon: '👥',
          name: 'Social & Practical Life',
          importance: 'MODERATE',
          importanceNote: '',
          items: [
            'Eating out is challenging: When you must, choose simple grilled proteins and vegetables. Ask about freshness.',
            'Bring your own food: To parties, events, travel. Don\'t rely on others understanding.',
            'Educate close family/friends: So they understand why you can\'t eat leftovers or certain foods.',
            'Don\'t apologize: This is medical necessity, not preference. You don\'t owe explanations.',
            'Plan ahead for travel: Bring safe snacks, research restaurants, book accommodations with kitchen.',
            'Cooler/lunch box essential: Carry safe foods with you. Ice packs keep things fresh.',
            'School considerations: Child may need to bring all meals/snacks. Educate school about freshness requirements.',
            'Medical alert bracelet: Consider if reactions severe. Indicates histamine intolerance.',
            'Community support: Online groups for people with histamine intolerance. Recipe sharing, support, validation.'
          ]
        },
        {
          icon: '🔄',
          name: 'Hormonal Considerations (for females)',
          importance: 'MODERATE',
          importanceNote: '',
          items: [
            'Estrogen reduces DAO: Higher estrogen = less DAO = more histamine symptoms.',
            'Track menstrual cycle: Symptoms often worse during ovulation and right before period (high estrogen times).',
            'Be extra strict during high-estrogen times: Follow low-histamine diet more carefully those days.',
            'Consider supplement support: DIM (diindolylmethane) or calcium-d-glucarate support estrogen metabolism.',
            'Birth control can worsen: Hormonal birth control raises estrogen. Discuss non-hormonal options with doctor.',
            'Perimenopause/menopause: Fluctuating hormones can worsen symptoms. May improve after menopause stabilizes.',
            'Pregnancy considerations: Some improve (progesterone helps), some worsen. Work closely with OB.',
            'Support liver detoxification: Liver clears excess estrogen. Cruciferous vegetables, milk thistle support liver.'
          ]
        },
        {
          icon: '📈',
          name: 'Building Tolerance Back',
          importance: 'MODERATE (after stabilization)',
          importanceNote: '',
          items: [
            'Strict elimination first: 2-4 weeks very strict low-histamine diet. Lower the bucket.',
            'Wait for symptom improvement: Don\'t rush reintroduction. Let body heal.',
            'One food at a time: Add back one new food every 3-5 days. Track response carefully.',
            'Start with moderate-histamine foods: Don\'t jump to highest histamine foods first.',
            'Small portions initially: Quarter serving, see how you react over 24-48 hours.',
            'If reaction occurs: Stop that food, go back to baseline, wait until symptoms clear, then try different food.',
            'Keep tolerance journal: What you can tolerate, how much, how often.',
            'Tolerance may vary: Might tolerate food one day but not another (depends on bucket level that day).',
            'Some foods may never be tolerated: That\'s okay. Focus on what you CAN eat.',
            'Goal: maximum diet variety without symptoms: Find your personal threshold.'
          ]
        }
      ],
      keyTakeaways: [
        'Freshness is the #1 rule - histamine increases over time in all foods',
        'Gut health is foundation - heal the gut, improve DAO production',
        'Sleep and stress management non-negotiable - both dramatically affect histamine',
        'This gets easier - as you heal, tolerance typically improves',
        'Track everything - you\'ll identify your unique triggers and tolerance levels',
        'Be patient - healing takes time, typically 3-6 months to see significant improvement',
        'Working with knowledgeable practitioner makes huge difference'
      ]
    },
    diet: {
      isExtendedHistamine: true,
      title: 'Low-Histamine Diet',
      intro: 'The foundation of managing histamine intolerance. Follow strictly for 2-4 weeks to lower histamine bucket, then slowly reintroduce foods to find personal tolerance.',
      safeFoods: {
        title: 'LOW-HISTAMINE FOODS (Safe to Eat)',
        icon: '✅',
        categories: [
          { name: 'Fresh meat & poultry', items: 'Freshly cooked chicken, turkey, lamb. Must be very fresh - freeze immediately if not eating within 24hrs.' },
          { name: 'Fresh fish', items: 'Freshly caught white fish (cod, halibut, sole). Must be extremely fresh. Avoid all aged/smoked fish.' },
          { name: 'Eggs', items: 'Generally tolerated (unless specific sensitivity)' },
          { name: 'Most vegetables', items: 'Leafy greens, broccoli, cauliflower, Brussels sprouts, cabbage, cucumber, asparagus, celery, carrots, beets, sweet potatoes, zucchini, butternut squash' },
          { name: 'Most fresh fruits', items: 'Apples, pears, peaches, nectarines, apricots, mangos, melons, grapes, cherries, blueberries, blackberries (avoid strawberries initially)' },
          { name: 'Oils', items: 'Olive oil, coconut oil' },
          { name: 'Herbs', items: 'Most fresh herbs - basil, cilantro, parsley, sage, rosemary' },
          { name: 'Grains', items: 'Rice, quinoa, oats, millet' },
          { name: 'Dairy alternatives', items: 'Coconut milk, rice milk, oat milk' },
          { name: 'Beverages', items: 'Water, herbal teas (chamomile, peppermint, rooibos)' }
        ]
      },
      avoidFoods: {
        title: 'HIGH-HISTAMINE FOODS (Avoid Completely)',
        icon: '🔴',
        categories: [
          { name: 'Fermented foods', items: 'Sauerkraut, kimchi, kombucha, kefir, yogurt, aged cheese, soy sauce, miso, tempeh' },
          { name: 'Aged/cured meats', items: 'Salami, pepperoni, bacon, ham, hot dogs, lunch meat' },
          { name: 'Fish', items: 'Tuna, mackerel, sardines, anchovies, shellfish, smoked fish. Any fish that\'s not ultra-fresh.' },
          { name: 'Alcohol', items: 'Especially wine (red worse than white), beer, champagne' },
          { name: 'Fermented dairy', items: 'Aged cheese, sour cream, buttermilk, yogurt' },
          { name: 'Vinegar', items: 'All types except apple cider vinegar (some tolerate)' },
          { name: 'Citrus', items: 'Oranges, lemons, limes, grapefruit' },
          { name: 'Berries', items: 'Strawberries (highest), raspberries' },
          { name: 'Dried fruit', items: 'Raisins, dates, apricots, prunes' },
          { name: 'Nuts', items: 'Walnuts, cashews, peanuts' },
          { name: 'Tomatoes', items: 'Tomatoes and tomato products: Ketchup, tomato sauce, paste' },
          { name: 'Spinach', items: 'Despite being healthy, relatively high histamine' },
          { name: 'Eggplant', items: 'High histamine vegetable' },
          { name: 'Avocado', items: 'Controversial - some tolerate, some don\'t' },
          { name: 'Soy products', items: 'Tofu, soy sauce' },
          { name: 'Leftovers', items: 'Histamine increases in food over time - eat fresh only' }
        ]
      },
      liberators: {
        title: 'HISTAMINE LIBERATORS (Trigger Release)',
        icon: '⚠️',
        intro: 'These foods don\'t contain histamine but trigger your body to release it:',
        items: [
          'Strawberries, citrus fruits, pineapple, papaya, kiwi',
          'Chocolate',
          'Alcohol',
          'Shellfish',
          'Tomatoes',
          'Nuts (especially peanuts, cashews, walnuts)',
          'Food additives and preservatives',
          'Spicy foods'
        ]
      },
      guidelines: {
        title: 'Critical Guidelines',
        items: [
          'FRESHNESS IS EVERYTHING - histamine increases rapidly in food after slaughter/harvest',
          'Cook and eat immediately - no leftovers (histamine increases in cooked food too)',
          'Freeze fresh meat/fish immediately if not eating within 24 hours',
          'Thaw in refrigerator and cook immediately',
          'Read all labels - many foods contain hidden high-histamine ingredients',
          'Everyone\'s tolerance different - these are guidelines, track YOUR responses',
          'May be able to tolerate some "moderate" histamine foods once bucket lowered',
          'Typical trial: 2-4 weeks strict low-histamine, then gradual reintroduction'
        ]
      }
    }
  },
  'mast-cell-activation': {
    name: 'Mast Cell Activation',
    color: 'bg-purple-500',
    lightColor: 'bg-purple-100',
    textColor: 'text-purple-700',
    description: 'A condition where mast cells release excessive mediators including histamine, causing systemic symptoms',
    emergencyQuickRef: {
      title: 'MCAS Emergency Quick Reference',
      subtitle: 'Print this card and keep it in wallet, car, and at home',
      preMedicationProtocol: {
        title: 'Pre-Medication Protocol',
        subtitle: '(Before ANY medical/dental procedure)',
        steps: [
          '12 hours before: H1 antihistamine (Zyrtec, Claritin, or Allegra) + H2 blocker (Pepcid/famotidine)',
          '1 hour before: Repeat H1 + H2 antihistamines',
          'Day of: Bring emergency meds to appointment',
          'After procedure: Continue antihistamines for 24-48 hours',
          'Inform ALL providers about MCAS before any procedure'
        ]
      },
      epiPenInstructions: {
        title: 'EpiPen Instructions',
        whenToUse: [
          'Throat tightening or swelling',
          'Difficulty breathing or wheezing',
          'Feeling faint or losing consciousness',
          'Severe drop in blood pressure',
          'If unsure - USE IT (better safe than sorry)'
        ],
        howToUse: [
          '1. Remove blue safety cap',
          '2. Hold orange tip against outer thigh (through clothes OK)',
          '3. Press firmly until click - hold 10 seconds',
          '4. Call 911 immediately after',
          '5. Go to ER even if feeling better (biphasic reactions possible)'
        ]
      },
      emergencyMeds: {
        title: 'Emergency Medication Kit',
        items: [
          'EpiPen (2) - check expiration monthly',
          'Benadryl (diphenhydramine) 25-50mg',
          'H2 blocker (famotidine/Pepcid)',
          'Rescue inhaler (if prescribed)',
          'Prednisone (if prescribed by doctor)',
          'Copy of this card'
        ]
      },
      tellER: {
        title: 'Tell Emergency Room Staff',
        items: [
          'I have Mast Cell Activation Syndrome (MCAS)',
          'I may have anaphylaxis risk',
          'I need latex-free environment',
          'Some medications trigger my mast cells',
          'I have a safe medication list (show them)'
        ]
      },
      emergencyContacts: {
        title: 'Emergency Contacts',
        note: 'Fill in your personal information:',
        fields: [
          'MCAS Doctor: ___________________ Phone: ___________________',
          'Primary Care: ___________________ Phone: ___________________',
          'Emergency Contact: ___________________ Phone: ___________________',
          'Allergies: ___________________',
          'Current Medications: ___________________'
        ]
      }
    },
    about: {
      isExtendedMCAS: true,
      whatIs: {
        title: 'What is Mast Cell Activation Syndrome?',
        description: 'Mast Cell Activation Syndrome (MCAS) is a condition where mast cells - a type of immune cell - inappropriately and excessively release chemical mediators (like histamine) in response to triggers, causing a wide array of symptoms affecting multiple body systems.'
      },
      whatAreMastCells: {
        title: 'What Are Mast Cells?',
        intro: 'Mast cells are white blood cells found throughout the body, particularly concentrated in:',
        locations: [
          { name: 'Skin', description: 'First line of defense against external threats' },
          { name: 'Gut lining', description: 'Protect against pathogens and toxins' },
          { name: 'Respiratory tract', description: 'Nose, throat, lungs' },
          { name: 'Near blood vessels and nerves', description: 'Throughout body' }
        ],
        normalFunction: 'Normal mast cell function: Mast cells are guardians of the body. When they detect a threat (allergen, pathogen, injury), they release chemical mediators to trigger appropriate immune response.'
      },
      whatGoesWrong: {
        title: 'What Goes Wrong in MCAS?',
        intro: 'In MCAS, mast cells become hyperreactive and "trigger-happy." They:',
        points: [
          'Release mediators in response to normally harmless stimuli',
          'Release excessive amounts of mediators',
          'Release mediators spontaneously without clear trigger',
          'Don\'t properly regulate themselves after activation'
        ]
      },
      mediators: {
        title: 'Mast Cell Mediators (Over 200!)',
        intro: 'Mast cells contain granules packed with over 200 different chemical mediators. Key ones include:',
        items: [
          { name: 'Histamine', description: 'Most famous mediator - causes allergic symptoms, inflammation, vasodilation' },
          { name: 'Tryptase', description: 'Enzyme that causes inflammation; blood test marker for MCAS' },
          { name: 'Heparin', description: 'Blood thinner, can cause bruising' },
          { name: 'Prostaglandins', description: 'Cause pain, inflammation, GI symptoms' },
          { name: 'Leukotrienes', description: 'Cause airway constriction, inflammation' },
          { name: 'Cytokines', description: 'IL-6, TNF-alpha, and others cause widespread inflammation' }
        ]
      },
      pansConnection: {
        title: 'The PANDAS/PANS Connection',
        intro: 'MCAS and PANDAS/PANS frequently co-occur. The connection:',
        points: [
          'Infections trigger mast cells: The infections that trigger PANS also activate mast cells',
          'Autoimmune connection: Autoantibodies can activate mast cells',
          'Inflammation amplifies both: PANS causes inflammation, which activates mast cells, which cause more inflammation (vicious cycle)',
          'Brain mast cells: Mast cells in the brain can directly affect neurological function',
          'Worsens PANS symptoms: Mast cell mediators can worsen anxiety, OCD, behavioral symptoms',
          'Genetic susceptibility: Same genetic factors may predispose to both conditions'
        ]
      },
      types: {
        title: 'Types of Mast Cell Disorders',
        items: [
          { name: 'MCAS (Mast Cell Activation Syndrome)', description: 'Normal number of mast cells but they\'re hyperreactive. Most common type. Diagnosis based on symptoms, response to treatment, and sometimes mediator levels.' },
          { name: 'Mastocytosis', description: 'Abnormal accumulation of mast cells in tissues. Rare. Diagnosed by biopsy and genetic testing.' },
          { name: 'HαT (Hereditary Alpha Tryptasemia)', description: 'Genetic condition with extra copies of tryptase gene. Causes MCAS-like symptoms.' }
        ]
      },
      diagnosticChallenges: {
        title: 'Diagnostic Challenges',
        intro: 'MCAS is notoriously difficult to diagnose:',
        challenges: [
          { name: 'Symptoms episodic', note: 'Symptoms come and go, may be normal between episodes' },
          { name: 'No single test', note: 'Diagnosis based on clinical picture plus lab support' },
          { name: 'Mediator testing tricky', note: 'Mediators release in episodes, must catch during flare' },
          { name: 'Many doctors unfamiliar', note: 'Relatively newly recognized condition' },
          { name: 'Mimics other conditions', note: 'Can look like allergies, IBS, POTS, anxiety disorders' }
        ]
      },
      diagnosticCriteria: {
        title: 'Diagnostic Criteria',
        intro: 'Diagnosis requires meeting several criteria:',
        criteria: [
          'Symptoms: Episodic symptoms in 2+ organ systems from mast cell mediator release',
          'Labs: Evidence of mediator release (tryptase, histamine, prostaglandins) OR response to mast cell medications',
          'Response to treatment: Improvement with mast cell stabilizers and antihistamines',
          'Exclusion: Other causes ruled out'
        ]
      },
      labTests: {
        title: 'Lab Tests',
        intro: 'Tests that may support MCAS diagnosis (best done during flare):',
        tests: [
          'Serum tryptase: Best done within 4 hours of reaction; baseline also useful',
          '24-hour urine: N-methylhistamine, prostaglandin D2, leukotriene E4',
          'Serum chromogranin A',
          'Heparin (plasma)'
        ],
        note: 'Note: Normal labs don\'t rule out MCAS. Clinical diagnosis is valid.'
      },
      vsHistamine: {
        title: 'MCAS vs Histamine Intolerance',
        intro: 'These conditions overlap significantly and often co-exist:',
        comparison: [
          { name: 'Histamine Intolerance', description: 'Problem breaking down histamine (DAO enzyme deficiency). Symptoms from dietary histamine and bacterial histamine production.' },
          { name: 'MCAS', description: 'Problem with mast cells releasing too many mediators (including histamine). Symptoms from endogenous mediator release.' }
        ],
        note: 'Many people have both: MCAS causes more histamine release, which overwhelms already compromised breakdown capacity.'
      }
    },
    symptoms: {
      isExtendedMCAS: true,
      intro: 'MCAS can affect virtually every organ system because mast cells are found throughout the body. Symptoms are often episodic and can vary from mild to life-threatening.',
      title: 'MCAS Symptoms: The "Great Masquerader"',
      categories: [
        {
          name: 'Skin/Dermatologic',
          icon: '🔴',
          items: [
            'Hives (urticaria) - appearing and disappearing',
            'Flushing (red face, chest, neck)',
            'Itching (can be intense, without visible rash)',
            'Rashes (various types)',
            'Angioedema (swelling, especially face, lips, tongue)',
            'Easy bruising',
            'Dermatographism (skin writing - raised red lines from light scratching)',
            'Eczema or atopic dermatitis flares',
            'Sensitive skin - reactions to products'
          ]
        },
        {
          name: 'Gastrointestinal',
          icon: '🤢',
          items: [
            'Abdominal pain and cramping',
            'Nausea and vomiting',
            'Diarrhea (sometimes explosive)',
            'Bloating and distention',
            'Reflux and heartburn',
            'Difficulty swallowing',
            'IBS-like symptoms',
            'Food sensitivities/reactions',
            'Malabsorption'
          ]
        },
        {
          name: 'Respiratory',
          icon: '🫁',
          items: [
            'Wheezing or asthma symptoms',
            'Shortness of breath',
            'Throat tightness or closing sensation',
            'Chronic cough',
            'Nasal congestion and runny nose',
            'Post-nasal drip',
            'Frequent throat clearing'
          ]
        },
        {
          name: 'Cardiovascular',
          icon: '❤️',
          items: [
            'Heart palpitations or racing heart',
            'Chest pain or tightness',
            'Low blood pressure (hypotension) - can cause fainting',
            'High blood pressure (less common)',
            'POTS-like symptoms (postural orthostatic tachycardia)',
            'Lightheadedness or dizziness',
            'Near-syncope or fainting'
          ]
        },
        {
          name: 'Neurological/Cognitive',
          icon: '🧠',
          items: [
            'Brain fog and cognitive dysfunction',
            'Headaches or migraines',
            'Difficulty concentrating and memory problems',
            'Anxiety and panic attacks',
            'Depression or mood changes',
            'Irritability and emotional lability',
            'Tremors',
            'Numbness or tingling',
            'Sleep disturbances',
            'Feeling \'wired but tired\''
          ]
        },
        {
          name: 'Musculoskeletal',
          icon: '💪',
          items: [
            'Joint pain and inflammation',
            'Muscle pain and aches',
            'Bone pain',
            'Weakness',
            'Hypermobility (joint laxity)'
          ]
        },
        {
          name: 'Eyes, Ears, Nose, Throat',
          icon: '👁️',
          items: [
            'Eye redness, itching, watering',
            'Blurred vision',
            'Ear ringing (tinnitus)',
            'Ear pain or pressure',
            'Sinus pressure and pain',
            'Nosebleeds',
            'Throat swelling sensation'
          ]
        },
        {
          name: 'Genitourinary',
          icon: '💧',
          items: [
            'Urinary frequency and urgency',
            'Painful urination (without infection)',
            'Bladder pain',
            'Interstitial cystitis symptoms',
            'Menstrual irregularities',
            'Painful periods'
          ]
        },
        {
          name: 'Systemic/Constitutional',
          icon: '⚡',
          items: [
            'Extreme fatigue',
            'Malaise (feeling unwell)',
            'Fever or feeling feverish',
            'Temperature dysregulation (too hot/cold)',
            'Night sweats',
            'Weight changes',
            'Poor exercise tolerance',
            'Feeling of impending doom'
          ]
        },
        {
          name: 'Anaphylaxis-Related',
          icon: '🚨',
          items: [
            'Severe reactions to triggers',
            'Throat swelling',
            'Difficulty breathing',
            'Severe drop in blood pressure',
            'Loss of consciousness',
            'Note: Some MCAS patients have anaphylaxis or near-anaphylaxis episodes'
          ]
        }
      ],
      emergency: {
        title: 'Emergency Symptoms - Seek Immediate Help',
        items: [
          'Difficulty breathing or throat closing',
          'Severe drop in blood pressure (feeling faint, losing consciousness)',
          'Severe swelling of face, lips, tongue',
          'Severe abdominal pain with vomiting',
          'Chest pain or severe palpitations',
          'These can indicate anaphylaxis - call 911'
        ]
      }
    },
    triggers: {
      isExtendedMCAS: true,
      title: 'MCAS Triggers',
      intro: 'Mast cells can be triggered by an enormous variety of stimuli. Triggers vary by individual.',
      categories: [
        {
          name: 'Foods',
          items: [
            'High-histamine foods (see diet tab)',
            'Histamine liberators',
            'Alcohol (especially wine)',
            'Food additives and preservatives',
            'Artificial colors and flavors',
            'MSG',
            'Spicy foods',
            'Individual food sensitivities'
          ]
        },
        {
          name: 'Environmental',
          items: [
            'Mold exposure (major trigger)',
            'Pollen and seasonal allergies',
            'Dust and dust mites',
            'Pet dander',
            'Strong smells (perfumes, chemicals, smoke)',
            'Air pollution',
            'Pesticides and herbicides'
          ]
        },
        {
          name: 'Temperature/Physical',
          items: [
            'Extremes of temperature (hot or cold)',
            'Sudden temperature changes',
            'Pressure on skin',
            'Vibration',
            'Intense exercise',
            'Friction or scratching',
            'Hot showers or baths',
            'Sun exposure'
          ]
        },
        {
          name: 'Infections & Illness',
          items: [
            'Bacterial infections',
            'Viral infections',
            'Parasitic infections',
            'Tick-borne diseases (Lyme, Bartonella)',
            'Mold illness (CIRS)',
            'Dental infections'
          ]
        },
        {
          name: 'Stress & Emotions',
          items: [
            'Emotional stress',
            'Physical stress',
            'Lack of sleep',
            'Anxiety and panic',
            'Excitement (even positive)',
            'Overexertion'
          ]
        },
        {
          name: 'Hormones',
          items: [
            'Menstrual cycle',
            'Ovulation',
            'Pregnancy',
            'Menopause',
            'Hormone fluctuations'
          ]
        },
        {
          name: 'Medications & Medical',
          items: [
            'NSAIDs (ibuprofen, aspirin)',
            'Opioids',
            'Some antibiotics',
            'Contrast dyes',
            'General anesthesia',
            'Vaccines (individual variation)',
            'Surgeries or procedures'
          ]
        },
        {
          name: 'Other',
          items: [
            'Insect stings/bites',
            'Tight clothing or waistbands',
            'Prolonged standing',
            'Dehydration',
            'Low blood sugar',
            'Vitamin D deficiency',
            'Individual idiosyncratic triggers'
          ]
        }
      ]
    },
    supplements: {
      isExtendedMCAS: true,
      intro: 'Focus is on stabilizing mast cells (preventing degranulation) and reducing inflammation. Many MCAS patients also benefit from histamine intolerance supplements.',
      items: [
        {
          name: 'Quercetin',
          dosage: '500-1000 mg twice daily',
          evidence: 'Strong evidence',
          highlight: 'The gold standard natural mast cell stabilizer',
          benefits: [
            'Stabilizes mast cell membranes',
            'Prevents histamine release',
            'Natural antihistamine',
            'Anti-inflammatory',
            'Antioxidant'
          ],
          note: 'Take with vitamin C for synergistic effect. May take several weeks for full benefit. Often first-line natural treatment.'
        },
        {
          name: 'Vitamin C (high dose)',
          dosage: '500-1000 mg three times daily (or more)',
          evidence: 'Strong evidence',
          highlight: 'Essential mast cell stabilizer and antihistamine',
          benefits: [
            'Stabilizes mast cells',
            'Breaks down histamine',
            'Powerful antioxidant',
            'Supports immune function',
            'Enhances quercetin effectiveness'
          ],
          note: 'Buffered form best. Can dose to bowel tolerance (loose stools = too much). Some MCAS patients take very high doses (multiple grams). Start low.'
        },
        {
          name: 'Vitamin D3',
          dosage: '4000-5000 IU daily (test levels, aim for 60-80 ng/mL)',
          evidence: 'Strong evidence',
          highlight: 'Powerful mast cell stabilizer, often severely deficient in MCAS',
          benefits: [
            'Stabilizes mast cells',
            'Reduces mast cell mediator release',
            'Anti-inflammatory',
            'Immune regulation',
            'Deficiency common in MCAS'
          ],
          note: 'CRITICAL supplement. Test levels regularly. Take with K2 and fat. May need higher doses than typical to reach therapeutic levels.'
        },
        {
          name: 'DAO Enzyme',
          dosage: '1-2 capsules before meals',
          evidence: 'Strong clinical evidence',
          highlight: 'Breaks down dietary histamine',
          benefits: [
            'Helps digest high-histamine foods',
            'Reduces food reaction severity',
            'Allows some dietary flexibility'
          ],
          note: 'Expensive but effective for food-triggered symptoms. Not a cure but symptom management tool.'
        },
        {
          name: 'Omega-3 Fatty Acids',
          dosage: '2000-3000 mg EPA+DHA daily',
          evidence: 'Strong evidence',
          highlight: 'Powerful anti-inflammatory and mast cell stabilizer',
          benefits: [
            'Stabilizes mast cell membranes',
            'Reduces inflammation',
            'Competes with arachidonic acid (pro-inflammatory)',
            'Supports brain health'
          ],
          note: 'High-quality fish oil tested for purity. Higher doses often needed in MCAS than typical recommendations.'
        },
        {
          name: 'Curcumin',
          dosage: '500-1500 mg daily',
          evidence: 'Strong evidence',
          highlight: 'Potent anti-inflammatory and mast cell stabilizer',
          benefits: [
            'Stabilizes mast cells',
            'Reduces mediator release',
            'Powerful anti-inflammatory',
            'Antioxidant'
          ],
          note: 'Must include piperine or use liposomal form. Take with fat. Very effective for many MCAS patients.'
        },
        {
          name: 'Luteolin',
          dosage: '100-200 mg daily',
          evidence: 'Good evidence',
          highlight: 'Powerful mast cell stabilizer, especially for brain mast cells',
          benefits: [
            'Stabilizes mast cells',
            'Crosses blood-brain barrier',
            'Reduces brain inflammation',
            'May help neuropsychiatric symptoms',
            'Neuroprotective'
          ],
          note: 'Less well-known but very effective. Good for PANS/MCAS overlap. Often found in combination with quercetin.'
        },
        {
          name: 'N-Acetylcysteine (NAC)',
          dosage: '600-1200 mg daily',
          evidence: 'Good evidence',
          highlight: 'Antioxidant and glutathione precursor',
          benefits: [
            'Reduces oxidative stress',
            'Supports detoxification',
            'Mast cell stabilizing effects',
            'May help OCD symptoms',
            'Thins mucus'
          ],
          note: 'Take on empty stomach. Start low. Important for PANS/MCAS.'
        },
        {
          name: 'Magnesium',
          dosage: '300-500 mg daily',
          evidence: 'Moderate evidence',
          highlight: 'Mast cell stabilizer and calming',
          benefits: [
            'Stabilizes mast cells',
            'Calms nervous system',
            'Improves sleep',
            'Reduces anxiety',
            'Often deficient'
          ],
          note: 'Magnesium glycinate best for MCAS (calming, well-absorbed). Before bed ideal.'
        },
        {
          name: 'Probiotics (specific strains)',
          dosage: 'Multi-strain, 25-50 billion CFU',
          evidence: 'Moderate evidence',
          highlight: 'Support gut health and reduce inflammation',
          benefits: [
            'Reduce gut inflammation',
            'Support gut barrier',
            'Some strains stabilize mast cells',
            'Immune modulation'
          ],
          note: 'AVOID histamine-producing strains. Use: Bifidobacterium species, L. rhamnosus, L. plantarum. Seeking Health ProBiota HistaminX or similar.'
        },
        {
          name: 'B-Complex (methylated)',
          dosage: 'B-complex with B6, B12, folate',
          evidence: 'Moderate evidence',
          highlight: 'Support DAO function and methylation',
          benefits: [
            'B6 needed for DAO enzyme',
            'Methylation support',
            'Energy production',
            'Nervous system support'
          ],
          note: 'Use methylated forms (methylfolate, methylcobalamin). Important for detoxification pathways.'
        },
        {
          name: 'Zinc',
          dosage: '15-30 mg daily',
          evidence: 'Good evidence',
          highlight: 'Mast cell stabilizer and immune support',
          benefits: [
            'Stabilizes mast cells',
            'Immune function',
            'Often deficient',
            'Supports DAO'
          ],
          note: 'Take with food. Balance with copper. Zinc picolinate or glycinate.'
        }
      ],
      prescriptionMeds: {
        title: 'Prescription Medications for MCAS',
        intro: 'Many MCAS patients require prescription medications in addition to supplements. Discuss with your doctor:',
        items: [
          'H1 antihistamines: Cetirizine, loratadine, fexofenadine (often high doses needed)',
          'H2 antihistamines: Famotidine, ranitidine (blocks different histamine receptors)',
          'Mast cell stabilizers: Cromolyn sodium (best mast cell stabilizer), ketotifen',
          'Leukotriene inhibitors: Montelukast',
          'Low-dose naltrexone (LDN): Immune modulation',
          'Many MCAS patients take multiple medications targeting different mediators'
        ]
      }
    },
    lifestyle: {
      isExtendedMCAS: true,
      title: 'MCAS Lifestyle Management',
      intro: 'Living with MCAS requires careful trigger avoidance, stress management, and lifestyle modifications. The goal is to keep mast cells stable and prevent degranulation episodes. This is more restrictive than typical chronic illness management.',
      categories: [
        {
          icon: '🔍',
          name: 'Trigger Identification & Avoidance',
          importance: 'CRITICAL',
          importanceNote: '',
          items: [
            'Keep detailed symptom diary: Track everything - foods, activities, environment, stress, symptoms. Use the tracker in this app daily.',
            'Rate symptoms 1-10: For each body system. Helps identify severity and patterns.',
            'Note timing: Symptoms can occur minutes to hours after trigger. Track when relative to exposures.',
            'Look for patterns: Review diary weekly. What triggers appear repeatedly before flares?',
            'Once identified, avoid completely: No \'just a little bit\' with true triggers. Complete avoidance necessary.',
            'Test suspected triggers carefully: In controlled way when stable. Small exposure, monitor 24-48 hours.',
            'Triggers can change: What you tolerate may change over time. Keep tracking even when stable.',
            'Multiple trigger phenomenon: May tolerate triggers individually but not combined. Bucket theory applies.',
            'Threshold varies by day: Based on stress, sleep, hormones, inflammation level. More reactive on bad days.',
            'Share trigger list: With family, school, caregivers. Everyone needs to know.'
          ]
        },
        {
          icon: '🍄',
          name: 'Mold Exposure Prevention',
          importance: 'CRITICAL',
          importanceNote: '',
          items: [
            'Mold is major MCAS trigger: Can prevent improvement even with perfect diet and medications.',
            'Test your home: ERMI test or visual inspection by professional if you suspect mold.',
            'Common mold locations: Bathrooms, basements, under sinks, around windows, behind walls with water damage.',
            'Fix water leaks immediately: Any water intrusion can cause mold within 24-48 hours.',
            'Dehumidifier essential: Keep humidity 30-50%. Mold grows above 60% humidity.',
            'Bathroom ventilation: Run exhaust fan during and 30 min after showers. Keep door open.',
            'Clean visible mold immediately: Small amounts can clean with hydrogen peroxide or vinegar. Large amounts need professional.',
            'HEPA air filters: Help remove mold spores from air. Change filters regularly.',
            'Consider moving if severe mold: Remediation not always effective. Health more important than house.',
            'Avoid musty places: Basements, old buildings, libraries. If you smell must, there\'s mold.',
            'After rain caution: Mold spore counts highest after rain. Indoor activities those days.'
          ]
        },
        {
          icon: '🌙',
          name: 'Sleep Optimization',
          importance: 'CRITICAL',
          importanceNote: '',
          items: [
            '9-11 hours non-negotiable for children: Sleep deprivation triggers mast cell activation. This isn\'t optional.',
            'Consistent schedule: Same time every night, even weekends. Mast cells respond to circadian rhythm.',
            'Cool bedroom (65-68°F): Heat triggers mast cells. Keep room cool with good air flow.',
            'Dark room: Blackout curtains essential. Any light disrupts melatonin and can trigger mast cells.',
            'No screens 2+ hours before bed: Blue light triggers mast cell activation and disrupts sleep hormones.',
            'HEPA filter in bedroom: Removes allergens that can trigger nighttime reactions.',
            'Hypoallergenic bedding: Allergen-proof mattress/pillow covers. Wash weekly in hot water.',
            'Avoid synthetic fabrics: Cotton or bamboo sheets. Natural fabrics less irritating.',
            'Magnesium glycinate before bed: 300-500mg. Stabilizes mast cells, promotes sleep.',
            'Calming routine: Warm (not hot) bath, reading, gentle stretching. Predictable and soothing.',
            'Address sleep disorders: Sleep apnea, restless leg syndrome can trigger mast cells. Get evaluated if sleep quality poor despite adequate hours.'
          ]
        },
        {
          icon: '🧘',
          name: 'Stress Management',
          importance: 'CRITICAL',
          importanceNote: '',
          items: [
            'Stress = mast cell activation: Even good stress (excitement) can trigger. Chronic stress keeps mast cells hyperreactive.',
            'Daily stress reduction practice: Non-negotiable. Meditation, breathwork, yoga - pick something and do it daily.',
            'Deep breathing exercises: 4-7-8 breathing, box breathing. Activates parasympathetic (calming) nervous system.',
            'Vagus nerve stimulation: Gargling, humming, singing, cold water on face. Increases vagal tone (reduces mast cell reactivity).',
            'Meditation 10-20 min daily: Reduces stress hormones, calms immune system. Apps: Headspace, Calm, Insight Timer.',
            'Gentle yoga or tai chi: Combines movement, breathwork, mindfulness. Avoid hot yoga.',
            'Time in nature essential: 20+ minutes outdoors daily. Reduces cortisol, improves mood, supports healing.',
            'Therapy/counseling: Living with MCAS is traumatic. Professional support critical.',
            'Set boundaries: Say no to non-essential obligations. Protect your energy and nervous system.',
            'Avoid stressful people/situations: When possible. Your health is priority.',
            'Process trauma: MCAS reactions themselves are traumatic. EMDR or somatic therapy can help.',
            'Creative expression: Art, music, journaling. Non-verbal stress release.'
          ]
        },
        {
          icon: '🌡️',
          name: 'Temperature & Physical Triggers',
          importance: 'HIGH',
          importanceNote: '',
          items: [
            'Avoid temperature extremes: Both heat and cold trigger mast cells. Stay in comfortable range.',
            'Keep cool: Air conditioning helpful, especially summer. Mast cells very temperature-sensitive.',
            'Lukewarm showers only: Hot showers major trigger. Keep water warm, not hot.',
            'Dress in layers: Easy to adjust. Natural breathable fabrics (cotton, bamboo).',
            'Avoid hot tubs, saunas: Too much heat stress. Triggers reactions in most MCAS patients.',
            'Gradual temperature changes: Don\'t go from hot to cold quickly (like hot car to air conditioned building).',
            'Exercise in cool environment: Air conditioned gym or cool weather outdoors. Overheating triggers mast cells.',
            'Avoid tight clothing: Pressure on skin can trigger. Loose, comfortable clothing.',
            'Sun exposure cautious: Some tolerate, some don\'t. Start with short exposures. Use mineral sunscreen (chemical ones trigger some).',
            'Cool packs during reactions: Cool compress on face, neck, wrists during flare. Don\'t use ice directly.',
            'Watch weather changes: Barometric pressure changes trigger some MCAS patients. Be extra cautious during weather shifts.'
          ]
        },
        {
          icon: '🏃',
          name: 'Exercise Modifications',
          importance: 'MODERATE',
          importanceNote: '',
          items: [
            'Intense exercise triggers mast cells: High-intensity causes histamine release. Most MCAS patients can\'t tolerate.',
            'Gentle movement beneficial: Walking, gentle yoga, stretching. Reduces inflammation without triggering.',
            'Walking ideal: 20-30 minutes daily. Pace that allows conversation. Outdoor preferred.',
            'Swimming if tolerated: Low-impact, full-body. Chlorine bothers some - rinse immediately.',
            'Avoid HIIT, CrossFit, intense cardio: Too much stress on system. Save for when mast cells stable.',
            'Listen to body always: If exercise causes flushing, palpitations, fatigue - it\'s too much. Back off.',
            'Don\'t push through: \'No pain, no gain\' doesn\'t apply to MCAS. Pain = mast cell activation.',
            'Exercise in cool environment: Overheating triggers mast cells. Air conditioning or cool weather.',
            'Stay well hydrated: Before, during, after. Dehydration worsens symptoms.',
            'Post-exercise snack: Protein and healthy carb stabilizes blood sugar (low blood sugar triggers mast cells).',
            'Gradually build tolerance: As mast cells stabilize, may be able to increase intensity slowly. But always listen to body.'
          ]
        },
        {
          icon: '🧴',
          name: 'Chemical & Fragrance Avoidance',
          importance: 'HIGH',
          importanceNote: '',
          items: [
            'Fragrance-free everything: Laundry detergent, dish soap, shampoo, lotion, deodorant. Fragrances major trigger.',
            'Read labels: \'Unscented\' not same as \'fragrance-free.\' Unscented often has fragrance to mask smell.',
            'Natural cleaning products: Vinegar (if tolerated), baking soda, hydrogen peroxide. Avoid commercial cleaners.',
            'No air fresheners, plugins, candles: Synthetic fragrances trigger mast cells. Natural essential oils okay for some (test carefully).',
            'Avoid perfume counters: Department stores, malls. Overwhelming chemical exposure.',
            'Ask others not to wear perfume: Around you. Your health matters more than their preference.',
            'Fragrance-free zones: Car, bedroom, workspace. Protect your safe spaces.',
            'Natural personal care: Brands: Beautycounter, Branch Basics, Dr. Bronner\'s. Minimal ingredients.',
            'Avoid nail salons, hair salons: Chemical overload. If you must go, highly ventilated or outdoor area.',
            'New items off-gas: New furniture, carpets, paint release chemicals. Air out before using or avoid.',
            'Dry cleaning chemicals: Perchloroethylene triggers reactions. Air out dry-cleaned clothes before wearing or avoid.',
            'Notify workplaces/schools: About fragrance sensitivity. Many now have fragrance-free policies.'
          ]
        },
        {
          icon: '🚨',
          name: 'Emergency Preparedness',
          importance: 'CRITICAL',
          importanceNote: '',
          items: [
            'EpiPen (epinephrine auto-injector): Many MCAS patients need prescription. Can be life-saving during anaphylaxis.',
            'Carry EpiPens everywhere: Two pens minimum. One may not be enough. Keep unexpired.',
            'Know when to use: Throat swelling, difficulty breathing, severe BP drop, losing consciousness. Don\'t wait.',
            'After EpiPen, call 911: Always go to ER even if feeling better. Biphasic reactions can occur.',
            'Medical alert bracelet: States MCAS, anaphylaxis risk, emergency contacts. Wear always.',
            'Emergency med kit: Benadryl, H2 blocker, EpiPen, rescue inhaler if needed. Keep in purse/car.',
            'Emergency plan written out: What to do, who to call, what meds to take. Share with family.',
            'Educate family/friends: How to recognize severe reaction, how to use EpiPen, when to call 911.',
            'Inform all doctors: Every provider needs to know about MCAS. Affects treatment decisions.',
            'Hospital letter: From MCAS specialist explaining condition, safe medications, what to avoid. Bring to ER.',
            'Safe medication list: What meds you tolerate, what you react to. Keep updated list.',
            'Avoid ER triggers: Some hospitals use fragrances, latex. Inform staff immediately of sensitivities.'
          ]
        },
        {
          icon: '🏥',
          name: 'Medical Procedures & Dental Work',
          importance: 'HIGH',
          importanceNote: '',
          items: [
            'Pre-medication protocol: Before any procedure: H1 antihistamine + H2 blocker 12 hours before, 1 hour before, and after.',
            'Inform all providers: Dentist, surgeon, anesthesiologist about MCAS before any procedure.',
            'Anesthesia considerations: Some anesthetics trigger mast cells. Need experienced anesthesiologist.',
            'Avoid unnecessary procedures: Every procedure is risk. Only do what\'s truly necessary.',
            'Request preservative-free medications: When possible. Preservatives can trigger.',
            'Latex-free environment: Many MCAS patients react to latex. Ensure all gloves, equipment latex-free.',
            'Fragrance-free facility if possible: Ask staff not to wear perfume/cologne day of procedure.',
            'Contrast dyes dangerous: CT/MRI contrast can cause severe reactions. Pre-medicate heavily or avoid if possible.',
            'Dental work triggers: Local anesthetics, epinephrine in novocaine, stress. Use epinephrine-free anesthetic.',
            'Have someone with you: For all procedures. They can advocate if you have reaction.',
            'Stay for observation: Don\'t leave medical facility immediately after procedure. Watch for delayed reactions.',
            'Recovery time needed: Procedures are huge stress on system. Plan for extra rest after.'
          ]
        },
        {
          icon: '🏠',
          name: 'Environmental Controls',
          importance: 'HIGH',
          importanceNote: '',
          items: [
            'HEPA air filters everywhere: Bedroom, main living areas. Run 24/7. Remove airborne triggers.',
            'Hard floors, no carpets: Carpets impossible to fully clean. Harbor dust, mold, chemicals.',
            'Humidity control: 30-50% ideal. Dehumidifier if too humid, humidifier if too dry.',
            'Non-toxic everything: Furniture, mattresses, paint. Off-gassing triggers reactions.',
            'Frequent vacuuming: HEPA filter vacuum only. Regular vacuums blow allergens into air.',
            'Dust frequently: Damp cloth to trap dust. Dust mites major trigger.',
            'Keep windows closed during high pollen: Check pollen counts. Indoor air filtered better than outdoor.',
            'Remove shoes at door: Don\'t track outdoor allergens through house.',
            'Shower before bed: Removes pollen, chemicals, allergens from hair and skin.',
            'Pet considerations: Pet dander triggers many. If you have pets, keep out of bedroom, bathe weekly, HEPA filter essential.',
            'Indoor plants careful: Mold can grow in soil. If you have plants, watch for mold, use pebbles on top of soil.',
            'Safe haven room: Make bedroom ultra-clean, allergen-free zone. Sanctuary from triggers.'
          ]
        },
        {
          icon: '👥',
          name: 'Social & Practical Life',
          importance: 'MODERATE',
          importanceNote: '',
          items: [
            'Eating out nearly impossible: Can\'t control freshness, ingredients, cross-contamination. Cook at home.',
            'When you must eat out: Simple grilled protein and steamed vegetables. Ask questions. Bring safe foods as backup.',
            'Bring your own food everywhere: Parties, travel, events. Cooler with ice packs keeps things fresh.',
            'Educate close circle: Family and close friends need to understand. You\'re not difficult, you\'re managing medical condition.',
            'Don\'t apologize for needs: Medical necessities aren\'t inconveniences. Advocate for yourself.',
            'Travel challenging: Book accommodations with kitchen. Bring supplements, emergency meds, safe foods.',
            'Airplane travel: Bring all food. Ask flight attendant about fragrance-free seating. Pre-medicate before flight.',
            'School/work accommodations: May need to bring all food, avoid certain areas (cafeteria smells), work from home.',
            'Medical alert jewelry: Bracelet or necklace stating MCAS, anaphylaxis risk, emergency contacts.',
            'Connect with MCAS community: Online groups (Facebook, Reddit). Recipe ideas, support, validation.',
            'Prepare others: Friends, family for your needs. Send them info on MCAS so they understand.'
          ]
        },
        {
          icon: '💊',
          name: 'Medication Management',
          importance: 'CRITICAL',
          importanceNote: '',
          items: [
            'Take mast cell stabilizers consistently: Quercetin, vitamin C, other stabilizers work best when taken regularly, not as needed.',
            'H1 + H2 antihistamine combo: Blocking both receptor types more effective than either alone. Take daily if prescribed.',
            'Cromolyn sodium if prescribed: Best pharmaceutical mast cell stabilizer. Take 15-30 min before meals. Must be consistent.',
            'Avoid DAO-blocking medications: NSAIDs (ibuprofen, aspirin) reduce DAO. Use acetaminophen instead for pain.',
            'Pre-medicate before known triggers: Dental work, procedures, potential exposures. H1+H2 antihistamines 12 hrs before and after.',
            'Carry emergency meds always: Benadryl, EpiPen, rescue inhaler. In purse, car, at work/school.',
            'Medication diary: Track what you take, when, effectiveness, side effects.',
            'Work with MCAS-knowledgeable doctor: Regular doctors often don\'t understand need for high-dose antihistamines, multiple medications.',
            'Compounded medications if reactions: Some react to fillers/dyes in commercial meds. Compounding pharmacy can make pure versions.',
            'Low-dose naltrexone (LDN): If prescribed, take at bedtime. Immune modulator, can be very helpful.',
            'Don\'t stop meds without doctor: Even if feeling better. Mast cells need ongoing stabilization.'
          ]
        },
        {
          icon: '⚡',
          name: 'Pacing & Energy Management',
          importance: 'MODERATE-HIGH',
          importanceNote: '',
          items: [
            'Spoon theory applies: Limited energy each day. Choose wisely how to spend.',
            'Rest before you\'re exhausted: Pushing through triggers mast cells. Rest proactively.',
            'Break activities into smaller chunks: Don\'t try to do everything at once.',
            'Plan for recovery time: After any exertion or trigger exposure, need extra rest.',
            'Good days don\'t mean cured: Don\'t overdo it on good days. Leads to crash next day.',
            'Listen to body signals: Fatigue, brain fog, increased symptoms = stop and rest.',
            'Schedule rest periods: Build in downtime between activities. Not optional.',
            'Reduce commitments: During healing phase, do less. Quality of life improves when you\'re not constantly pushing.',
            'Ask for help: Delegate tasks, accept assistance, use delivery services.',
            'Celebrate small accomplishments: Getting through day is success with MCAS.'
          ]
        }
      ],
      keyPrinciples: [
        'MCAS requires significant lifestyle changes - this is real, medical, and necessary',
        'Trigger avoidance is treatment, not overreacting',
        'Stress, sleep, and mold are the big three - prioritize managing these',
        'Emergency preparedness essential - carry EpiPen, wear medical alert',
        'Pacing and energy management prevent crashes',
        'Build support system - you can\'t do this alone',
        'With proper management, many MCAS patients stabilize and regain quality of life',
        'Be patient - stabilizing mast cells takes time, typically 6-12+ months'
      ]
    },
    diet: {
      isExtendedMCAS: true,
      title: 'MCAS Diet: Low-Histamine + Low-Inflammatory',
      intro: 'Diet for MCAS is similar to histamine intolerance but even stricter. Focus on fresh, simple, low-histamine, anti-inflammatory foods. See Histamine Intolerance page for detailed food lists.',
      corePrinciples: {
        title: 'Core Principles',
        items: [
          'FRESH IS EVERYTHING: Histamine increases in food over time',
          'No leftovers: Eat immediately after cooking',
          'Simple ingredients: Avoid packaged/processed foods',
          'Anti-inflammatory focus: Lots of vegetables, healthy fats',
          'Organic when possible: Reduce pesticide exposure',
          'Read ALL labels: Many hidden triggers'
        ]
      },
      strictlyAvoid: {
        title: 'Foods to Strictly Avoid in MCAS',
        intro: 'All high-histamine foods PLUS mast cell triggers:',
        items: [
          'All fermented foods',
          'All aged/cured/smoked meats and fish',
          'Alcohol (major trigger)',
          'Vinegar (except some tolerate apple cider vinegar)',
          'Tomatoes and tomato products',
          'Citrus fruits',
          'Strawberries',
          'Chocolate (mast cell liberator)',
          'Nuts (especially peanuts, cashews, walnuts)',
          'Shellfish',
          'Artificial additives, colors, preservatives',
          'MSG',
          'Spicy foods'
        ]
      },
      sampleMeal: {
        title: 'Sample MCAS-Safe Meal',
        meals: [
          { name: 'Breakfast', description: 'Scrambled eggs with fresh zucchini and basil, side of blueberries' },
          { name: 'Lunch', description: 'Grilled chicken breast (cooked fresh) with steamed broccoli and cauliflower, drizzled with olive oil' },
          { name: 'Dinner', description: 'Baked white fish (cod) with roasted sweet potato and green beans' },
          { name: 'Snacks', description: 'Apple slices, carrot sticks, rice cakes with coconut oil' }
        ]
      },
      reminders: {
        title: 'Important Reminders',
        items: [
          'MCAS diet is one of the most restricted - work with nutritionist to ensure adequate nutrition',
          'Children may resist - work with feeding therapist if needed',
          'Some MCAS patients react to "safe" foods - bio-individual tolerance',
          'Goal is to stabilize mast cells, then may be able to slowly expand diet',
          'Keep detailed food/symptom diary to identify YOUR triggers',
          'This is temporary - with treatment, diet can usually be liberalized over time'
        ]
      }
    }
  },
  'mthfr-gene': {
    name: 'MTHFR Gene Mutation',
    color: 'bg-blue-500',
    lightColor: 'bg-blue-100',
    textColor: 'text-blue-700',
    description: 'A genetic variation affecting folate metabolism, detoxification, and neurotransmitter production',
    about: {
      overview: `Methylation is a critical biochemical process that happens billions of times per second in every cell of your body. It's essential for detoxification, neurotransmitter production, immune function, DNA repair, and countless other vital processes. When methylation is impaired—often due to genetic variations—toxins accumulate, neurotransmitters become imbalanced, and inflammation increases, significantly worsening PANDAS/PANS symptoms.`,
      whatIsMethylation: {
        title: 'What is Methylation?',
        description: 'Methylation is the process of adding a methyl group (one carbon atom and three hydrogen atoms: CH₃) to another molecule. This simple chemical addition has profound effects on:',
        effects: [
          { name: 'Detoxification', description: 'Breaking down and eliminating toxins, heavy metals, chemicals, excess hormones' },
          { name: 'Neurotransmitter production', description: 'Making and breaking down dopamine, serotonin, norepinephrine, epinephrine' },
          { name: 'DNA repair', description: 'Fixing damaged DNA, turning genes on and off (epigenetics)' },
          { name: 'Immune function', description: 'Regulating inflammation, producing immune cells' },
          { name: 'Histamine breakdown', description: 'Eliminating excess histamine' },
          { name: 'Cell membrane integrity', description: 'Building phospholipids for healthy cell membranes' },
          { name: 'Glutathione production', description: "Creating body's master antioxidant" },
          { name: 'Mood and mental health', description: "Producing SAMe (S-adenosylmethionine), nature's antidepressant" }
        ]
      },
      mthfrGene: {
        title: 'The MTHFR Gene: Master Regulator',
        whatIs: 'MTHFR stands for Methylenetetrahydrofolate Reductase—an enzyme that converts folate (vitamin B9) from food into 5-MTHF (5-methyltetrahydrofolate), the active form your body can use. This active folate is essential for the methylation cycle.',
        mutations: {
          intro: 'You inherit one copy of the MTHFR gene from each parent. Common mutations (polymorphisms or SNPs) can reduce enzyme function:',
          c677t: {
            name: 'C677T Mutation (Most Common)',
            heterozygous: '~30-40% enzyme activity reduction. Very common (40-50% of population).',
            homozygous: '~70-80% enzyme activity reduction. About 10-15% of population.'
          },
          a1298c: {
            name: 'A1298C Mutation',
            heterozygous: 'Mild reduction in activity',
            homozygous: 'More significant reduction, affects BH4 (tetrahydrobiopterin) production',
            compound: 'Compound heterozygous: One C677T and one A1298C = moderate impairment'
          }
        },
        impairmentEffects: [
          "Can't convert folic acid: Synthetic folic acid from fortified foods/supplements builds up, actually blocking folate receptors",
          'Methylation slows down: All methylation-dependent processes impaired',
          'Homocysteine rises: Toxic amino acid that damages blood vessels, increases inflammation',
          'Detoxification impaired: Toxins, heavy metals, chemicals accumulate',
          'Glutathione production drops: Less antioxidant protection, worse detox',
          'Neurotransmitter imbalances: Depression, anxiety, OCD symptoms worsen',
          "Increased disease risk: Cardiovascular disease, blood clots, miscarriage, neural tube defects, cancer, Alzheimer's"
        ]
      },
      comtGene: {
        title: 'The COMT Gene: Breaking Down Neurotransmitters',
        whatIs: 'COMT (Catechol-O-Methyltransferase) is an enzyme that breaks down catecholamines—neurotransmitters including dopamine, norepinephrine, and epinephrine. It also breaks down estrogen and certain toxins.',
        variations: {
          intro: 'The most significant COMT mutation is V158M (also called Val158Met or rs4680):',
          valval: {
            name: 'Val/Val (Fast COMT) - "Warrior"',
            activity: '3-4x faster at breaking down neurotransmitters',
            result: 'Lower dopamine and norepinephrine levels in brain',
            personality: 'Handles stress well, thrives under pressure, better pain tolerance',
            downsides: 'More prone to impulsivity, ADHD, need more stimulation, may self-medicate with caffeine/stimulants'
          },
          metmet: {
            name: 'Met/Met (Slow COMT) - "Worrier"',
            activity: '3-4x slower at breaking down neurotransmitters',
            result: 'Higher dopamine and norepinephrine levels in brain (stay elevated longer)',
            personality: 'Better memory and attention to detail, creative, reflective',
            downsides: "Highly sensitive to stress, anxiety-prone, \"worrier\" personality, can't handle as much pressure, more sensitive to pain, estrogen dominance (women), very sensitive to stimulants (coffee, medications)"
          },
          valmet: {
            name: 'Val/Met (Intermediate COMT)',
            description: 'Balanced between fast and slow. Most flexible, can adapt to different situations.'
          }
        },
        pandasRelevance: [
          'Slow COMT + stress = disaster: Already high catecholamines get even higher with PANS stress. Severe anxiety, panic, OCD.',
          "Can't tolerate stimulants: Methylphenidate, amphetamines may cause severe anxiety in slow COMT",
          'Estrogen buildup (women): COMT breaks down estrogen. Slow COMT = estrogen dominance = worsening symptoms',
          'Needs magnesium: COMT enzyme requires magnesium to function. Deficiency worsens slow COMT problems.',
          'Pain sensitivity: Slow COMT associated with lower pain threshold. More pain from PANS symptoms.'
        ]
      },
      otherGenes: {
        title: 'Other Important Methylation & Detox Genes',
        genes: [
          {
            name: 'MTR & MTRR Genes',
            function: 'MTR (Methionine Synthase) and MTRR (Methionine Synthase Reductase) work together to recycle homocysteine back into methionine, which then becomes SAMe.',
            whenImpaired: 'Homocysteine rises, SAMe drops. Need more B12 (methylcobalamin) to support these enzymes.'
          },
          {
            name: 'CBS Gene',
            function: 'Cystathionine Beta-Synthase converts homocysteine to cysteine (for glutathione production) via transsulfuration pathway.',
            upregulation: "Enzyme works too fast. Drains homocysteine, can lower SAMe. Produces excess sulfite and ammonia (brain fog, irritability). Sulfur sensitivity common (can't tolerate high-sulfur foods, Epsom salt baths, cruciferous vegetables).",
            management: 'Limit sulfur foods, support ammonia clearance, may need to avoid methylfolate initially.'
          },
          {
            name: 'BHMT Gene',
            function: 'Betaine-Homocysteine Methyltransferase provides backup pathway to convert homocysteine to methionine.',
            whenImpaired: 'More reliant on MTR/MTRR pathway. Benefit from betaine (trimethylglycine/TMG) supplementation.'
          },
          {
            name: 'SUOX Gene',
            function: 'Sulfite Oxidase converts sulfite to sulfate (less toxic form).',
            whenImpaired: 'Sulfite buildup. Sulfur/sulfite sensitivity. Need molybdenum to support enzyme. Avoid sulfite preservatives.'
          },
          {
            name: 'GST Genes (GSTM1, GSTT1, GSTP1)',
            function: 'Glutathione S-Transferase enzymes use glutathione to detoxify chemicals, heavy metals, oxidative stress.',
            whenImpaired: 'Poor detoxification. Higher toxic burden. More sensitive to chemicals, mold, environmental toxins.',
            support: 'Increase glutathione (NAC, liposomal glutathione), cruciferous vegetables (support GST enzymes), reduce toxic exposure.'
          },
          {
            name: 'SOD2 Gene',
            function: 'Superoxide Dismutase 2 protects mitochondria from oxidative damage.',
            whenImpaired: 'More mitochondrial damage, increased fatigue. Need more antioxidants (especially manganese, zinc, CoQ10).'
          },
          {
            name: 'MAO-A Gene',
            function: 'Monoamine Oxidase A breaks down serotonin, dopamine, norepinephrine.',
            slowMAOA: 'Higher serotonin and other neurotransmitters. May have anxiety, mood swings. Called "warrior gene" when combined with other variants - associated with aggression under stress.',
            fastMAOA: 'Lower neurotransmitters. Depression, low motivation.'
          },
          {
            name: 'VDR Gene',
            function: 'Vitamin D Receptor - determines how well you use vitamin D.',
            whenImpaired: 'Need higher vitamin D levels to get same benefit. Affects immune function, bone health, mood. Very relevant for PANS.'
          }
        ]
      },
      detoxification: {
        title: 'Phase I & Phase II Detoxification',
        intro: 'The liver detoxifies in two main phases:',
        phaseI: {
          title: 'Phase I Detoxification (CYP450 Enzymes)',
          points: [
            'What it does: Transforms fat-soluble toxins into water-soluble intermediates using oxidation, reduction, hydrolysis',
            'Problem: Intermediates often MORE toxic than original substance. Must quickly move to Phase II.',
            'Creates free radicals: Need antioxidants to protect from damage',
            'Can be too fast or too slow: Genetic variations in CYP genes affect speed'
          ]
        },
        phaseII: {
          title: 'Phase II Detoxification (Conjugation)',
          points: [
            'What it does: Attaches molecules to toxic intermediates to neutralize and eliminate them',
            'Six pathways: Glutathione conjugation, sulfation, glucuronidation, acetylation, methylation, amino acid conjugation',
            'Most important for PANS: Glutathione conjugation (GST genes), sulfation (CBS, SUOX), methylation (MTHFR, COMT)',
            'Problem: If Phase II can\'t keep up with Phase I, toxic intermediates accumulate. Feel poisoned.'
          ]
        },
        whenWrong: [
          'Phase I too fast, Phase II too slow: Most problematic combination. Toxic buildup. Common with methylation issues.',
          "Glutathione depleted: Phase II can't work. Toxins accumulate.",
          'Nutrient deficiencies: Each pathway needs specific nutrients. Deficiency = pathway fails.',
          'Toxic overload: Too many toxins overwhelm system regardless of genetics.'
        ]
      },
      pandasConnection: {
        title: 'The PANDAS/PANS Connection',
        intro: 'Why Methylation & Detox Matter for PANS',
        points: [
          "Can't eliminate inflammatory toxins: Bacterial toxins, mold toxins, environmental chemicals accumulate. Worsen neuroinflammation.",
          "Neurotransmitter chaos: Can't properly make or break down dopamine, serotonin, norepinephrine. OCD, anxiety, mood swings worse.",
          'Histamine buildup: HNMT enzyme uses methylation to break down histamine. Poor methylation = histamine intolerance.',
          "Low glutathione: Can't protect brain from oxidative stress and inflammation.",
          'Immune dysregulation: Methylation controls immune response. Impaired methylation = worse autoimmunity.',
          "Can't tolerate treatments: Side effects from antibiotics, antivirals, supplements worse when can't detoxify.",
          "Slow or incomplete recovery: Can't heal while toxins accumulating. Supporting methylation often unlocks recovery."
        ],
        commonScenario: {
          title: 'Common Scenario in PANS Children',
          description: "Child has MTHFR mutation (can't methylate well) + ongoing infection/mold exposure (constant toxin production) + eating fortified foods with folic acid (blocking folate receptors) + standard multivitamin with folic acid (making it worse) + antibiotic treatment (killing bacteria that produce B vitamins) = methylation crash = SEVERE worsening of symptoms. Switching to methylated B vitamins and removing folic acid often provides dramatic improvement."
        }
      }
    },
    symptoms: {
      intro: "Symptoms vary based on which genes are affected and how impaired detoxification is. Many symptoms overlap with PANS itself, but addressing methylation often improves everything.",
      mthfrSymptoms: {
        title: 'MTHFR Mutation Symptoms',
        categories: [
          {
            name: 'Neurological/Mental Health',
            items: [
              { name: 'Depression', description: 'Low SAMe, low neurotransmitters' },
              { name: 'Anxiety', description: 'Elevated homocysteine, poor methylation' },
              { name: 'OCD symptoms', description: 'Neurotransmitter imbalances' },
              { name: 'Brain fog and cognitive dysfunction', description: '' },
              { name: 'Memory problems', description: '' },
              { name: 'Irritability and mood swings', description: '' },
              { name: 'Insomnia or sleep disturbances', description: '' },
              { name: 'Difficulty focusing', description: 'ADHD-like symptoms' },
              { name: 'Sensory sensitivities', description: '' }
            ]
          },
          {
            name: 'Detoxification Issues',
            items: [
              { name: 'Chemical sensitivity', description: "Can't detoxify chemicals" },
              { name: 'Sensitivity to medications', description: "Can't metabolize properly" },
              { name: 'Reactions to supplements', description: 'Especially high-dose B vitamins' },
              { name: 'Poor tolerance to alcohol', description: '' },
              { name: "Feeling 'toxic' or 'poisoned'", description: '' },
              { name: 'Adverse reactions to anesthesia', description: '' },
              { name: 'Extreme sensitivity to smells', description: '' }
            ]
          },
          {
            name: 'Cardiovascular',
            items: [
              { name: 'Elevated homocysteine', description: 'Damages blood vessels' },
              { name: 'Increased risk of blood clots', description: '' },
              { name: 'Heart palpitations', description: '' },
              { name: 'High blood pressure', description: '' },
              { name: 'Stroke risk', description: 'Especially with high homocysteine' },
              { name: 'Atherosclerosis', description: '' }
            ]
          },
          {
            name: 'Immune & Inflammatory',
            items: [
              { name: 'Frequent infections', description: '' },
              { name: 'Autoimmune conditions', description: '' },
              { name: 'Chronic inflammation', description: '' },
              { name: 'Histamine intolerance', description: '' },
              { name: 'Allergies', description: '' },
              { name: 'Eczema or skin problems', description: '' },
              { name: 'Chronic pain and fibromyalgia-like symptoms', description: '' }
            ]
          },
          {
            name: 'Fatigue & Energy',
            items: [
              { name: 'Chronic fatigue', description: 'Impaired mitochondrial function' },
              { name: 'Low energy despite rest', description: '' },
              { name: 'Weakness', description: '' },
              { name: 'Exercise intolerance', description: '' },
              { name: 'Post-exertional malaise', description: '' }
            ]
          },
          {
            name: 'Reproductive & Hormonal',
            items: [
              { name: 'Miscarriages', description: 'Especially recurrent' },
              { name: 'Infertility', description: '' },
              { name: 'Neural tube defects in babies', description: '' },
              { name: 'Hormonal imbalances', description: '' },
              { name: 'PMS or severe menstrual symptoms', description: '' },
              { name: 'Pregnancy complications', description: '' }
            ]
          },
          {
            name: 'Other',
            items: [
              { name: 'Migraines or headaches', description: '' },
              { name: 'IBS or digestive issues', description: '' },
              { name: 'Neuropathy', description: 'Numbness, tingling' },
              { name: 'Muscle pain', description: '' },
              { name: 'Joint pain', description: '' }
            ]
          }
        ]
      },
      slowCOMT: {
        title: 'Slow COMT (Met/Met) Symptoms',
        items: [
          { name: 'High anxiety and worry', description: "Can't break down stress hormones" },
          { name: 'Rumination and overthinking', description: '' },
          { name: 'Panic attacks', description: '' },
          { name: 'Insomnia', description: 'Elevated norepinephrine at night' },
          { name: 'Extreme sensitivity to stress', description: '' },
          { name: 'Cannot tolerate stimulants', description: 'Coffee, ADHD meds cause severe anxiety' },
          { name: 'Lower pain tolerance', description: '' },
          { name: 'Estrogen dominance in women', description: 'Heavy periods, PMS, breast tenderness, fibroids' },
          { name: 'Feeling "wired"', description: '' },
          { name: 'Difficulty relaxing', description: '' }
        ]
      },
      fastCOMT: {
        title: 'Fast COMT (Val/Val) Symptoms',
        items: [
          { name: 'ADHD or difficulty focusing', description: 'Rapid dopamine breakdown' },
          { name: 'Impulsivity', description: '' },
          { name: 'Low motivation', description: '' },
          { name: 'Need for constant stimulation', description: '' },
          { name: 'Risk-taking behavior', description: '' },
          { name: 'Self-medicating with caffeine or other stimulants', description: '' },
          { name: 'Better pain tolerance', description: '' },
          { name: 'Performs well under pressure', description: '' }
        ]
      },
      cbsUpregulation: {
        title: 'CBS Upregulation Symptoms',
        items: [
          { name: 'Sulfur sensitivity', description: "Can't tolerate high-sulfur foods like eggs, garlic, broccoli" },
          { name: 'Epsom salt bath intolerance', description: 'Feel worse after' },
          { name: 'Ammonia buildup symptoms', description: 'Brain fog, irritability, confusion' },
          { name: 'Low homocysteine', description: 'Unusual - most have high' },
          { name: 'May not tolerate methylfolate', description: 'Makes symptoms worse initially' },
          { name: 'Heavy metal accumulation', description: '' }
        ]
      },
      gstDefects: {
        title: 'GST Gene Defects (Poor Glutathione Conjugation)',
        items: [
          { name: 'Extreme chemical sensitivity', description: '' },
          { name: 'Mold illness', description: "Can't detoxify mycotoxins" },
          { name: 'Multiple chemical sensitivity (MCS)', description: '' },
          { name: 'Severe reactions to pesticides, cleaning products, fragrances', description: '' },
          { name: 'Heavy metal toxicity', description: '' },
          { name: 'Chronic fatigue', description: '' },
          { name: 'Autoimmune conditions', description: '' }
        ]
      },
      keyPoint: "If PANS child has severe anxiety, chemical sensitivity, histamine issues, or isn't responding to treatment, consider methylation and detox gene testing. Supporting these pathways can be game-changing."
    },
    triggers: {
      tabLabel: 'Testing',
      intro: "Genetic testing can identify variants affecting methylation and detoxification, allowing targeted support. However, genes load the gun - environment pulls the trigger. Testing helps guide treatment but clinical picture most important.",
      geneticTesting: {
        title: 'Genetic Testing Options',
        options: [
          {
            name: '23andMe or AncestryDNA (Most Accessible)',
            cost: '$99-199',
            whatYouGet: 'Raw genetic data file with 600,000+ SNPs (single nucleotide polymorphisms)',
            nextStep: 'Upload raw data to interpretation sites (see below)',
            pros: 'Inexpensive, easy, comprehensive genetic data',
            cons: "Doesn't interpret methylation genes - need additional service"
          }
        ],
        interpretationSites: {
          title: 'Raw Data Interpretation Sites',
          intro: 'Upload 23andMe/Ancestry raw data to these services:',
          sites: [
            { name: 'StrateGene (Seeking Health - Dr. Ben Lynch)', cost: '$40-50', description: 'Excellent methylation and detox gene report. Clear explanations, actionable recommendations.' },
            { name: 'Genetic Genie (Free - Donations)', cost: 'Free', description: 'Methylation and detox reports. Basic but good starting point.' },
            { name: 'LiveWello', cost: '$20-40', description: 'Comprehensive SNP analysis including methylation.' },
            { name: 'NutraHacker', cost: '$50-100', description: 'Very detailed reports on methylation, neurotransmitters, detox.' }
          ]
        },
        medicalGrade: {
          title: 'Medical-Grade Genetic Testing',
          items: [
            'Genomind, GeneSight: Focus on psychiatric medication metabolism. Some methylation genes. Expensive ($300-2000), may be covered by insurance.',
            'Whole Genome Sequencing: Most comprehensive. $300-1000. More data than 23andMe but interpretation more complex.'
          ]
        },
        directMTHFR: {
          title: 'Direct MTHFR Testing (Lab)',
          items: [
            { label: 'Who offers', value: 'Quest, LabCorp, Genova Diagnostics' },
            { label: 'Cost', value: '$100-300' },
            { label: 'What it tests', value: 'Only C677T and A1298C variants (most common)' },
            { label: 'Pros', value: 'Insurance may cover, medical-grade' },
            { label: 'Cons', value: 'Only tests 2 MTHFR variants, misses all other methylation genes' }
          ]
        }
      },
      keyGenes: {
        title: 'Key Genes to Look For',
        genes: [
          'MTHFR: C677T and A1298C variants',
          'COMT: V158M (rs4680)',
          'MTR: A2756G',
          'MTRR: A66G',
          'CBS: C699T, A360A',
          'BHMT: R239Q',
          'SUOX: Various',
          'GST genes: GSTM1, GSTT1 (deletion common), GSTP1',
          'SOD2: A16V',
          'MAO-A: R297R',
          'VDR: Multiple variants'
        ]
      },
      functionalTesting: {
        title: 'Functional Testing (Shows Current Status)',
        intro: "Genetic testing shows potential. Functional testing shows what's actually happening right now.",
        tests: [
          {
            name: 'Homocysteine (Blood Test)',
            optimal: '6-7 µmol/L',
            elevated: 'Elevated (>10 µmol/L): Indicates methylation problems',
            meaning: 'High homocysteine = MTHFR, MTR, MTRR not working well. Need methylfolate, methylB12.',
            veryLow: 'Very low (<6 µmol/L): May indicate CBS upregulation'
          },
          {
            name: 'Methylmalonic Acid (MMA) - Urine or Blood',
            whatItShows: 'B12 functional status at cellular level',
            elevated: 'Elevated: Functional B12 deficiency even if blood B12 looks normal'
          },
          {
            name: 'Organic Acids Test (OAT) - Urine',
            description: 'Shows methylation metabolites, neurotransmitter metabolites, mitochondrial function, detox capacity',
            providers: 'Great Plains Laboratory, Mosaic Diagnostics (formerly Great Plains), Genova',
            cost: '$300-400, comprehensive functional picture'
          },
          {
            name: 'Neurotransmitter Testing - Urine',
            description: 'Shows dopamine, serotonin, norepinephrine, epinephrine, GABA levels and metabolites',
            helps: 'Helps understand COMT, MAO function',
            note: "Controversial (some say urine levels don't reflect brain levels) but clinically useful"
          },
          {
            name: 'Glutathione Testing',
            blood: 'Total glutathione, reduced vs oxidized ratio',
            important: 'Low glutathione = poor detoxification, oxidative stress'
          }
        ]
      },
      recommendations: {
        title: 'Testing Recommendations',
        steps: [
          '1. Start with 23andMe ($99-199) - Get comprehensive genetic data',
          '2. Upload to StrateGene ($40) - Best methylation interpretation',
          '3. Get homocysteine test ($30-50) - Shows current methylation status',
          '4. Consider OAT ($300-400) - If budget allows, very comprehensive',
          '5. Work with practitioner familiar with methylation - Interpretation and protocol design'
        ]
      },
      cautions: {
        title: 'Important Cautions',
        items: [
          "Don't panic about genetic results: Having mutations doesn't mean they're all active/problematic",
          'Epigenetics matter: Lifestyle, environment, supplements can turn genes on or off',
          'Clinical picture > genetics: How you feel and functional tests more important than genetic results',
          'Start low, go slow: When beginning methylation support based on results',
          'Not all interpretations accurate: Some sites over-interpret significance of variants'
        ]
      }
    },
    supplements: {
      isExtendedMTHFR: true,
      intro: "Supporting methylation and detoxification requires specific forms of nutrients that bypass genetic bottlenecks. START LOW AND GO SLOW - methylation support can cause strong reactions if started too aggressively.",
      criticalWarning: {
        title: 'CRITICAL WARNING: Start Low, Go Slow',
        description: 'Methylation supplements can cause intense reactions ("overmethylation") if started at full doses:',
        symptoms: ['Anxiety, irritability, insomnia', 'Headaches, brain fog', 'Nausea, heart palpitations', 'Feeling "wired" or "overstimulated"'],
        solution: 'Start with 1/4 or even 1/8 of recommended dose. Increase every 3-5 days if tolerated. If reaction occurs, stop 2-3 days, then restart at lower dose.'
      },
      items: [
        {
          name: 'Methylfolate (5-MTHF)',
          dosage: 'Start 200-400 mcg, build to 800-5000 mcg',
          evidence: 'Essential for MTHFR',
          benefits: [
            'Bypasses MTHFR mutation - provides active folate directly',
            'Lowers homocysteine',
            'Supports methylation cycle',
            'Improves mood (increases SAMe production)',
            'Essential for neurotransmitter production',
            'Reduces inflammation',
            'Supports detoxification'
          ],
          note: 'Forms: L-methylfolate (Metafolin®, Quatrefolic®) most bioavailable. Start LOW (200 mcg) especially if severe MTHFR mutation. Some need 5-15mg for therapeutic effect but must build slowly. Take with methylB12 always. Can cause overstimulation if dose too high. NEVER use folic acid if you have MTHFR mutation.'
        },
        {
          name: 'Methylcobalamin (Methyl-B12)',
          dosage: 'Start 500-1000 mcg, build to 1000-5000 mcg',
          evidence: 'Essential for methylation',
          benefits: [
            'Active form of B12',
            'Works with methylfolate in methylation cycle',
            'Lowers homocysteine',
            'Supports nerve function',
            'Increases energy',
            'Improves cognitive function',
            'Required for MTR/MTRR function'
          ],
          note: 'Sublingual or injection most effective. Must take with methylfolate (they work together). Some need hydroxocobalamin or adenosylcobalamin instead/additionally. High doses (5000-10,000 mcg sublingual or shots) often needed in severe methylation problems. Blood B12 may look high on labs but functional deficiency can still exist (check MMA).'
        },
        {
          name: 'P5P (Pyridoxal-5-Phosphate) - Active B6',
          dosage: '25-50 mg daily',
          evidence: 'Important cofactor',
          benefits: [
            'Active form of B6',
            'Cofactor for many enzymes including CBS',
            'Supports neurotransmitter production',
            'Helps COMT function',
            'Reduces homocysteine',
            'Supports hormone metabolism'
          ],
          note: "More bioavailable than regular B6. Don't exceed 100mg daily long-term (peripheral neuropathy risk). Part of B-complex is fine."
        },
        {
          name: 'Methylated B-Complex',
          dosage: 'Start with 1/4-1/2 capsule, build to 1-2 daily',
          evidence: 'Essential foundation',
          benefits: [
            'Provides all B vitamins in active forms',
            'Supports entire methylation cycle',
            'More comprehensive than individual Bs',
            'Supports energy production',
            'Nervous system support'
          ],
          note: 'Must contain methylfolate and methylB12 (not folic acid or cyanocobalamin). Brands: Thorne, Pure Encapsulations, Seeking Health. Start LOW - methylated B-complex can be overstimulating. Take in morning (can interfere with sleep).'
        },
        {
          name: 'TMG (Trimethylglycine/Betaine)',
          dosage: '500-3000 mg daily',
          evidence: 'Strong evidence',
          benefits: [
            'Donates methyl groups directly',
            'Bypasses MTHFR via BHMT pathway',
            'Lowers homocysteine',
            "Alternative methylation support if can't tolerate methylfolate",
            'Supports liver detoxification',
            'Digestive support (increases stomach acid)'
          ],
          note: "Excellent for those who can't tolerate methylfolate. Particularly helpful with BHMT mutations. Some find it calming vs stimulating from methylfolate. Take with meals. Can increase stomach acid (good for digestion but may bother those with ulcers)."
        },
        {
          name: 'SAMe (S-Adenosylmethionine)',
          dosage: '200-800 mg daily',
          evidence: 'Strong evidence',
          benefits: [
            "Body's universal methyl donor",
            'Bypasses entire methylation cycle - provides end product',
            'Powerful antidepressant (studies show equal to prescription antidepressants)',
            'Supports liver detoxification',
            'Reduces inflammation',
            'Supports joint health',
            'Increases glutathione production'
          ],
          note: "Take on empty stomach for best absorption. Can be very activating - take in morning. May cause anxiety/mania if bipolar. Expensive but very effective. Good option if can't tolerate methylfolate or if need immediate mood support. Don't take with antidepressants without doctor supervision."
        },
        {
          name: 'Magnesium (Glycinate form best)',
          dosage: '300-600 mg daily',
          evidence: 'Essential cofactor',
          benefits: [
            'Cofactor for COMT enzyme (required for function)',
            'Slows down COMT (helpful for slow COMT/high anxiety)',
            'Calms nervous system',
            'Supports sleep',
            'Muscle relaxation',
            'Hundreds of enzymatic processes',
            'Often severely deficient'
          ],
          note: 'CRITICAL for slow COMT. Magnesium glycinate most absorbable and calming. Take before bed. Essential supplement for almost everyone with methylation issues. Deficiency worsens all methylation problems.'
        },
        {
          name: 'Niacin (B3) or Niacinamide',
          dosage: '50-500 mg as needed',
          evidence: 'Clinical use for overmethylation',
          benefits: [
            'Slows methylation (methyl group scavenger)',
            'Calms overmethylation symptoms',
            'Reduces anxiety from too much methylation',
            'Can help if methylfolate dose too high',
            'Supports detoxification'
          ],
          note: "Niacin causes flush (face turns red, hot, itchy for 15-30min) - harmless but uncomfortable. Niacinamide (no-flush) better for most. Use as 'rescue' if feel overmethylated. Slows down methylation so helpful for those who feel too 'revved up.' Don't use if undermethylated/depressed."
        },
        {
          name: 'Glutathione (Liposomal or IV)',
          dosage: '500-1000 mg liposomal daily, or IV weekly/bi-weekly',
          evidence: 'Strong evidence',
          benefits: [
            'Master antioxidant and detoxifier',
            'End product of methylation cycle',
            'Directly eliminates toxins',
            'Protects cells from oxidative stress',
            'Supports liver detoxification',
            'Reduces neuroinflammation',
            'Often severely depleted in chronic illness'
          ],
          note: "Liposomal form much better absorbed than regular capsules (which don't work well). IV glutathione can provide dramatic relief. Expensive. Precursors (NAC, glycine, glutamine) help body make its own. Some feel worse initially (detox reaction)."
        },
        {
          name: 'NAC (N-Acetylcysteine)',
          dosage: '600-1800 mg daily',
          evidence: 'Strong evidence',
          benefits: [
            "Precursor to glutathione - increases body's production",
            'Powerful antioxidant',
            'Breaks up biofilms (infections hide in biofilms)',
            'Supports liver detoxification',
            'Reduces OCD symptoms',
            'Thins mucus (helpful for sinus issues)',
            'Supports mitochondria'
          ],
          note: 'Take on empty stomach for best absorption. Start low, build up. Can cause nausea if too high dose. Less expensive than glutathione supplements but still very effective. Critical for detoxification support.'
        },
        {
          name: 'Alpha-Lipoic Acid (ALA)',
          dosage: '300-600 mg daily',
          evidence: 'Strong evidence',
          benefits: [
            'Universal antioxidant (works in water and fat)',
            'Regenerates other antioxidants (glutathione, vitamins C & E)',
            'Chelates heavy metals',
            'Protects mitochondria',
            'Supports detoxification',
            'Reduces inflammation'
          ],
          note: 'R-lipoic acid more bioavailable (and expensive). Take on empty stomach. Important for Phase II detoxification. Helps eliminate heavy metals but must have good elimination pathways open first.'
        },
        {
          name: 'Molybdenum',
          dosage: '150-500 mcg daily',
          evidence: 'Important for sulfur metabolism',
          benefits: [
            'Cofactor for SUOX enzyme (converts sulfite to sulfate)',
            'Essential for people with CBS upregulation or SUOX mutations',
            'Helps sulfur sensitivity',
            'Supports aldehyde detoxification',
            'Reduces Candida die-off symptoms'
          ],
          note: "Often forgotten but critical for those with sulfur issues. Helps process sulfur-containing amino acids. Trace mineral - don't need much. Part of some multivitamins."
        },
        {
          name: 'Zinc',
          dosage: '15-30 mg daily',
          evidence: 'Important cofactor',
          benefits: [
            'Cofactor for hundreds of enzymes',
            'Supports SOD2 (antioxidant enzyme)',
            'Essential for immune function',
            'Supports detoxification',
            'Often deficient',
            'Supports COMT function'
          ],
          note: 'Take with food. Balance with copper (10:1 ratio). Zinc picolinate or glycinate most absorbable. Deficiency common and worsens all methylation issues.'
        },
        {
          name: 'Folinic Acid (NOT Folic Acid)',
          dosage: '800 mcg to 5 mg',
          evidence: 'Alternative to methylfolate',
          benefits: [
            'Reduced form of folate (closer to active than folic acid)',
            'Better tolerated than methylfolate for some',
            'Supports methylation via different pathway',
            'Can convert to methylfolate in body',
            'Less likely to cause overmethylation symptoms'
          ],
          note: 'Good option if methylfolate too stimulating. Calcium folinate or leucovorin. Not as direct as methylfolate but still helpful. Some need combination of methylfolate and folinic acid.'
        },
        {
          name: 'Riboflavin (B2)',
          dosage: '10-400 mg daily',
          evidence: 'Required cofactor',
          benefits: [
            'Cofactor for MTHFR enzyme',
            'Required for MTHFR to work',
            'Supports mitochondrial function',
            'May reduce migraines (high dose)',
            'Often overlooked but essential'
          ],
          note: "Higher doses (400mg) used for migraine prevention. Part of B-complex. Turns urine bright yellow (harmless). Important cofactor - MTHFR won't work without adequate B2."
        }
      ],
      buildingProtocol: {
        title: 'Building Your Protocol: Step-by-Step',
        steps: [
          '1. Start with basics (Week 1-2): Magnesium, general B-complex (non-methylated initially), omega-3',
          '2. Add methylfolate (Week 3-4): Start 200 mcg, increase slowly every 3-5 days if tolerated',
          '3. Add methylB12 (Week 4-5): Start 500-1000 mcg. Always take with methylfolate.',
          '4. Switch to methylated B-complex (Week 5-6): Replace regular B-complex',
          '5. Add detox support (Week 6+): NAC, glutathione, ALA as tolerated',
          '6. Add TMG or SAMe if needed: For additional methylation support',
          '7. Keep niacin on hand: For overmethylation rescue',
          '8. Increase doses gradually: Every 2-4 weeks as tolerated'
        ]
      },
      reactionProtocol: {
        title: 'What to Do If You Have a Reaction',
        steps: [
          'Stop the supplement that caused reaction for 2-3 days',
          'Take niacin or niacinamide 50-100mg to slow methylation',
          'Increase magnesium',
          'Hydrate well',
          'Rest - let system calm down',
          'Restart at 1/4 the dose that caused problem',
          "If continues: May need different form or approach (folinic acid vs methylfolate, TMG vs methylfolate)",
          'Work with practitioner if reactions severe or confusing'
        ]
      }
    },
    lifestyle: {
      isExtendedMTHFR: true,
      title: 'Lifestyle for Methylation & Detox Support',
      intro: "Lifestyle choices profoundly impact methylation and detoxification. Genetics load the gun, but environment and lifestyle pull the trigger.",
      categories: [
        {
          icon: '🛡️',
          name: 'Reduce Toxic Burden (Most Important)',
          importance: 'CRITICAL',
          importanceNote: '',
          items: [
            "Can't out-supplement toxic exposure: If constantly exposed to toxins, methylation and detox systems overwhelmed regardless of supplements.",
            "Avoid pesticides: Buy organic for 'Dirty Dozen' foods minimum (strawberries, spinach, apples, grapes, tomatoes, bell peppers, cherries, peaches, pears, celery, potatoes, kale). Conventionally grown produce covered in pesticides that impair detoxification.",
            'Filter water: Municipal water contains chlorine, fluoride, pharmaceutical residues, heavy metals. Reverse osmosis or high-quality carbon filter essential.',
            'No plastic: Especially for food/drinks. BPA, phthalates disrupt hormones and impair detoxification. Glass, stainless steel only.',
            "Fragrance-free everything: Air fresheners, candles, laundry detergent, personal care products. 'Fragrance' contains dozens of unlabeled chemicals that overload detox.",
            'Natural cleaning products: Conventional cleaners highly toxic. Vinegar, baking soda, hydrogen peroxide for most cleaning. Branch Basics, Seventh Generation if buying.',
            'Mold remediation if present: Mold overwhelms detox systems. Must address if present in home.',
            'EMF reduction: Turn off WiFi at night, limit screen time, keep phone away from body. EMFs may impair detoxification.',
            "Avoid Teflon/non-stick: PFOA and related chemicals (forever chemicals) extremely toxic, impair detoxification. Cast iron, stainless steel, ceramic for cookware.",
            'Check personal care products: Think Dirty app or EWG Skin Deep database. Most conventional products full of endocrine disruptors.',
            'No synthetic fragrances: Perfume, cologne, scented lotions. Hundreds of chemicals, neurotoxic.',
            "Avoid fire retardants: Furniture, mattresses. Highly toxic chemicals that bioaccumulate. Look for 'no flame retardants' labels."
          ]
        },
        {
          icon: '💧',
          name: 'Support Detoxification Pathways',
          importance: 'HIGH',
          importanceNote: '',
          items: [
            'Hydration essential: Half body weight in ounces minimum. Water carries toxins out through kidneys. Dehydration = toxin accumulation.',
            'Sweat regularly: Infrared sauna, hot baths, exercise (if tolerated). Skin major elimination route. Sweat out toxins.',
            'Daily bowel movements mandatory: Constipation = reabsorbing toxins from stool. Magnesium, vitamin C, fiber, water all help. Must eliminate daily minimum.',
            'Castor oil packs: Over liver, 3x week. Increases lymphatic drainage, supports liver detoxification.',
            'Dry brushing: Before shower, brush toward heart. Stimulates lymphatic system (carries toxins).',
            'Rebounding (mini trampoline): Gentle bouncing moves lymph fluid. 10-15 minutes daily if able.',
            'Massage or lymphatic drainage: Professional or at-home. Moves toxins through lymph for elimination.',
            'Deep breathing: Lungs eliminate toxins. 10 deep breaths several times daily.',
            'Epsom salt baths: Sulfate supports detoxification. 2 cups Epsom salt, 20 min. Note: If CBS upregulation/sulfur sensitivity, may not tolerate. Start with 1/4 cup.',
            'Foot baths: Bentonite clay or Epsom salt foot soaks. Pulls toxins through feet.',
            'Support bile flow: Bile carries toxins out. Bitter foods (arugula, dandelion greens), lemon water, beet kvass, ox bile supplements help.'
          ]
        },
        {
          icon: '🌙',
          name: 'Optimize Sleep',
          importance: 'CRITICAL',
          importanceNote: '',
          items: [
            "Detoxification happens during sleep: Glymphatic system (brain's waste clearance) most active during deep sleep. Poor sleep = toxin buildup in brain.",
            '9-11 hours for children, 8-9 for adults: Non-negotiable for healing.',
            'Consistent schedule: Same sleep/wake time. Circadian rhythm regulates detoxification enzymes.',
            'Dark room: Blackout curtains, no light sources. Light disrupts melatonin and detoxification.',
            'Cool temperature: 65-68°F. Body detoxifies better when cool.',
            'No screens 2 hours before bed: Blue light blocks melatonin, disrupts detox cycles.',
            'Magnesium before bed: 400-600mg. Supports sleep and detoxification.',
            'Address methylation: Poor methylation often causes insomnia. Supporting methylation improves sleep.',
            'Slow COMT need more help: High nighttime norepinephrine prevents sleep. Magnesium, phosphatidylserine, L-theanine help.'
          ]
        },
        {
          icon: '🧘',
          name: 'Stress Management (Affects Gene Expression)',
          importance: 'HIGH',
          importanceNote: '',
          items: [
            "Stress turns off detox genes: Chronic stress downregulates detoxification enzymes. Can't detoxify well when stressed.",
            "Slow COMT particularly sensitive: Can't break down stress hormones. Stress impacts them more severely.",
            'Daily practice non-negotiable: Meditation, breathwork, yoga, prayer - something daily to calm nervous system.',
            'Boundaries essential: Say no to energy drainers. Protect your peace.',
            'Therapy if needed: Chronic illness creates trauma. Processing helps.',
            'Adaptogenic herbs: Ashwagandha, rhodiola, holy basil help stress response (but check with practitioner - some interact with methylation).',
            'Time in nature: Reduces cortisol, supports healing.',
            'Avoid stressful media: News, violent shows, doom-scrolling increase inflammation and impair detoxification.',
            'Social connection: Isolation increases inflammation. Connect with supportive people.',
            "Don't fight genetic reality: Slow COMT shouldn't try to be high-stress warrior. Accept your biology, work with it."
          ]
        },
        {
          icon: '🏃',
          name: 'Exercise Appropriately for Your Type',
          importance: 'MODERATE',
          importanceNote: '',
          items: [
            "Slow COMT (worrier): Gentle exercise best. Yoga, walking, swimming. High-intensity worsens anxiety (can't break down adrenaline fast enough). Overexercise counterproductive.",
            'Fast COMT (warrior): Thrive on intense exercise. HIIT, competitive sports, heavy weights. Need that stimulation.',
            'General guidelines: Movement supports lymphatic drainage and detoxification. But must not exceed capacity.',
            'Listen to body: Post-exertional malaise = too much. Back off.',
            'Start low if detox impaired: 5-10 min walking. Build very gradually.',
            'Sweat is goal: For detoxification. Even gentle movement that produces sweat helps eliminate toxins.',
            'Timing matters: Morning or afternoon. Avoid evening (disrupts sleep, especially slow COMT).',
            'Outdoor exercise ideal: Nature + movement = stress reduction + detox support.',
            "Don't compare: Your genetic capacity may differ from others. Work with your biology."
          ]
        },
        {
          icon: '🍽️',
          name: 'Optimize Digestion & Gut Health',
          importance: 'HIGH',
          importanceNote: '',
          items: [
            "Gut produces/uses methylation nutrients: Healthy gut bacteria produce B vitamins. Damaged gut can't absorb nutrients or support methylation.",
            'Heal leaky gut: L-glutamine, bone broth, eliminating inflammatory foods. Leaky gut allows toxins into bloodstream.',
            'Probiotics essential: Multi-strain, 50-100 billion CFU. Supports methylation and detoxification.',
            'Prebiotic foods: Feed good bacteria. Asparagus, garlic, onions, cooked and cooled potatoes.',
            'Digestive enzymes if needed: Support proper breakdown. Reduces gut burden.',
            'Address dysbiosis: SIBO, candida overgrowth impair detoxification. Test and treat if suspected.',
            'Chew thoroughly: Digestion starts in mouth. Poor chewing = poor digestion = increased toxin production.',
            "Don't eat when stressed: Stress shuts down digestion. Eat in calm state.",
            'Bitter foods stimulate bile: Arugula, dandelion greens, radicchio. Bile carries toxins out.',
            'Apple cider vinegar: 1 tbsp in water before meals. Supports stomach acid (needed for digestion and detox).'
          ]
        },
        {
          icon: '💊',
          name: 'Manage Medication Carefully',
          importance: 'MODERATE-HIGH',
          importanceNote: '',
          items: [
            'Many meds impair methylation: Proton pump inhibitors (Prilosec, Nexium), metformin, birth control pills all deplete B vitamins and impair methylation.',
            "Slow COMT can't tolerate stimulants: ADHD meds, caffeine cause severe anxiety. May need non-stimulant options.",
            'Genetic testing for medication metabolism: CYP450 genes determine how you process medications. GeneSight, Genomind test this. Can prevent adverse reactions.',
            'Start low, go slow: With any new medication. Impaired detoxification means meds stay in system longer.',
            'Work with knowledgeable prescriber: Who understands methylation genetics and medication interactions.',
            'Avoid unnecessary medications: Every medication is burden on detox systems.',
            'Support detox if on medications: Extra methylation support, liver support, plenty of water.',
            "Don't stop meds without doctor: But do discuss alternatives if causing problems."
          ]
        },
        {
          icon: '☀️',
          name: 'Sunshine & Vitamin D',
          importance: 'MODERATE',
          importanceNote: '',
          items: [
            'VDR gene variations common: Vitamin D receptor mutations mean need more vitamin D for same benefit.',
            "10-30 min sun daily: Without sunscreen (but don't burn). Vitamin D production + circadian rhythm regulation.",
            'Vitamin D essential: For immune function, mood, detoxification. Methylation requires adequate vitamin D.',
            'Test levels: Aim for 60-80 ng/mL. Many need high doses (5000-10,000 IU) to reach this.',
            'Take with K2 and magnesium: Work together. K2 directs calcium properly, magnesium required for D activation.',
            'Morning sun best: Sets circadian rhythm. Supports melatonin production at night (detoxification).',
            'Red light therapy: May support mitochondria and detoxification. 10-20 min daily.'
          ]
        },
        {
          icon: '⏰',
          name: 'Timing of Supplements',
          importance: 'MODERATE',
          importanceNote: '',
          items: [
            'Methylation support in morning: Methylfolate, methylB12, B-complex can be energizing. Take early.',
            'Magnesium before bed: Calming, supports sleep.',
            'Detox support away from meals: NAC, glutathione, ALA on empty stomach for best absorption.',
            'Methylation with food: Or may cause nausea.',
            'SAMe on empty stomach: Morning, 30 min before food.',
            'Niacin as needed: When feel overmethylated.',
            'Consistency matters: Same time each day. Body expects it, works better.',
            "Don't take everything at once: Spread throughout day. Easier on system."
          ]
        },
        {
          icon: '🧬',
          name: 'Personalize Based on Genes',
          importance: 'HIGH',
          importanceNote: '',
          items: [
            'Slow COMT: Avoid stimulants (coffee, ADHD meds), manage stress meticulously, prioritize magnesium, gentle exercise, support estrogen metabolism.',
            'Fast COMT: May benefit from moderate caffeine, need higher dopamine precursors (tyrosine), can handle more stress and intense exercise.',
            'MTHFR mutation: NEVER use folic acid. Methylfolate essential. Higher doses often needed.',
            'CBS upregulation: Limit sulfur foods, avoid Epsom salts initially, support ammonia clearance (yucca, ornithine), may not tolerate high-dose methylfolate initially.',
            'GST deletions/mutations: Extreme caution with chemical exposure. Heavy antioxidant support. Focus on glutathione.',
            'VDR mutations: Higher vitamin D doses needed.',
            'This is personalized medicine: Your genetics unique. What works for others may not work for you. Track responses, adjust accordingly.'
          ]
        }
      ],
      keyTakeaways: [
        "You can't supplement your way out of a toxic lifestyle. Reduce exposures first, then support detoxification.",
        'Work WITH your genetics, not against them.',
        'This is personalized medicine - what you need may look very different from what someone else needs.',
        'Be patient, track responses, adjust as needed.'
      ]
    },
    diet: {
      isExtendedMTHFR: true,
      title: 'Methylation-Supporting, Detox-Friendly Diet',
      intro: "Diet provides the raw materials for methylation and detoxification. Without proper nutrients, these systems can't work regardless of genetics or supplements.",
      include: {
        icon: '✅',
        title: 'Foods That Support Methylation & Detox',
        categories: [
          {
            name: 'Folate-Rich Foods (Natural, NOT Folic Acid)',
            items: [
              'Dark leafy greens: Spinach, kale, collards, Swiss chard, romaine, arugula (highest sources)',
              'Cruciferous vegetables: Broccoli, Brussels sprouts, cauliflower, cabbage',
              'Asparagus, beets, avocado, okra',
              'Legumes: Lentils, chickpeas, black beans (if tolerated)',
              'Goal: Multiple servings daily. Raw or lightly cooked (folate destroyed by heat).'
            ]
          },
          {
            name: 'B12 Sources (Animal Products)',
            items: [
              'Grass-fed beef, lamb, bison (highest)',
              "Liver (beef or chicken) (extremely high - nature's multivitamin)",
              'Wild-caught fish: Salmon, trout, sardines, mackerel',
              'Eggs (pasture-raised)',
              'Shellfish: Clams, oysters, mussels (if tolerated)',
              'Vegetarians/vegans MUST supplement B12 (methylcobalamin). No plant sources.'
            ]
          },
          {
            name: 'Cruciferous Vegetables (Detox Superstars)',
            items: [
              'Broccoli, Brussels sprouts, cauliflower, cabbage, kale, collards, bok choy, arugula, watercress',
              'Why critical: Contain sulforaphane and indole-3-carbinol. Upregulate Phase II detoxification enzymes (especially GST). Dramatically increase detoxification capacity.',
              "How to eat: Lightly steam or eat raw. Chopping/chewing activates sulforaphane. Don't overcook (destroys benefits).",
              'Note: If CBS upregulation/sulfur sensitivity, may not tolerate large amounts initially. Start small, build tolerance.'
            ]
          },
          {
            name: 'Sulfur-Rich Foods (Glutathione Building Blocks)',
            items: [
              'Allium vegetables: Garlic, onions, leeks, shallots, scallions (very high)',
              'Eggs (high in cysteine for glutathione)',
              'Cruciferous vegetables (also sulfur-rich)',
              'Grass-fed beef, wild fish',
              'CBS upregulation: These may need to be limited initially. Monitor symptoms.'
            ]
          },
          {
            name: 'Beets & Beet Greens (Betaine/TMG Source)',
            items: [
              'Provide betaine (TMG) which supports methylation via BHMT pathway.',
              'Also support liver detoxification and bile flow.'
            ]
          },
          {
            name: 'Colorful Vegetables (Antioxidants for Detox)',
            items: [
              'Bell peppers, carrots, sweet potatoes, squash, tomatoes (if tolerated).',
              'Antioxidants protect during detoxification.'
            ]
          },
          {
            name: 'Bitter Greens (Support Bile/Detox)',
            items: [
              'Dandelion greens, arugula, radicchio, endive.',
              'Stimulate bile production (bile carries toxins out).'
            ]
          },
          {
            name: 'High-Quality Proteins',
            items: [
              'Grass-fed/pasture-raised meats: Provide amino acids for methylation and glutathione',
              'Wild-caught fish: Omega-3s reduce inflammation, protein for glutathione',
              'Eggs: Complete protein, choline (methylation), cysteine (glutathione)',
              'Bone broth: Glycine and glutamine (both support methylation and detox)'
            ]
          },
          {
            name: 'Healthy Fats (Essential)',
            items: [
              'Extra virgin olive oil: Polyphenols support detoxification',
              'Avocado: Glutathione, healthy fats (if tolerated)',
              'Wild-caught fatty fish: Omega-3s reduce inflammation',
              'Coconut oil: MCTs, antimicrobial',
              'Nuts/seeds: Walnuts, pumpkin seeds, chia, flax (if tolerated)'
            ]
          },
          {
            name: 'Herbs & Spices (Powerful Support)',
            items: [
              'Turmeric: Upregulates detox enzymes, anti-inflammatory',
              'Cilantro: Chelates heavy metals',
              'Parsley: Supports kidney detoxification',
              'Ginger: Anti-inflammatory, digestive support',
              'Garlic: Sulfur, supports glutathione, antimicrobial',
              'Rosemary: Liver protective, antioxidant'
            ]
          }
        ]
      },
      avoid: {
        icon: '🔴',
        title: 'Foods That Impair Methylation & Detox',
        categories: [
          {
            name: 'Folic Acid (CRITICAL TO AVOID)',
            reason: "Synthetic form that MTHFR can't process. Builds up, blocks folate receptors, prevents real folate from working. Makes MTHFR mutation worse.",
            items: [
              'Fortified grains: Bread, pasta, cereal, crackers, flour (US law requires fortification)',
              'Energy drinks',
              'Protein powders (check labels)',
              'Most multivitamins (unless specifically methylated)',
              'Fortified plant milks',
              'Read every label. "Enriched" = contains folic acid. If you have MTHFR mutation, this is poison to your methylation.'
            ]
          },
          {
            name: 'Alcohol',
            reason: 'Depletes B vitamins (especially folate, B12, B6). Impairs methylation. Overburdens liver detoxification. Produces acetaldehyde (very toxic). Depletes glutathione.',
            items: [
              'Even moderate drinking problematic when methylation impaired.',
              'Avoid completely during healing.'
            ]
          },
          {
            name: 'Sugar & Refined Carbs',
            reason: 'Depletes B vitamins (needed for metabolism). Creates oxidative stress. Feeds inflammation. Impairs methylation. Depletes magnesium.',
            items: [
              'White sugar, brown sugar, high fructose corn syrup',
              'Candy, cookies, cakes, pastries',
              'Soda and sugary drinks',
              'White bread, white pasta, white rice'
            ]
          },
          {
            name: 'Processed Foods',
            reason: 'Contain folic acid, additives, preservatives, inflammatory oils. Burden detoxification. Lack nutrients needed for methylation.',
            items: [
              'Fast food, frozen dinners',
              'Packaged snacks',
              'Processed meats (deli meat, hot dogs)',
              'Anything with long ingredient list'
            ]
          },
          {
            name: 'Conventional Dairy & Meat',
            reason: 'Antibiotics (kill gut bacteria that produce B vitamins). Hormones (burden detoxification). Inflammatory for many. Pesticide residues in fat.',
            items: [
              'If eating animal products, organic/grass-fed/pasture-raised essential.',
              'Otherwise toxic burden too high.'
            ]
          },
          {
            name: 'Food Additives',
            reason: 'Toxic, burden Phase II detoxification. Many are neurotoxic.',
            items: [
              'Artificial colors and dyes (especially harmful)',
              'MSG and hidden MSG (natural flavors, yeast extract)',
              'Artificial sweeteners (aspartame, sucralose)',
              'Preservatives (BHT, BHA, sodium benzoate)'
            ]
          },
          {
            name: 'High-Sulfur Foods (If CBS Upregulation)',
            reason: 'If you have CBS upregulation/sulfur sensitivity, limit these. Note: This is specific to CBS issues. Most people with methylation problems need these foods. Monitor symptoms.',
            items: [
              'Eggs',
              'Garlic, onions',
              'Cruciferous vegetables (in large amounts)',
              'Red meat',
              'Dairy'
            ]
          }
        ]
      },
      sampleDay: {
        title: 'Sample Day of Methylation-Supporting Eating',
        meals: [
          { name: 'Upon Waking', description: 'Large glass of filtered water with lemon. Methylation supplements.' },
          { name: 'Breakfast', description: 'Pasture-raised eggs scrambled with spinach, kale, and onions cooked in coconut oil. Side of sautéed asparagus with garlic. Handful of berries.' },
          { name: 'Mid-Morning', description: 'Green smoothie: Spinach, kale, cilantro, parsley, avocado, berries, water/coconut milk' },
          { name: 'Lunch', description: 'Huge salad: Mixed greens, arugula, broccoli sprouts, beets, carrots, bell peppers, pumpkin seeds, grilled wild salmon or grass-fed beef. Olive oil and lemon dressing with fresh herbs.' },
          { name: 'Afternoon', description: 'Celery sticks with almond butter, or walnuts and berries' },
          { name: 'Dinner', description: 'Grass-fed beef or wild fish, roasted Brussels sprouts and cauliflower with garlic and turmeric, steamed broccoli, large side salad. Bone broth as soup or drink.' },
          { name: 'Evening', description: 'Herbal tea (ginger, chamomile). Magnesium supplement.' }
        ]
      },
      tips: {
        title: 'Practical Diet Guidelines',
        items: [
          'Organic essential: Pesticides impair methylation and detoxification',
          'Read every label: Avoid folic acid at all costs if MTHFR mutation',
          'Eat rainbow of vegetables: 7-9 servings daily. More is better.',
          'Leafy greens daily: Best folate sources. Raw or lightly cooked.',
          'Quality over quantity: Grass-fed, wild-caught, organic worth the cost',
          'Cook at home: Only way to control ingredients',
          'Prep when able: Wash and chop vegetables when you have energy',
          'Smoothies are friend: Easy way to get massive amounts of greens',
          "Don't fear fat: Needed for hormone production and fat-soluble vitamin absorption",
          'Hydration critical: Half body weight in ounces of filtered water',
          'Consider elimination diet: If multiple sensitivities, remove common triggers 4-6 weeks',
          'Personalize: CBS upregulation needs different approach. Listen to your body.'
        ]
      }
    }
  },

  'mold': {
    name: 'Mold Illness (CIRS)',
    color: 'bg-stone-500',
    lightColor: 'bg-stone-500/10',
    textColor: 'text-stone-600',
    description: 'Chronic Inflammatory Response Syndrome (CIRS) - a complex, multi-system inflammatory condition triggered by exposure to water-damaged buildings and biotoxins',
    about: {
      overview: `**What is Mold Illness (CIRS)?**

Chronic Inflammatory Response Syndrome (CIRS), commonly known as mold illness, is a complex, multi-system inflammatory condition triggered by exposure to water-damaged buildings and the biotoxins they harbor. It's a serious condition that can devastate health and quality of life.

**Understanding CIRS**

CIRS was identified and researched extensively by Dr. Ritchie Shoemaker. It occurs when genetically susceptible individuals are exposed to biotoxins from water-damaged buildings. About 25% of the population has genetic variants that make them unable to properly identify and eliminate these biotoxins.

**What Are Biotoxins?**

Water-damaged buildings don't just harbor mold. They create a toxic soup of:

• Mold spores and fragments: Even dead mold releases inflammatory particles
• Mycotoxins: Toxic compounds produced by mold (aflatoxins, ochratoxin, trichothecenes, etc.)
• Bacterial endotoxins: From bacteria that thrive in damp environments
• Actinomycetes: Bacteria that produce toxins similar to mold
• Beta-glucans: Inflammatory compounds from fungal cell walls
• VOCs (Volatile Organic Compounds): Gases released by mold and bacteria
• Hemolysins, proteinases, mannans: Additional inflammatory agents

**The Genetic Component**

CIRS is linked to HLA (Human Leukocyte Antigen) gene variations, specifically HLA-DR genes. These genes control immune response to biotoxins:

• 24% of population is "susceptible": Their immune system can't recognize and tag biotoxins for removal
• 2% are "highly susceptible": Even more impaired biotoxin clearance, worse outcomes
• Biotoxins accumulate: Without proper clearance, toxins recirculate, causing ongoing inflammation
• Can be tested: HLA-DR testing available but not required for diagnosis

**The CIRS Cascade: How It Progresses**

Dr. Shoemaker identified a specific sequence of events that occurs in CIRS:

1. Biotoxin exposure: Person with genetic susceptibility exposed to water-damaged building
2. Innate immune activation: Body recognizes threat, inflammatory cytokines released (TNF-alpha, IL-6, IL-1β, etc.)
3. Chronic inflammation: Because toxins aren't cleared, inflammation becomes chronic and systemic
4. Vascular effects: Inflammation affects blood vessels, reducing blood flow and oxygen delivery
5. Hormone dysregulation: MSH (melanocyte-stimulating hormone) drops, affecting multiple systems
6. Pituitary dysfunction: Affects ACTH, cortisol, ADH (antidiuretic hormone), sex hormones
7. MMP-9 elevation: Enzyme that breaks down tissue, causes blood-brain barrier permeability
8. Autoimmunity: Gliadin antibodies (even without gluten exposure), other autoantibodies develop
9. Coagulation problems: Clotting factors affected, blood flow further reduced
10. Multi-system dysfunction: Brain, lungs, muscles, gut, endocrine system all affected

**The PANDAS/PANS Connection**

Mold exposure is a significant trigger and perpetuating factor for PANDAS/PANS:

• Activates mast cells: Mold triggers mast cell degranulation, releasing histamine and inflammatory mediators
• Increases neuroinflammation: Biotoxins cross blood-brain barrier, inflame brain tissue
• Immune dysregulation: CIRS causes immune system dysfunction that worsens PANS
• Prevents healing: Children can't recover from PANS while in moldy environment
• Worsens all symptoms: OCD, anxiety, tics, rages all worse with mold exposure
• Common in PANS population: Many PANS children have undiagnosed mold exposure contributing to illness

**Why Is CIRS So Difficult to Diagnose?**

⚠️ Affects multiple systems: Symptoms so varied that patients see many specialists, no one connects dots
⚠️ Most doctors unfamiliar: CIRS not taught in medical school. Many doctors dismiss mold illness
⚠️ No single test: Diagnosis based on clinical picture, exposure history, lab patterns, response to treatment
⚠️ Controversial: Some conventional medicine doctors don't believe mold causes chronic illness
⚠️ Invisible exposure: Mold often hidden behind walls, under floors. You don't always see it
⚠️ Other family members fine: If they don't have genetic susceptibility, they may not be affected

**Diagnostic Approach**

Dr. Shoemaker developed specific criteria for CIRS diagnosis:

1. History of exposure: Water-damaged building (home, school, workplace)
2. Symptoms in multiple clusters: From defined symptom categories
3. Genetic susceptibility: HLA-DR testing shows susceptible genes (not required but supportive)
4. Abnormal lab tests: Specific biomarkers out of range (see below)
5. Visual Contrast Sensitivity (VCS) test: Screening test that's often abnormal in CIRS
6. Response to treatment: Improvement with proper CIRS treatment protocol

**CIRS Biomarkers (Lab Tests)**

Specific lab abnormalities seen in CIRS patients:

• MSH (Melanocyte-Stimulating Hormone): Usually LOW. Controls many hormones and sleep.
• MMP-9 (Matrix Metalloproteinase-9): Usually HIGH. Indicates inflammation, breaks down tissues.
• TGF-beta-1 (Transforming Growth Factor): Often HIGH. Inflammatory marker, linked to fibrosis.
• C4a (Complement Component 4a): Elevated in chronic inflammation from biotoxins.
• VEGF (Vascular Endothelial Growth Factor): Usually LOW. Affects blood flow, oxygen delivery.
• VIP (Vasoactive Intestinal Peptide): Usually LOW. Affects gut, breathing, immune regulation.
• Leptin: Often abnormal (high or low). Affects metabolism, satiety.
• ADH/Osmolality: Problems with water balance, thirst regulation.
• ACTH/Cortisol: Dysregulated stress hormone response.
• Anti-gliadin antibodies: Often positive even without celiac disease or gluten exposure.

**The Shoemaker Protocol**

Dr. Shoemaker developed a step-by-step treatment protocol:

1. Remove from exposure: MUST leave contaminated environment. Can't heal while still exposed.
2. Binders (cholestyramine or Welchol): Bind biotoxins in gut so they can be eliminated.
3. Treat MARCoNS if present: Antibiotic-resistant staph in sinuses produces toxins.
4. Correct hormones: Replace low MSH, correct ADH/cortisol issues.
5. Lower MMP-9: Omega-3s, low-amylose diet.
6. VIP nasal spray: Replaces low VIP, reduces inflammation.
7. Exercise rehabilitation: Carefully rebuild exercise tolerance.

**Note:** The Shoemaker Protocol should only be done under supervision of CIRS-trained physician. Steps must be done in order and monitored with labs.`,
      keyPoints: [
        'CIRS is a multi-system inflammatory condition triggered by biotoxin exposure',
        'About 25% of the population is genetically susceptible to mold illness',
        'Diagnosis requires exposure history, symptom clusters, lab abnormalities, and VCS testing',
        'Mold exposure is a significant trigger and perpetuating factor for PANDAS/PANS',
        'Treatment must begin with removal from the contaminated environment',
        'The Shoemaker Protocol is the gold standard treatment approach'
      ],
      prevalence: 'Estimated that 25% of population is genetically susceptible; millions affected',
      ageOfOnset: 'Can affect any age; children are particularly vulnerable'
    },
    symptoms: {
      overview: `**CIRS Symptoms: The "Great Pretender"**

CIRS affects virtually every body system because biotoxin-induced inflammation is systemic. Dr. Shoemaker identified 37 symptoms grouped into 13 symptom clusters. Having symptoms from 8+ clusters is suggestive of CIRS.`,
      primary: [
        {
          name: '🧠 Neurological/Cognitive',
          description: '',
          details: [
            'Memory problems (short-term memory especially)',
            'Brain fog / difficulty concentrating',
            'Confusion or disorientation',
            'Difficulty with word finding',
            'Decreased learning ability',
            'Difficulty with executive function (planning, organizing)',
            'Headaches (often severe)',
            'Tremors',
            'Numbness or tingling',
            'Vertigo or dizziness',
            'Light-headedness'
          ]
        },
        {
          name: '😴 Fatigue & Weakness',
          description: '',
          details: [
            'Extreme, debilitating fatigue',
            'Weakness (generalized)',
            'Muscle fatigue',
            'Post-exertional malaise (feel worse after activity)',
            'Need for excessive sleep',
            'Unrefreshing sleep',
            '"Wired but tired" feeling',
            'Energy crashes'
          ]
        },
        {
          name: '💢 Pain',
          description: '',
          details: [
            'Muscle aches and pain',
            'Joint pain (without swelling)',
            'Cramping',
            'Ice pick-like pain (sharp, stabbing)',
            'Unusual pain locations',
            'Chest pain or tightness',
            'Abdominal pain'
          ]
        }
      ],
      secondaryOverview: '**Additional Symptom Clusters:**',
      secondary: [
        {
          name: '🫁 Respiratory',
          description: '',
          details: [
            'Shortness of breath / air hunger',
            'Asthma or asthma-like symptoms',
            'Cough (chronic, often non-productive)',
            'Sinus problems (chronic sinusitis)',
            'Nasal congestion',
            'Post-nasal drip'
          ]
        },
        {
          name: '🤢 Gastrointestinal',
          description: '',
          details: [
            'Nausea',
            'Diarrhea',
            'Abdominal pain',
            'Bloating',
            'Changes in appetite',
            'Unexplained weight changes',
            'IBS-like symptoms',
            'Food sensitivities'
          ]
        },
        {
          name: '😢 Mood & Psychiatric',
          description: '',
          details: [
            'Anxiety',
            'Depression',
            'Mood swings',
            'Irritability',
            'Panic attacks',
            'Rage episodes (especially children)',
            'Emotional lability (crying easily)',
            'Feeling of impending doom'
          ]
        },
        {
          name: '🌙 Sleep Disturbances',
          description: '',
          details: [
            'Insomnia',
            'Non-restorative sleep',
            'Sleep disruption (waking frequently)',
            'Difficulty falling asleep',
            'Nightmares or vivid dreams',
            'Night sweats',
            'Sleep schedule disruption'
          ]
        },
        {
          name: '⚡ Sensitivity to Stimuli',
          description: '',
          details: [
            'Light sensitivity (photophobia)',
            'Blurred vision',
            'Tearing eyes',
            'Sound sensitivity',
            'Static shocks',
            'Sensitivity to smells',
            'Sensitivity to touch'
          ]
        },
        {
          name: '🌡️ Temperature Regulation',
          description: '',
          details: [
            'Difficulty regulating temperature',
            'Feeling too hot or too cold',
            'Night sweats',
            'Unusual sweating',
            'Cold extremities (hands, feet)'
          ]
        },
        {
          name: '💧 Thirst & Urination',
          description: '',
          details: [
            'Excessive thirst',
            'Frequent urination',
            'Static shocks (related to dehydration)',
            'Dehydration despite drinking water'
          ]
        },
        {
          name: '🔴 Skin',
          description: '',
          details: [
            'Rashes',
            'Skin sensitivity',
            'Unusual sweating',
            'Numbness or tingling (skin)',
            'Ice pick pains in skin'
          ]
        },
        {
          name: '👅 Metallic Taste',
          description: '',
          details: [
            'Metallic taste in mouth (distinctive symptom)',
            'Altered taste',
            'Bad taste that won\'t go away'
          ]
        },
        {
          name: '👶 Children-Specific Symptoms',
          description: '',
          details: [
            'PANDAS/PANS symptoms (OCD, tics, anxiety)',
            'Rage episodes / behavioral changes',
            'Decline in school performance',
            'Regression in development',
            'Bed-wetting (new onset or return)',
            'Growth problems',
            'Frequent infections',
            'Separation anxiety',
            'Sensory processing issues'
          ]
        }
      ],
      importantNotes: {
        title: 'Important Notes About Symptoms',
        notes: [
          'Symptoms often worsen with continued exposure',
          'May improve when away from contaminated building (vacation test)',
          'Symptoms can persist even after leaving exposure (biotoxins recirculate)',
          'Children often present with behavioral/psychiatric symptoms primarily',
          'Having 8+ symptom clusters is highly suggestive of CIRS',
          'Other family members may not be affected (genetic susceptibility required)',
          'Can be confused with chronic fatigue, fibromyalgia, Lyme, or psychiatric disorders'
        ]
      }
    },
    triggers: {
      tabLabel: 'Triggers/Exposure',
      isExtended: true,
      overview: {
        title: 'Sources of Mold & Water Damage',
        description: 'Any building with water intrusion or high humidity can harbor mold and other biotoxins. The key is identifying and eliminating the source.'
      },
      categories: [
        {
          name: 'Common Water Damage Sources',
          items: [
            'Roof leaks (current or past)',
            'Plumbing leaks (pipes, toilets, sinks)',
            'Window leaks',
            'Foundation cracks allowing water entry',
            'Flooding (even minor)',
            'HVAC condensation issues',
            'Washing machine leaks or overflow',
            'Dishwasher leaks',
            'Water heater leaks',
            'Ice maker line leaks',
            'Sprinkler system hitting house',
            'Poor drainage around foundation',
            'Gutters overflowing onto walls'
          ]
        },
        {
          name: 'High-Risk Locations in Buildings',
          items: [
            'Bathrooms (especially poorly ventilated)',
            'Basements',
            'Crawl spaces',
            'Attics',
            'Under sinks',
            'Around windows and doors',
            'Behind walls with plumbing',
            'Around HVAC systems',
            'Laundry rooms',
            'Kitchen (behind appliances)',
            'Closets on exterior walls',
            'Any area with prior water damage'
          ]
        },
        {
          name: 'Signs of Water Damage/Mold',
          items: [
            'Visible mold (any color - black, white, green, orange)',
            'Musty smell (even if you don\'t see mold)',
            'Water stains on ceilings, walls, floors',
            'Discoloration of surfaces',
            'Peeling paint or wallpaper',
            'Warped or buckled floors',
            'Soft spots in walls or floors',
            'Increased humidity (over 60%)',
            'Condensation on windows',
            'Rust on nails/screws in drywall',
            'Efflorescence (white powdery substance on concrete)'
          ]
        },
        {
          name: 'Conditions That Promote Mold',
          items: [
            'High humidity (>60%)',
            'Poor ventilation',
            'Insufficient air circulation',
            'Temperature between 40-100°F',
            'Moisture source (even high humidity is enough)',
            'Organic material (drywall, wood, paper, dust)',
            'Darkness (mold grows best without light)',
            'No air movement (corners, closets)'
          ]
        },
        {
          name: 'Hidden Mold Locations',
          items: [
            'Behind drywall',
            'Inside walls with prior leaks',
            'Under flooring (especially carpet)',
            'Behind wallpaper',
            'In HVAC ducts and systems',
            'Behind refrigerators',
            'Under vinyl flooring',
            'In ceiling tiles',
            'Behind shower tiles',
            'Inside mattresses and pillows',
            'In books and papers',
            'In stuffed furniture'
          ]
        },
        {
          name: 'Types of Buildings at Higher Risk',
          items: [
            'Older buildings (more likely to have had water events)',
            'Buildings in humid climates',
            'Coastal buildings (salt air, hurricanes)',
            'Buildings with flat roofs',
            'Buildings with poor construction',
            'Mobile homes / trailers',
            'Buildings that have been flooded',
            'Buildings with poor drainage',
            'Multi-family buildings (leaks from other units)',
            'Schools (often old, poorly maintained)'
          ]
        },
        {
          name: 'Non-Building Exposures',
          items: [
            'Cars with water leaks or flooding',
            'Boats',
            'RVs / campers',
            'Stored items (boxes in damp areas)',
            'Clothing/shoes stored in damp places',
            'Tents and camping gear'
          ]
        }
      ],
      criticalFacts: {
        title: 'Critical Facts About Mold',
        facts: [
          'Mold can start growing within 24-48 hours of water exposure',
          'You don\'t need to see mold for it to be there - can be hidden',
          '"Musty smell" = mold, even if you can\'t see it',
          'Dead mold is still toxic - spores and fragments cause inflammation',
          'Bleach doesn\'t kill mold on porous surfaces (drywall, wood)',
          'Professional assessment often needed - DIY tests unreliable',
          'Small visible mold often indicates much larger hidden problem',
          'Not everyone in house will react (genetic susceptibility required)'
        ]
      }
    },
    supplements: {
      isExtendedPandas: true,
      intro: 'These supplements support detoxification, reduce inflammation, and aid recovery. However, the most important "treatment" is removing from moldy environment. Supplements alone won\'t work if still exposed.',
      items: [
        {
          name: 'Binders (Essential)',
          dosage: 'Varies by type - take away from food and other supplements',
          evidence: 'Strong evidence',
          benefits: [
            'Bind mycotoxins in digestive tract so they can be eliminated',
            'Prevent reabsorption of toxins',
            'Reduce toxic burden on body',
            'Essential component of mold detox'
          ],
          note: 'Types: Activated charcoal, bentonite clay, zeolite, chlorella, modified citrus pectin. Cholestyramine (prescription) most effective for CIRS. Take 2 hours away from food, meds, supplements. Start low, increase slowly to avoid constipation. Drink plenty of water.'
        },
        {
          name: 'Glutathione',
          dosage: '500-1000 mg daily (liposomal form best)',
          evidence: 'Strong evidence',
          benefits: [
            'Master antioxidant and detoxifier',
            'Directly binds and eliminates mycotoxins',
            'Protects cells from oxidative damage',
            'Supports liver detoxification',
            'Often severely depleted in mold illness'
          ],
          note: 'Liposomal or IV glutathione most effective (oral often poorly absorbed). NAC (below) increases body\'s glutathione production. Some people feel worse initially (detox reaction).'
        },
        {
          name: 'N-Acetylcysteine (NAC)',
          dosage: '600-1800 mg daily',
          evidence: 'Strong evidence',
          benefits: [
            'Precursor to glutathione - increases body\'s production',
            'Powerful antioxidant',
            'Thins mucus (helpful for sinus issues)',
            'Protects liver',
            'Less expensive than glutathione supplements'
          ],
          note: 'Take on empty stomach for best absorption. Start with lower dose and increase gradually. Can cause nausea if dose too high. Very important for mold detox.'
        },
        {
          name: 'Milk Thistle (Silymarin)',
          dosage: '200-400 mg standardized extract, 2-3 times daily',
          evidence: 'Strong evidence',
          benefits: [
            'Protects and regenerates liver',
            'Liver processes and eliminates toxins',
            'Anti-inflammatory',
            'Antioxidant',
            'Supports detoxification pathways'
          ],
          note: 'Liver is primary detox organ - must support it. Standardized to 70-80% silymarin for best effect. Safe for long-term use.'
        },
        {
          name: 'Omega-3 Fatty Acids (High EPA)',
          dosage: '2000-4000 mg EPA+DHA daily',
          evidence: 'Strong evidence',
          benefits: [
            'Powerful anti-inflammatory',
            'Reduces MMP-9 (elevated in CIRS)',
            'Supports brain health',
            'Reduces neuroinflammation',
            'Supports cell membrane integrity'
          ],
          note: 'High-quality fish oil tested for purity and heavy metals. Higher EPA formulas better for inflammation. Take with food containing fat. Essential for CIRS treatment.'
        },
        {
          name: 'Vitamin C (High Dose)',
          dosage: '1000-3000 mg daily, divided doses',
          evidence: 'Strong evidence',
          benefits: [
            'Powerful antioxidant',
            'Supports immune function',
            'Helps detoxification',
            'Mast cell stabilizer',
            'Reduces inflammation'
          ],
          note: 'Buffered form (ascorbate) best. Split into multiple doses throughout day for better absorption. Can dose to bowel tolerance (loose stools = too much).'
        },
        {
          name: 'Methylated B-Complex',
          dosage: 'High-quality B-complex with methylated forms',
          evidence: 'Strong evidence',
          benefits: [
            'Essential for detoxification pathways',
            'Methylation support (critical for eliminating toxins)',
            'Energy production',
            'Nervous system support',
            'Often depleted in chronic illness'
          ],
          note: 'Must use methylated forms (methylfolate, methylcobalamin) - more bioavailable. Especially important if MTHFR gene variants. Take in morning (can be energizing).'
        },
        {
          name: 'Magnesium',
          dosage: '300-500 mg daily',
          evidence: 'Good evidence',
          benefits: [
            'Supports over 300 enzymatic processes including detoxification',
            'Mast cell stabilizer',
            'Calms nervous system',
            'Improves sleep',
            'Often deficient in mold illness'
          ],
          note: 'Magnesium glycinate best for absorption and calming. Take before bed. Essential for detox pathways to function.'
        },
        {
          name: 'Alpha-Lipoic Acid (ALA)',
          dosage: '300-600 mg daily',
          evidence: 'Moderate to strong evidence',
          benefits: [
            'Powerful antioxidant',
            'Both water and fat soluble (works everywhere)',
            'Chelates heavy metals (often found with mold)',
            'Regenerates other antioxidants (glutathione, vitamin C, E)',
            'Protects mitochondria'
          ],
          note: 'Take on empty stomach for best absorption. Can lower blood sugar - monitor if diabetic. R-lipoic acid more expensive but more effective.'
        },
        {
          name: 'Curcumin (Turmeric)',
          dosage: '500-1500 mg daily',
          evidence: 'Strong evidence',
          benefits: [
            'Potent anti-inflammatory',
            'Reduces neuroinflammation',
            'Antioxidant',
            'Supports liver detoxification',
            'Mast cell stabilizer'
          ],
          note: 'Must include piperine (black pepper) or use liposomal form for absorption. Take with fat. Very effective for reducing inflammation.'
        },
        {
          name: 'Quercetin',
          dosage: '500-1000 mg twice daily',
          evidence: 'Strong evidence',
          benefits: [
            'Mast cell stabilizer (mold activates mast cells)',
            'Natural antihistamine',
            'Anti-inflammatory',
            'Antioxidant',
            'Supports immune regulation'
          ],
          note: 'Take with vitamin C for synergy. Especially important if histamine issues or MCAS present. May take several weeks for full benefit.'
        },
        {
          name: 'Probiotics (Specific Strains)',
          dosage: 'Multi-strain, 50-100 billion CFU',
          evidence: 'Moderate evidence',
          benefits: [
            'Support gut health (often damaged by mold)',
            'Aid detoxification',
            'Immune modulation',
            'Reduce gut inflammation',
            'Some strains help eliminate mycotoxins'
          ],
          note: 'Choose strains that don\'t produce histamine (Bifidobacterium species, Lactobacillus rhamnosus, L. plantarum). Saccharomyces boulardii can help bind toxins. Take away from binders.'
        },
        {
          name: 'Vitamin D3',
          dosage: '4000-5000 IU daily (test levels, aim for 60-80 ng/mL)',
          evidence: 'Strong evidence',
          benefits: [
            'Immune regulation',
            'Anti-inflammatory',
            'Mast cell stabilizer',
            'Often severely deficient in mold illness',
            'Supports overall healing'
          ],
          note: 'Test levels every 3 months. Take with vitamin K2 and fat. Many mold patients need higher doses to reach therapeutic levels.'
        },
        {
          name: 'Zinc',
          dosage: '15-30 mg daily',
          evidence: 'Good evidence',
          benefits: [
            'Essential for immune function',
            'Supports detoxification enzymes',
            'Anti-inflammatory',
            'Often deficient in chronic illness',
            'Supports healing'
          ],
          note: 'Take with food. Use zinc picolinate or glycinate. Balance with copper (10:1 ratio). Test levels if possible.'
        },
        {
          name: 'CoQ10 (Ubiquinol form)',
          dosage: '200-400 mg daily',
          evidence: 'Moderate evidence',
          benefits: [
            'Supports mitochondria (energy production)',
            'Antioxidant',
            'Mold damages mitochondria - CoQ10 helps repair',
            'Reduces fatigue',
            'Protects heart'
          ],
          note: 'Ubiquinol form more bioavailable than ubiquinone. Take with fatty meal. Expensive but important for mitochondrial support.'
        }
      ],
      criticalGuidelines: {
        title: 'Critical Supplement Guidelines',
        items: [
          'MUST remove from mold first: Supplements won\'t work if still exposed',
          'Start one at a time: Wait 3-5 days before adding another',
          'Start LOW doses: Detox reactions common. Increase gradually.',
          'Binders away from everything: Take 2 hours from food, meds, other supplements',
          'Drink lots of water: Essential for elimination of toxins',
          'Be patient: Mold detox takes months to years, not weeks',
          'Work with knowledgeable practitioner: Especially for cholestyramine protocol',
          'Quality matters: Use third-party tested brands, especially for detox supps'
        ]
      }
    },
    lifestyle: {
      intro: 'The single most important "treatment" for mold illness is removing from exposure. Everything else is secondary. But comprehensive lifestyle changes support recovery and prevent re-exposure.',
      categories: [
        {
          name: '🏃‍♂️ Remove From Moldy Environment',
          priority: 'ABSOLUTELY CRITICAL',
          items: [
            'YOU CANNOT HEAL WHILE STILL EXPOSED: This is non-negotiable. Continuing to live/work in contaminated building prevents all recovery.',
            'Leave immediately if possible: Go stay with family, hotel, rental while you figure out next steps.',
            'Test your home: ERMI test (Environmental Relative Moldiness Index) or hire qualified mold inspector. Not all inspectors are equal - find one experienced with health effects.',
            'Remediation vs. moving: Professional remediation expensive and not always successful. Sometimes moving is only option.',
            'Remediation requirements: Must address source of water, remove ALL affected materials (not just surface clean), use HEPA filtration during work, verify with testing after.',
            'DIY remediation often fails: Mold spreads during cleaning if not done properly. Can make problem worse.',
            'Can\'t take belongings: Many items will be contaminated and must be left behind or professionally cleaned. This is devastating but necessary.',
            'Cross-contamination is real: Moving contaminated belongings to new place brings mold with you. Very careful process needed.',
            'School/workplace mold: If child\'s school or your workplace is source, must address with administration or find new school/job. Health first.',
            'Trust your body: If you feel better away from building and worse when you return, that\'s your answer. Don\'t wait for "proof."',
            'Financial reality: This often means financial hardship. Breaking leases, moving costs, lost deposits. Your health is worth it.'
          ]
        },
        {
          name: '🧳 Preventing Cross-Contamination',
          priority: 'CRITICAL',
          items: [
            'Spores stick to everything: Clothing, furniture, books, toys, electronics. All can harbor mold.',
            'Hard surfaces can be cleaned: Wipe down with Benefect or other mold-specific cleaner. Multiple times.',
            'Porous items often must go: Mattresses, pillows, upholstered furniture, stuffed animals, carpets usually can\'t be salvaged.',
            'Clothing in hot water/borax: Wash multiple times in hot water with borax. Some may need to be discarded.',
            'Books and papers difficult: Can try wiping covers, airing out. Some people freeze books to kill spores. Many must be discarded.',
            'Electronics complicated: Can wipe exteriors but spores get inside. May need professional cleaning or replacement.',
            'Don\'t bring cardboard boxes: Cardboard is mold food. Transfer to plastic bins.',
            'HEPA vacuum everything: Before packing. Multiple times. Regular vacuum spreads spores.',
            'Air out items: Sunlight and fresh air helpful. Ozone treatment controversial but some use it.',
            'Isolate questionable items: If unsure, store separately in shed/garage. See if you react to them.',
            'This is grief: Losing possessions, memories, home is traumatic. Allow yourself to grieve.'
          ]
        },
        {
          name: '🏠 Creating Safe Living Space',
          priority: 'CRITICAL',
          items: [
            'Choose new housing carefully: Inspect thoroughly before moving in. Look for any signs of water damage, past or present.',
            'Avoid basement apartments: Higher risk of moisture and mold.',
            'Check for leaks: Under sinks, around toilets, windows, roof. Fix immediately if found.',
            'Run dehumidifier: Keep humidity 30-50%. Below 50% mold can\'t grow. Essential in humid climates.',
            'HEPA air filters everywhere: Multiple units. Run 24/7. Change filters regularly. This is not optional.',
            'Hard floors only: No carpet. Carpet harbors mold, dust, allergens. Hard floors can be cleaned.',
            'Bathroom ventilation critical: Exhaust fan during and 30 min after shower. Keep door open after to dry.',
            'Fix leaks immediately: Within 24-48 hours of water intrusion to prevent mold growth.',
            'Regular inspection: Check all high-risk areas monthly. Under sinks, around windows, basement, attic.',
            'Don\'t ignore musty smells: If you smell must, there\'s mold somewhere. Find it and fix it.',
            'Minimal belongings initially: While healing, keep living space sparse. Fewer items = less surface area for potential contamination.'
          ]
        },
        {
          name: '💧 Supporting Detoxification',
          priority: 'HIGH',
          items: [
            'Drink lots of water: Half body weight in ounces minimum. Flushes toxins through kidneys.',
            'Sweat regularly: Sauna (if tolerated), hot baths (Epsom salt), exercise. Sweating eliminates mycotoxins through skin.',
            'Infrared sauna ideal: Mobilizes toxins. Start with 5-10 min, build to 20-30 min several times/week. Drink water before, during, after.',
            'Epsom salt baths: 2 cups Epsom salt in warm bath, 20-30 minutes. Helps detox, relaxes muscles, reduces inflammation.',
            'Support bowel movements: Must eliminate daily (multiple times ideal). Constipation = toxin reabsorption. Magnesium, vitamin C, fiber help.',
            'Dry brushing: Before shower, brush skin toward heart. Stimulates lymph flow (lymph carries toxins).',
            'Massage or lymphatic drainage: Helps move toxins through lymph system for elimination.',
            'Deep breathing: Lungs eliminate toxins. Deep breathing exercises several times daily.',
            'Avoid alcohol: Liver is overworked detoxing mold. Don\'t add burden of alcohol.',
            'Reduce toxic load elsewhere: Organic food when possible, chemical-free products, filtered water.'
          ]
        },
        {
          name: '🌙 Sleep Optimization',
          priority: 'HIGH',
          items: [
            '9-11 hours for children, 8-9 for adults: Sleep is when body repairs and detoxifies. Non-negotiable.',
            'Bedroom is sanctuary: Cleanest room in house. HEPA filter, hypoallergenic bedding, hard floors.',
            'New mattress and pillows: If came from contaminated environment, must replace. Can\'t clean them adequately.',
            'Allergen-proof covers: On all pillows and mattress. Barrier against dust and any potential mold.',
            'Wash bedding weekly: Hot water, fragrance-free detergent.',
            'Cool, dark room: 65-68°F, blackout curtains. Supports melatonin production.',
            'No electronics in bedroom: Blue light disrupts sleep. EMF sensitivity common in mold illness.',
            'Consistent schedule: Same bedtime/wake time. Body needs routine for optimal detox.',
            'Magnesium before bed: 300-500mg magnesium glycinate. Supports sleep and detox.',
            'Melatonin if needed: Helps sleep and is antioxidant. Start low (0.5-1mg).'
          ]
        },
        {
          name: '🧘 Stress Management',
          priority: 'HIGH',
          items: [
            'Mold illness is traumatic: Losing home, belongings, health, money. Need mental health support.',
            'Therapy essential: EMDR, CBT, or trauma-focused therapy. This is real trauma, not just stress.',
            'Support groups: Connect with others who\'ve been through it. Online groups helpful. Less isolating.',
            'Daily stress reduction: Meditation, deep breathing, yoga. Not optional - stress impairs detox.',
            'Set boundaries: Say no to obligations. Your only job right now is healing.',
            'Avoid stressful people/situations: When possible. Protect your nervous system.',
            'Journal: Process the grief, anger, fear. Writing helps.',
            'Lower expectations: You\'re dealing with serious illness. Survival mode is okay.',
            'Creative expression: Art, music, crafts. Non-verbal emotional release.',
            'Time in nature: If you have energy. Nature is healing and reduces cortisol.',
            'Address financial stress: Social worker, financial counselor if needed. Many mold patients face financial crisis.'
          ]
        },
        {
          name: '⚡ Pacing & Energy Management',
          priority: 'MODERATE-HIGH',
          items: [
            'Severe fatigue is hallmark: Mold illness causes debilitating exhaustion. Honor it.',
            'Rest before exhausted: Don\'t push through. Crashes set you back days or weeks.',
            'Spoon theory applies: Limited energy each day. Choose activities wisely.',
            'Break tasks into small pieces: Can\'t do everything at once. 15 min work, 15 min rest.',
            'Good days don\'t mean healed: Don\'t overdo on good days. Leads to crash.',
            'Schedule rest periods: Throughout day. Not optional, it\'s treatment.',
            'Ask for help: Accept offers. Use grocery delivery, meal delivery, cleaning services if possible.',
            'Reduce commitments: Work less, social obligations less, everything less. You\'re healing.',
            'Celebrate small wins: Getting through day is accomplishment with mold illness.',
            'Be patient with yourself: Recovery takes time. Months to years, not weeks.'
          ]
        },
        {
          name: '🏃 Exercise Modifications',
          priority: 'MODERATE',
          items: [
            'Exercise intolerance common: Mold damages mitochondria (energy producers). Body can\'t tolerate normal exercise.',
            'Start with gentle movement: 5-10 min walking if that\'s all you can do. Even that may be too much initially.',
            'Post-exertional malaise: Feeling worse after activity can last days. Sign you did too much.',
            'Build very gradually: Add 1-2 minutes when you can consistently do current amount without crashes.',
            'Listen to your body: If activity makes you feel worse, it\'s too much. Back off.',
            'Movement helps detox: But only if it doesn\'t cause crashes. Fine line.',
            'Infrared sauna instead: If can\'t exercise, sauna gets similar detox benefits without exertion.',
            'Gentle yoga or stretching: When ready. Restorative, not power yoga.',
            'Swimming if tolerated: Low-impact, full body. But chlorine may bother some.',
            'Don\'t compare to before: You\'re sick. Your exercise capacity will return as you heal.',
            'Exercise rehab takes time: May take many months to return to previous activity level.'
          ]
        },
        {
          name: '🛡️ Avoiding Re-Exposure',
          priority: 'HIGH',
          items: [
            'Once sensitized, more reactive: After mold illness, you\'re more sensitive to mold. Must be more careful.',
            'Avoid moldy buildings: Trust your senses. If place smells musty, leave.',
            'Be cautious with rentals: Many have hidden mold. Inspect thoroughly before signing lease.',
            'Hotels can be problematic: Water damage common. Ask for room that\'s never had leaks.',
            'Schools often have mold: Old buildings, poor maintenance. If child reacts to school, investigate.',
            'Workplace mold: May need to change jobs if workplace contaminated. Health over job.',
            'Friends\' homes: Some homes will bother you. It\'s okay to decline invitations or meet elsewhere.',
            'Basements and old buildings: Higher risk. Be cautious.',
            'After rain: Mold spore counts high. Stay indoors with HEPA filters running.',
            'Trust your body: If place makes you feel worse, believe it. Leave.',
            'This isolation is hard: But necessary for healing. Your real friends will understand.'
          ]
        },
        {
          name: '👥 Relationships & Social Life',
          priority: 'MODERATE',
          items: [
            'Many people won\'t understand: Mold illness not well-known. You\'ll be doubted, even by doctors.',
            'Educate close family/friends: Share resources. Dr. Shoemaker\'s site, documentaries (Moldy), support groups.',
            'Some relationships won\'t survive: Sad reality. People who don\'t believe you will leave. Let them.',
            'Find your people: Support groups online and in-person. Others who\'ve lived it get it.',
            'It\'s okay to be selective: You don\'t have energy for everyone. Choose people who support healing.',
            'Family strain is real: Moving, financial stress, chronic illness affects whole family. Family therapy may help.',
            'Children need extra support: They don\'t understand why life changed. Age-appropriate explanations, therapy.',
            'Social activities limited: Can\'t go many places due to mold risk or fatigue. This is temporary.',
            'Online connections: When can\'t leave house. Facebook groups, Instagram, Reddit.',
            'Rebuild slowly: As you heal, social life gradually returns. Be patient.',
            'Self-compassion essential: You\'re dealing with invisible illness in isolation. Be kind to yourself.'
          ]
        },
        {
          name: '💰 Financial Survival',
          priority: 'MODERATE-HIGH',
          items: [
            'Mold illness is expensive: Moving costs, new belongings, medical care, lost work. Devastating financially.',
            'Can\'t work often: Fatigue and cognitive issues prevent working. Apply for disability if needed.',
            'Health insurance often doesn\'t cover: Most CIRS treatment not covered. Supplements, testing expensive.',
            'Prioritize spending: Health first. Everything else second.',
            'GoFundMe not shameful: Many mold patients need crowdfunding help. Ask for help.',
            'Look for assistance programs: Utility assistance, food banks, housing assistance, medical bill assistance.',
            'Social worker can help: Navigate assistance programs, disability applications.',
            'Simplify life: Reduce expenses where possible. Minimalist living becomes necessity.',
            'Can\'t afford everything at once: Do what you can. Remove from mold first (most important). Add supplements gradually.',
            'This isn\'t forever: As you heal, work capacity typically returns. Financial recovery possible.'
          ]
        }
      ],
      hardTruth: {
        title: 'The Hard Truth About Mold Illness',
        items: [
          'Recovery requires leaving contaminated environment - there are no shortcuts',
          'You will likely lose possessions, possibly your home',
          'It\'s expensive, isolating, and often not believed by others',
          'Recovery takes months to years, not weeks',
          'But people do recover - with proper treatment and time, healing is possible',
          'Your health is worth more than any possession or place',
          'You\'re not crazy, you\'re not weak - you\'re dealing with real toxins affecting real biology',
          'Focus on what you CAN control: leaving mold, supporting detox, being patient'
        ]
      }
    },
    diet: {
      intro: 'Diet plays crucial role in mold recovery. Goals are: 1) Reduce inflammation, 2) Avoid mold in food (many foods contain mold), 3) Support detoxification, 4) Stabilize blood sugar, and 5) Starve mold (if colonization present).',
      foodsToEmphasize: {
        title: '✅ Foods to Emphasize (Clean, Anti-Inflammatory)',
        categories: [
          {
            name: 'Fresh Proteins',
            items: [
              'Organic, grass-fed meats: Beef, lamb, bison (if tolerated - some react during detox)',
              'Organic, pasture-raised poultry: Chicken, turkey',
              'Wild-caught fish: Salmon, cod, halibut, sardines (high in omega-3s, anti-inflammatory)',
              'Eggs: Pasture-raised if possible',
              'Bone broth: Homemade from organic bones. Healing for gut, supports detox'
            ],
            note: 'All must be FRESH. Nothing aged, cured, or smoked (mold concerns).'
          },
          {
            name: 'Vegetables (Abundance)',
            items: [
              'Cruciferous: Broccoli, cauliflower, Brussels sprouts, cabbage, kale (support detox pathways)',
              'Leafy greens: Lettuce, arugula, Swiss chard, spinach, collards',
              'Colorful veggies: Bell peppers, carrots, beets, sweet potatoes, squash, zucchini',
              'Sulfur-rich: Onions, garlic, leeks (support glutathione production - essential for detox)',
              'Others: Celery, cucumber, asparagus, green beans'
            ],
            note: 'Goal: 7-9 servings daily. Most powerful anti-inflammatory foods.'
          },
          {
            name: 'Healthy Fats (Essential)',
            items: [
              'Extra virgin olive oil: Anti-inflammatory, use liberally',
              'Coconut oil: Antimicrobial, supports thyroid',
              'Avocado and avocado oil: If tolerated (some have histamine issues)',
              'Grass-fed ghee or butter: If dairy tolerated',
              'Omega-3 fish: Salmon, sardines, mackerel'
            ]
          },
          {
            name: 'Low-Glycemic Fruits (Limited)',
            items: [
              'Berries: Blueberries, blackberries, raspberries (high antioxidants, lower sugar)',
              'Green apples: Lower sugar than other apples',
              'Lemon and lime: In water, great for detox'
            ],
            note: 'Limit to 1-2 servings daily. Sugar feeds yeast/mold overgrowth.'
          },
          {
            name: 'Gluten-Free Grains (If Tolerated)',
            items: [
              'Quinoa, rice (white and brown), millet, buckwheat'
            ],
            note: 'Many mold patients do better avoiding all grains initially. Test tolerance.'
          },
          {
            name: 'Herbs & Spices (Powerful)',
            items: [
              'Turmeric: Most powerful anti-inflammatory. Use daily.',
              'Ginger: Anti-inflammatory, digestive aid',
              'Garlic: Antimicrobial, immune support',
              'Oregano, thyme, rosemary, basil, cilantro, parsley: All anti-inflammatory and/or antimicrobial'
            ]
          }
        ]
      },
      foodsToAvoid: {
        title: '🔴 Foods to Avoid (Inflammatory or Moldy)',
        categories: [
          {
            name: 'High-Mold Foods',
            reason: 'Contain mold or mycotoxins. Adds to toxic burden.',
            items: [
              'Peanuts and peanut butter: Nearly always contain aflatoxin (highly toxic mycotoxin)',
              'Corn and corn products: High mold contamination',
              'Wheat and wheat products: Often contaminated with mold in storage',
              'All grains if moldy-smelling or past prime: Don\'t eat questionable grains',
              'Dried fruits: Mold grows during drying and storage',
              'Coffee (regular): High mycotoxin content. If must have coffee, buy mycotoxin-tested brands only.',
              'Alcoholic beverages: Made from fermented grains/grapes, contain mycotoxins',
              'Aged cheeses: Literally made with mold',
              'Mushrooms: They ARE fungi. Avoid during acute phase.',
              'Pistachios: High aflatoxin risk'
            ]
          },
          {
            name: 'Sugar & Refined Carbs',
            reason: 'Feed yeast and mold, cause inflammation, blood sugar spikes',
            items: [
              'White sugar, brown sugar, high fructose corn syrup',
              'Candy, cookies, cakes, pastries',
              'Soda and sugary drinks',
              'White bread, white pasta, white rice (in excess)',
              'Fruit juice (pure sugar without fiber)',
              'Artificial sweeteners (toxic, neurotoxic)'
            ]
          },
          {
            name: 'Dairy (Consider Eliminating)',
            reason: 'Inflammatory, mucus-producing, often contains antibiotics/hormones',
            items: [
              'Milk, cheese (especially aged cheese - mold!), ice cream, yogurt',
              'Exception: Grass-fed ghee or butter often tolerated',
              'Many mold patients do better completely dairy-free'
            ]
          },
          {
            name: 'Gluten',
            reason: 'Inflammatory, increases intestinal permeability (leaky gut), grains often moldy',
            items: [
              'Wheat, barley, rye, conventional oats',
              'Bread, pasta, baked goods (conventional)',
              'CIRS patients often develop anti-gliadin antibodies even without celiac'
            ]
          },
          {
            name: 'Processed Foods',
            reason: 'Inflammatory oils, additives, preservatives, lack nutrients',
            items: [
              'Fast food, fried foods',
              'Packaged snacks, frozen dinners',
              'Processed meats (hot dogs, deli meat, bacon, sausage)',
              'Anything with long ingredient list'
            ]
          },
          {
            name: 'Inflammatory Oils',
            reason: 'High omega-6, promote inflammation, oxidize easily',
            items: [
              'Vegetable oil, canola oil, soybean oil, corn oil',
              'Margarine, shortening',
              'Anything fried in these oils'
            ]
          }
        ]
      },
      sampleDay: {
        title: 'Sample Day of Mold-Recovery Eating',
        meals: [
          { name: 'Upon Waking', description: 'Large glass of water with lemon. Take binders (if using).' },
          { name: 'Breakfast (wait 30 min after binders)', description: 'Scrambled eggs with sautéed spinach, mushrooms (if tolerated), and onions cooked in coconut oil. Side of berries. Green tea or herbal tea.' },
          { name: 'Mid-Morning', description: 'Celery sticks with almond butter (if nuts tolerated), or apple slices' },
          { name: 'Lunch', description: 'Large salad with mixed greens, grilled chicken, colorful vegetables (bell peppers, carrots, cucumber), pumpkin seeds, olive oil and lemon dressing. Side of quinoa or sweet potato.' },
          { name: 'Afternoon', description: 'Carrot sticks with guacamole, or handful of berries' },
          { name: 'Dinner', description: 'Baked wild salmon with turmeric and garlic, roasted broccoli and cauliflower drizzled with olive oil, roasted sweet potato. Large side salad.' },
          { name: 'Evening', description: 'Herbal tea (chamomile, ginger). Supplements and magnesium.' }
        ]
      },
      tips: {
        title: 'Practical Diet Tips for Mold Recovery',
        items: [
          'Organic when possible: Reduce additional toxic burden from pesticides',
          'Focus on what you CAN eat: Don\'t obsess over restrictions',
          'Meal prep when able: Having safe food ready prevents poor choices when exhausted',
          'Hydration essential: Half body weight in ounces of water minimum',
          'Eat regularly: Don\'t skip meals. Blood sugar stability important.',
          'Chew thoroughly: Supports digestion, reduces gut burden',
          'This isn\'t forever: Very restrictive initially, but many can expand diet as they heal',
          'Individual tolerance varies: Track your responses, adjust accordingly',
          'Consider working with nutritionist: Familiar with mold illness',
          'Food is medicine: This diet is treatment, not deprivation'
        ]
      }
    }
  },

  'co-infections': {
    name: 'Co-Infections & PANDAS/PANS',
    color: 'bg-rose-500',
    lightColor: 'bg-rose-500/10',
    textColor: 'text-rose-600',
    description: 'Tick-borne co-infections—particularly Lyme disease and Bartonella—are major triggers and perpetuating factors in PANDAS/PANS',
    about: {
      overview: `**Co-Infections & PANDAS/PANS**

Co-infections are infections that occur alongside or are transmitted with a primary infection. In PANDAS/PANS, tick-borne co-infections—particularly Lyme disease (Borrelia) and Bartonella—are increasingly recognized as major triggers and perpetuating factors. These infections cause chronic inflammation, immune dysregulation, and can directly affect the brain, making PANS symptoms worse or preventing recovery.

**Why Co-Infections Matter in PANDAS/PANS**

• Common in PANS population: Many children with PANS have undiagnosed tick-borne infections
• Prevent healing: Children won't fully recover from PANS while co-infections active
• Cause chronic inflammation: Ongoing infection = ongoing immune activation = persistent symptoms
• Cross blood-brain barrier: Both Lyme and Bartonella can infect brain tissue directly
• Immune dysregulation: These bacteria manipulate immune system, contributing to autoimmunity
• Often missed by conventional testing: Standard tests frequently false negative

**Lyme Disease (Borrelia burgdorferi and other species)**

*What is Lyme Disease?*
Lyme disease is caused by Borrelia bacteria (B. burgdorferi in US, other species in Europe/Asia), transmitted primarily by black-legged (deer) ticks. It's the most common tick-borne illness in the Northern Hemisphere.

*How Lyme Causes Problems*
• Spirochete bacteria: Corkscrew-shaped, can burrow into tissues including brain, joints, heart
• Great imitator: Mimics many conditions - MS, fibromyalgia, chronic fatigue, psychiatric disorders
• Biofilm formation: Bacteria hide in protective biofilms, evading antibiotics and immune system
• Multiple forms: Spirochete, cell-wall-deficient (L-form), cyst form - change forms to survive
• Immune evasion: Suppresses immune response, hides inside cells, constantly changes surface proteins
• Neurotoxins: Produces toxins that damage nerves and brain
• Crosses blood-brain barrier: Can directly infect central nervous system (neuroborreliosis)

*Stages of Lyme Disease*
1. Early Localized (Days to weeks after bite): Bull's-eye rash (erythema migrans) in 70-80% of cases, flu-like symptoms, fatigue. Often missed if no rash or rash not noticed.
2. Early Disseminated (Weeks to months): Bacteria spread through body. Multiple rashes, joint pain, neurological symptoms (facial palsy, meningitis), heart problems (Lyme carditis), severe fatigue.
3. Late/Chronic Lyme (Months to years): Persistent infection despite treatment or untreated disease. Arthritis (especially knees), chronic neurological problems, cognitive dysfunction, chronic fatigue, pain. This is when most PANS children are diagnosed.

*Chronic Lyme / Post-Treatment Lyme Disease Syndrome (PTLDS)*
Controversy exists in medicine about chronic Lyme. Conventional medicine recognizes PTLDS (persistent symptoms after treatment) but attributes it to immune system damage, not active infection. Lyme-literate doctors (ILADS - International Lyme and Associated Diseases Society) recognize chronic/persistent Lyme infection requiring longer treatment.

**Bartonella (Multiple Species)**

*What is Bartonella?*
Bartonella is a genus of bacteria with multiple species that infect humans. B. henselae (cat scratch disease) most known, but B. quintana, B. elizabethae, B. vinsonii, and others also cause illness. Transmitted by ticks, fleas, lice, and cat scratches/bites.

*How Bartonella Causes Problems*
• Infects blood vessels: Lives inside endothelial cells (blood vessel lining), causing vascular inflammation
• Neuropsychiatric symptoms prominent: Strong association with rage, anxiety, OCD, mood swings
• The "rage bacteria": Bartonella particularly associated with severe behavioral outbursts in children
• Immune manipulation: Suppresses immune response, triggers inflammatory cytokines
• Intracellular: Hides inside cells, protected from antibiotics and immune system
• Affects brain directly: Can infect brain tissue, particularly associated with neuroinflammation
• Very difficult to treat: Intracellular location makes it hard to eradicate

*Routes of Transmission*
• Tick bites: Co-transmitted with Lyme (ticks often carry multiple infections)
• Cat scratches or bites: Cats are reservoir for B. henselae. Kittens especially.
• Fleas: Cat fleas can transmit between cats and potentially to humans
• Blood transfusions: Can be transmitted through blood (concerning for blood supply)
• Vertical transmission possible: Mother to child during pregnancy (debated but evidence exists)

**Other Common Tick-Borne Co-Infections**

• Babesia (protozoan parasite): Malaria-like illness. Infects red blood cells. Air hunger, night sweats, drenching sweats, temperature dysregulation. Very difficult to treat. Multiple species.
• Ehrlichia and Anaplasma (bacteria): Infect white blood cells. Acute illness can be severe. Chronic infection less understood but causes fatigue, headaches, joint pain.
• Mycoplasma pneumoniae (not tick-borne but common co-infection): Covered in separate section, but worth noting frequently found with Lyme and other tick infections.
• Rickettsia (Rocky Mountain Spotted Fever and others): Infect endothelial cells. Can be severe/fatal if untreated. Characteristic rash, fever, severe headache.

**The Connection to PANDAS/PANS**

*How Co-Infections Trigger PANS*
• Chronic immune activation: Persistent infection = persistent inflammation = neuroinflammation
• Molecular mimicry: Bacterial proteins similar to human proteins. Antibodies attack both.
• Direct brain infection: Both Lyme and Bartonella can cross blood-brain barrier
• Cytokine storm: Immune response to infection causes excessive inflammatory cytokine production
• Blood-brain barrier disruption: Inflammation damages barrier, allowing more inflammation into brain
• Neurotransmitter disruption: Infections affect production and function of dopamine, serotonin, etc.

*Clinical Overlap*
Many symptoms overlap between PANS and tick-borne infections, making it difficult to know which is causing what:
• Anxiety, OCD, depression, mood swings, rage
• Cognitive dysfunction (brain fog, memory problems)
• Sleep disturbances
• Sensory sensitivities
• Behavioral regression
• Tics and movement disorders

**Why Testing is Difficult**

⚠️ Standard testing inadequate: CDC two-tier testing for Lyme misses 50%+ of cases, especially chronic
⚠️ Immune suppression: These bacteria suppress immune response, so antibody tests often negative
⚠️ Intracellular hiding: Bacteria inside cells, not in bloodstream where tests look
⚠️ Clinical diagnosis important: Lyme-literate doctors diagnose based on history, symptoms, response to treatment
⚠️ Bartonella especially hard to detect: Standard cat scratch disease test only detects B. henselae, misses other species

**Treatment Challenges**

⚠️ Long-term antibiotics often needed: Months to years, not the standard 2-4 weeks
⚠️ Multiple antibiotics required: Different antibiotics target different bacterial forms and co-infections
⚠️ Herxheimer reactions: Feeling worse when bacteria die off (die-off toxins). Can be severe.
⚠️ Controversy in medicine: IDSA (Infectious Disease Society) vs ILADS (Lyme literate doctors) guidelines conflict
⚠️ Many doctors won't treat: Chronic Lyme not accepted by all doctors. Finding treatment can be difficult.
⚠️ Insurance often doesn't cover: Long-term antibiotic treatment, alternative therapies often not covered`,
      keyPoints: [
        'Tick-borne co-infections are major triggers and perpetuating factors in PANDAS/PANS',
        'Lyme disease and Bartonella can directly infect brain tissue',
        'Standard testing frequently misses these infections (high false negative rate)',
        'Children won\'t fully recover from PANS while co-infections are active',
        'Clinical diagnosis by Lyme-literate doctors is often more reliable than lab tests',
        'Treatment typically requires long-term, multi-antibiotic approaches'
      ],
      prevalence: 'Many PANS children have undiagnosed tick-borne infections; Lyme is the most common tick-borne illness in the Northern Hemisphere',
      ageOfOnset: 'Can affect any age; children are particularly vulnerable to neuropsychiatric manifestations'
    },
    symptoms: {
      overview: `**Symptoms of Tick-Borne Co-Infections**

Symptoms are highly variable and often overlap between different infections and with PANS itself. Having many symptoms from multiple categories increases suspicion for co-infections.`,
      primary: [
        {
          name: 'Lyme Disease - Neurological/Cognitive',
          description: '',
          details: [
            'Brain fog (severe difficulty thinking)',
            'Memory problems (short-term especially)',
            'Difficulty with word finding',
            'Confusion or disorientation',
            'Headaches (often severe, migraine-like)',
            'Facial palsy (Bell\'s palsy)',
            'Numbness or tingling (especially hands/feet)',
            'Tremors',
            'Light and sound sensitivity',
            'Vertigo or dizziness',
            'Difficulty reading',
            'Processing speed decreased'
          ]
        },
        {
          name: 'Lyme Disease - Neuropsychiatric',
          description: '',
          details: [
            'Anxiety and panic attacks',
            'Depression',
            'Mood swings',
            'Irritability',
            'OCD symptoms',
            'Personality changes',
            'Behavioral problems in children',
            'Psychosis (in severe cases)'
          ]
        },
        {
          name: 'Lyme Disease - Musculoskeletal',
          description: '',
          details: [
            'Joint pain (especially knees, often migratory)',
            'Joint swelling',
            'Muscle pain and aches',
            'Neck stiffness',
            'Back pain',
            'Muscle twitching',
            'TMJ pain'
          ]
        }
      ],
      secondaryOverview: '**Additional Lyme Symptoms & Bartonella Symptoms:**',
      secondary: [
        {
          name: 'Lyme - Fatigue & Sleep',
          description: '',
          details: [
            'Extreme, debilitating fatigue',
            'Unrefreshing sleep',
            'Insomnia',
            'Excessive need for sleep',
            'Night sweats'
          ]
        },
        {
          name: 'Lyme - Cardiac',
          description: '',
          details: [
            'Heart palpitations',
            'Chest pain',
            'Lyme carditis (heart block - can be serious)',
            'Shortness of breath'
          ]
        },
        {
          name: 'Lyme - Other',
          description: '',
          details: [
            'Flu-like symptoms that come and go',
            'Fevers or chills',
            'Swollen lymph nodes',
            'Sore throat',
            'Vision changes or eye pain',
            'Hearing loss or ringing in ears',
            'Bull\'s-eye rash (erythema migrans) - but only 70-80% get it'
          ]
        },
        {
          name: 'Bartonella - Neuropsychiatric (PROMINENT)',
          description: '',
          details: [
            'Rage episodes (explosive, out-of-character anger)',
            'Severe anxiety',
            'OCD (intrusive thoughts, compulsions)',
            'Panic attacks',
            'Depression',
            'Mood swings (rapid, extreme)',
            'Agitation and restlessness',
            'Sleep disturbances',
            'Nightmares',
            'Paranoia',
            'Hallucinations (auditory or visual)',
            'Oppositional behavior in children'
          ]
        },
        {
          name: 'Bartonella - Neurological',
          description: '',
          details: [
            'Brain fog',
            'Memory problems',
            'Headaches (ice pick-like, sharp stabbing pain)',
            'Migraines',
            'Tremors',
            'Numbness or tingling (especially soles of feet)',
            'Burning sensations (feet especially)',
            'Seizures (in severe cases)'
          ]
        },
        {
          name: 'Bartonella - Pain (Characteristic)',
          description: '',
          details: [
            'Sole of feet pain (very characteristic of Bartonella)',
            'Shin bone pain',
            'Rib pain',
            'Abdominal pain',
            'Muscle pain',
            'Joint pain',
            'Pain that moves around'
          ]
        },
        {
          name: 'Bartonella - Skin',
          description: '',
          details: [
            'Stretch marks (striae) - unusual locations or rapid appearance',
            'Rashes (various types)',
            'Papules on hands and feet',
            'Skin sensitivity',
            'Easy bruising'
          ]
        },
        {
          name: 'Bartonella - Vascular/Other',
          description: '',
          details: [
            'Swollen lymph nodes',
            'Fever (often low-grade, comes and goes)',
            'Fatigue',
            'Night sweats',
            'Temperature dysregulation',
            'Eye problems (blurred vision, floaters)',
            'Enlarged spleen or liver (in some cases)',
            'Bone pain'
          ]
        }
      ],
      importantNotes: {
        title: 'Red Flags for Co-Infections in PANS Children',
        notes: [
          'Not responding to standard PANS treatment',
          'History of tick bite or exposure to ticks',
          'Severe, explosive rage episodes (Bartonella signature)',
          'Joint pain or swelling',
          'Severe headaches',
          'Cognitive decline more severe than typical PANS',
          'Night sweats',
          'Foot pain (sole of feet - Bartonella)',
          'New onset stretch marks',
          'Living in or visiting Lyme-endemic area',
          'Exposure to cats (especially kittens)'
        ]
      }
    },
    triggers: {
      tabLabel: 'Testing',
      isExtended: true,
      overview: {
        title: 'Testing for Co-Infections',
        description: 'Testing for tick-borne infections is notoriously unreliable. Clinical diagnosis (based on symptoms, history, and response to treatment) is often more important than lab results.'
      },
      categories: [
        {
          name: 'Why Testing is Problematic',
          items: [
            'High false negative rate: 50%+ of chronic Lyme cases test negative on standard tests',
            'Immune suppression: Bacteria suppress immune system so antibody production inadequate',
            'Tests look for antibodies, not bacteria: If no antibodies made, test negative despite active infection',
            'Timing matters: Antibodies take weeks to develop, may wane over time',
            'Intracellular bacteria: Hiding inside cells, not in blood where tests look',
            'Different labs, different results: Lab quality and methods vary significantly'
          ]
        },
        {
          name: 'Lyme Disease - Standard CDC Two-Tier Testing (Inadequate)',
          description: 'ELISA followed by Western Blot',
          items: [
            'First test: ELISA screening. If negative, often stop here (mistake).',
            'If ELISA positive: Western Blot for confirmation',
            'Problem: ELISA misses many cases. CDC criteria too strict. Designed for surveillance, not diagnosis.',
            'Pros: Covered by insurance, available everywhere',
            'Cons: High false negative rate, especially chronic Lyme'
          ]
        },
        {
          name: 'Lyme Disease - Specialty Lab Testing (Better but Expensive)',
          description: 'IGeneX, DNA Connexions, Armin Labs, etc.',
          items: [
            'More sensitive tests than standard labs',
            'Test for more species of Borrelia (not just B. burgdorferi)',
            'Report more bands on Western Blot',
            'Pros: Higher sensitivity, Lyme-literate interpretation',
            'Cons: Expensive ($200-500+), usually not covered by insurance'
          ]
        },
        {
          name: 'Lyme Disease - PCR (DNA Testing)',
          items: [
            'Looks for bacterial DNA directly, not antibodies',
            'Pros: Doesn\'t rely on immune response',
            'Cons: Must catch bacteria in blood (intermittent shedding), expensive, not widely available'
          ]
        },
        {
          name: 'Lyme Disease - CD57 Natural Killer Cells',
          items: [
            'Measures immune cell count that\'s often low in chronic Lyme',
            'Not specific to Lyme, but supportive data',
            'Used to monitor treatment progress'
          ]
        },
        {
          name: 'Bartonella - Serology (Antibody Tests)',
          items: [
            'IFA (Immunofluorescence Assay): Most common, but only tests for B. henselae',
            'Problem: Doesn\'t test for other Bartonella species that cause illness',
            'False negative rate very high: Even higher than Lyme',
            'Many clinically obvious Bartonella cases test negative'
          ]
        },
        {
          name: 'Bartonella - PCR Testing',
          items: [
            'DNA testing for multiple Bartonella species',
            'Galaxy Diagnostics has specialized Bartonella ePCR',
            'Better than serology but still many false negatives',
            'Very expensive'
          ]
        },
        {
          name: 'Bartonella - Clinical Diagnosis Most Important',
          items: [
            'Bartonella particularly difficult to test for',
            'Experienced Lyme doctors diagnose based on symptoms',
            '"Bartonella triad": Rage, sole of feet pain, stretch marks',
            'Response to treatment confirms diagnosis'
          ]
        },
        {
          name: 'Recommended Testing Approach',
          items: [
            'Start with clinical evaluation by Lyme-literate doctor (ILADS trained)',
            'Consider specialty lab testing (IGeneX, DNA Connexions) if affordable',
            'Don\'t rule out co-infections based on negative tests',
            'Clinical picture + response to treatment = diagnosis',
            'Test co-infections too: Babesia, Ehrlichia, Anaplasma, Mycoplasma',
            'Remember: Negative test does NOT rule out infection'
          ]
        },
        {
          name: 'Finding a Lyme-Literate Doctor',
          items: [
            'ILADS (ilads.org): Physician referral service for Lyme-literate doctors',
            'Local support groups: Facebook groups, LymeDisease.org forums',
            'Ask other PANS parents: Many PANS doctors also treat Lyme',
            'Be prepared: Often long wait lists, may not take insurance',
            'Avoid doctors who say "chronic Lyme doesn\'t exist"'
          ]
        }
      ]
    },
    supplements: {
      isExtendedPandas: true,
      intro: 'Supplements support immune function, reduce inflammation, and aid recovery. However, antibiotics (or sometimes herbal antimicrobials) are typically required to treat active infections. Supplements are adjunctive, not replacement for treatment.',
      items: [
        {
          name: 'Vitamin D3',
          dosage: '4000-5000 IU daily (test levels, aim for 60-80 ng/mL)',
          evidence: 'Strong evidence',
          benefits: [
            'Essential for immune function',
            'Deficiency common in Lyme disease',
            'Reduces inflammation',
            'Supports recovery',
            'Antibacterial properties',
            'Most Lyme patients severely deficient'
          ],
          note: 'Test levels regularly. Many Lyme patients need high doses (5000-10,000 IU) to reach therapeutic levels. Take with K2 and fat. Critical supplement for Lyme treatment.'
        },
        {
          name: 'Omega-3 Fatty Acids (High EPA)',
          dosage: '2000-4000 mg EPA+DHA daily',
          evidence: 'Strong evidence',
          benefits: [
            'Powerful anti-inflammatory',
            'Reduces neuroinflammation',
            'Supports brain healing',
            'Reduces joint inflammation',
            'Supports cell membrane integrity'
          ],
          note: 'High-quality, tested for purity. Higher doses often needed in chronic infection. Take with meals.'
        },
        {
          name: 'Probiotics (Multi-Strain)',
          dosage: '50-100 billion CFU daily',
          evidence: 'Strong evidence',
          benefits: [
            'Essential during/after antibiotics',
            'Prevents C. diff and other infections',
            'Supports immune function (70% of immune system in gut)',
            'Reduces gut inflammation',
            'Helps detoxification'
          ],
          note: 'Take 2 hours away from antibiotics. Use multi-strain. Include Saccharomyces boulardii (can take WITH antibiotics). Continue months after antibiotics stop. Critical for gut health during treatment.'
        },
        {
          name: 'B-Complex (Methylated)',
          dosage: 'High-potency B-complex with methylated forms',
          evidence: 'Strong evidence',
          benefits: [
            'Energy production (severe fatigue in Lyme)',
            'Nervous system support',
            'Detoxification support',
            'Methylation pathways',
            'Cognitive function',
            'Often depleted in chronic illness'
          ],
          note: 'Must use methylated forms (methylfolate, methylcobalamin). Some need additional B12 injections for neurological symptoms. Take in morning.'
        },
        {
          name: 'Magnesium',
          dosage: '400-600 mg daily',
          evidence: 'Good evidence',
          benefits: [
            'Muscle pain and cramping relief',
            'Nervous system support',
            'Sleep improvement',
            'Energy production',
            'Reduces anxiety',
            'Often deficient'
          ],
          note: 'Magnesium glycinate best absorbed, calming. Take before bed. Important for muscle and nerve symptoms.'
        },
        {
          name: 'Coenzyme Q10 (Ubiquinol)',
          dosage: '200-400 mg daily',
          evidence: 'Moderate to strong evidence',
          benefits: [
            'Mitochondrial support (Lyme damages mitochondria)',
            'Energy production',
            'Reduces fatigue',
            'Antioxidant',
            'Heart health (important if Lyme carditis risk)'
          ],
          note: 'Ubiquinol form more bioavailable. Expensive but important for energy. Take with fatty meal.'
        },
        {
          name: 'Alpha-Lipoic Acid',
          dosage: '300-600 mg daily',
          evidence: 'Moderate evidence',
          benefits: [
            'Antioxidant (water and fat soluble)',
            'Neuroprotective (important for neurological Lyme)',
            'Supports detoxification',
            'Reduces neuroinflammation',
            'Helps neuropathy symptoms'
          ],
          note: 'R-lipoic acid more expensive but more effective. Take on empty stomach. Particularly helpful for neurological symptoms.'
        },
        {
          name: 'Curcumin',
          dosage: '500-1500 mg daily',
          evidence: 'Strong evidence',
          benefits: [
            'Potent anti-inflammatory',
            'Reduces neuroinflammation',
            'Antibacterial properties',
            'Crosses blood-brain barrier',
            'Reduces joint inflammation'
          ],
          note: 'Must include piperine or use liposomal form for absorption. Take with fat. Very effective for inflammation.'
        },
        {
          name: 'Cat\'s Claw (Uncaria tomentosa)',
          dosage: '500-1000 mg three times daily',
          evidence: 'Moderate evidence (traditional use + some studies)',
          benefits: [
            'Immune support',
            'Anti-inflammatory',
            'Antimicrobial properties against Borrelia',
            'Reduces Lyme symptoms in studies',
            'Neuroprotective'
          ],
          note: 'TOA-free form (Samento) preferred by some Lyme doctors. Part of Cowden protocol and Buhner protocol. Herbal antimicrobial.'
        },
        {
          name: 'Japanese Knotweed (Resveratrol source)',
          dosage: '500-1000 mg three to four times daily',
          evidence: 'Moderate evidence',
          benefits: [
            'Anti-inflammatory',
            'Crosses blood-brain barrier',
            'Reduces neuroinflammation',
            'Protects against Lyme neurotoxins',
            'Supports circulation',
            'Antioxidant'
          ],
          note: 'Key herb in Buhner protocol for Lyme. High in resveratrol. Particularly good for neurological symptoms.'
        },
        {
          name: 'NAC (N-Acetylcysteine)',
          dosage: '600-1200 mg twice daily',
          evidence: 'Good evidence',
          benefits: [
            'Powerful antioxidant',
            'Increases glutathione production',
            'Breaks up biofilms (where bacteria hide)',
            'Supports detoxification',
            'Reduces brain inflammation',
            'May help OCD symptoms'
          ],
          note: 'Take on empty stomach. Start low, increase gradually. Important for breaking biofilms. Part of many Lyme protocols.'
        },
        {
          name: 'Glutathione (Liposomal or IV)',
          dosage: '500-1000 mg daily (liposomal)',
          evidence: 'Strong evidence',
          benefits: [
            'Master antioxidant and detoxifier',
            'Supports liver detoxification',
            'Reduces oxidative stress',
            'Neuroprotective',
            'Often severely depleted in Lyme'
          ],
          note: 'Liposomal form or IV most effective. Oral capsules poorly absorbed. IV glutathione can provide dramatic symptom relief for some. Expensive.'
        },
        {
          name: 'Milk Thistle',
          dosage: '300 mg twice daily (standardized extract)',
          evidence: 'Strong evidence',
          benefits: [
            'Liver protection and regeneration',
            'Essential during long-term antibiotics',
            'Supports detoxification',
            'Anti-inflammatory'
          ],
          note: 'Standardized to 70-80% silymarin. Liver works hard during infection and treatment - must support it.'
        },
        {
          name: 'Zinc',
          dosage: '15-30 mg daily',
          evidence: 'Good evidence',
          benefits: [
            'Essential for immune function',
            'Antibacterial properties',
            'Often deficient in chronic infection',
            'Supports healing',
            'Reduces inflammation'
          ],
          note: 'Take with food. Balance with copper. Zinc picolinate or glycinate. Many Lyme patients deficient.'
        },
        {
          name: 'Binders (for Herxheimer Reactions)',
          dosage: 'Varies by type',
          evidence: 'Moderate evidence (clinical use)',
          benefits: [
            'Bind bacterial die-off toxins',
            'Reduce Herxheimer reaction severity',
            'Support detoxification',
            'Reduce brain fog during treatment'
          ],
          note: 'Types: Activated charcoal, bentonite clay, zeolite, chlorella. Take 2 hours away from antibiotics and supplements. Use as needed during bad Herx reactions. Drink plenty of water.'
        }
      ],
      criticalGuidelines: {
        title: 'Important Guidelines',
        items: [
          'Antibiotics typically required: Supplements support but don\'t replace antibiotic treatment for active infection',
          'Work with Lyme-literate doctor: For comprehensive treatment plan',
          'Start low, go slow: Herx reactions can be severe. Add supplements gradually.',
          'Core supplements: Vitamin D, Omega-3, Probiotics, B-complex essential for everyone',
          'Quality matters: Use pharmaceutical-grade, third-party tested supplements',
          'Herbal protocols exist: Cowden, Buhner protocols use herbs as antimicrobials. Discuss with doctor.',
          'Be patient: Treatment takes months to years. Slow progress is still progress.'
        ]
      }
    },
    lifestyle: {
      intro: 'Living with and treating tick-borne infections requires comprehensive lifestyle adjustments to support recovery, manage symptoms, and prevent reinfection.',
      categories: [
        {
          name: '🛡️ Tick Bite Prevention',
          priority: 'CRITICAL',
          items: [
            'Avoid tick habitats: Wooded areas, tall grass, leaf litter especially May-September when ticks most active.',
            'Walk center of trails: Ticks can\'t jump or fly - they wait on vegetation to brush against you.',
            'Wear protective clothing: Long sleeves, long pants tucked into socks, light colors (easier to spot ticks).',
            'Use repellents: DEET on skin (20-30%), permethrin on clothing (lasts through washings). Natural alternatives: lemon eucalyptus oil.',
            'Check for ticks daily: Full body check after outdoor activities. Ticks prefer warm, moist areas: hairline, behind ears, armpits, groin, behind knees.',
            'Shower within 2 hours: After being outdoors. Helps wash off unattached ticks.',
            'Dry clothes on high heat: 10 minutes kills ticks. Washing may not.',
            'Keep yard tick-free: Mow grass short, remove leaf litter, create wood chip barrier between lawn and woods, consider tick tubes.',
            'Check pets daily: Dogs and cats can bring ticks inside. Use tick prevention products on pets.',
            'Remove ticks properly: Fine-tipped tweezers, grasp close to skin, pull straight up. Don\'t twist. Save tick in ziplock for identification.',
            'When to see doctor: If tick attached 36+ hours, if develop rash or flu symptoms within 30 days. Consider prophylactic doxycycline.'
          ]
        },
        {
          name: '😴 Rest & Energy Management',
          priority: 'CRITICAL',
          items: [
            'Extreme fatigue is hallmark: Lyme causes profound, debilitating exhaustion. This is not laziness - it\'s illness.',
            'Rest is treatment: Body needs energy to fight infection and heal. Resting isn\'t giving up, it\'s healing.',
            'Spoon theory applies: Limited energy each day. Choose activities wisely.',
            'Pacing essential: Don\'t push through fatigue. Leads to crashes that set back recovery weeks.',
            'Post-exertional malaise: Feeling worse after activity. Sign you did too much. Back off.',
            'Schedule rest periods: Throughout day, not just at night. 15-20 min rest breaks.',
            'Good days don\'t mean healed: Don\'t overdo on good days. Leads to payback.',
            'Reduce commitments: Work less, social activities less, say no to non-essentials.',
            'Ask for help: With household tasks, childcare, errands. You can\'t do it all.',
            'Disability may be needed: If unable to work. Don\'t feel guilty. You\'re sick.',
            'Recovery takes time: Months to years. Some improvement, then plateau, then more improvement. Not linear.'
          ]
        },
        {
          name: '🌙 Sleep Optimization',
          priority: 'CRITICAL',
          items: [
            'Sleep problems common: Insomnia, unrefreshing sleep, night sweats disrupt sleep in Lyme/Bartonella.',
            '9-11 hours for children, 8-10 for adults: Need more sleep than healthy people. Body healing during sleep.',
            'Consistent schedule: Same bedtime/wake time every day. Circadian rhythm important.',
            'Cool, dark room: 65-68°F, blackout curtains, no light sources.',
            'Address night sweats: Keep cool, moisture-wicking sheets, towel on pillow. May improve with treatment.',
            'Magnesium before bed: 400-600mg magnesium glycinate. Calms nervous system.',
            'Melatonin if needed: 0.5-5mg. Start low. Helpful for Lyme-related insomnia.',
            'No screens 2 hours before bed: Blue light worsens already problematic sleep.',
            'Sleep meds if necessary: Discuss with doctor. Quality sleep essential for healing.',
            'Treat sleep disorders: Sleep apnea, restless leg syndrome if present.',
            'Naps allowed: If body needs it. But not too late in day or too long (interferes with night sleep).'
          ]
        },
        {
          name: '🔥 Managing Herxheimer Reactions',
          priority: 'HIGH',
          items: [
            'What is Herx: Herxheimer (Jarisch-Herxheimer) reaction = feeling worse when bacteria die off. Dead bacteria release toxins.',
            'Common with Lyme treatment: Especially when starting antibiotics or herbal protocols. Can be severe.',
            'Symptoms increase: All symptoms worsen - fatigue, pain, brain fog, anxiety, etc. Can last days to weeks.',
            'Proves treatment working: If you Herx, it means you\'re killing bacteria. Good sign but difficult to endure.',
            'Hydration essential: Drink lots of water to flush toxins. Half body weight in ounces minimum.',
            'Use binders: Activated charcoal, bentonite clay, chlorella help bind and eliminate toxins. Take away from meds/supplements.',
            'Epsom salt baths: Help detoxify through skin. Add 2 cups Epsom salt, soak 20-30 min.',
            'Support detox pathways: Liver support (milk thistle), ensure daily bowel movements, sweat if able.',
            'Slow down treatment if severe: Pulsing antibiotics, lowering dose, adding more days off between treatment days.',
            'Rest more during Herx: Body working hard. Need extra rest.',
            'Anti-inflammatory support: Curcumin, omega-3s, quercetin help reduce inflammation.',
            'Be patient: Herx reactions temporary. Will pass. Means you\'re on right track.'
          ]
        },
        {
          name: '🧘 Stress Management',
          priority: 'HIGH',
          items: [
            'Stress worsens everything: Lyme thrives when immune system weakened by stress.',
            'Chronic illness is stressful: Pain, fatigue, financial stress, relationship stress, medical system stress.',
            'Daily stress reduction practice: Meditation, breathwork, gentle yoga - pick something and do it daily.',
            'Therapy essential: Living with chronic Lyme traumatic. Need mental health support.',
            'Support groups: Online or in-person. Connecting with others who understand reduces isolation.',
            'Boundaries critical: Say no to stressful situations, toxic people, unnecessary obligations.',
            'Lower expectations: Of yourself and others. You\'re managing serious illness.',
            'Mindfulness practices: Present moment awareness reduces anxiety about uncertain future.',
            'Gentle movement: Yoga, tai chi, walking if able. Movement reduces stress hormones.',
            'Time in nature: Even 10-15 minutes. Nature reduces cortisol, supports healing.',
            'Creative expression: Art, music, writing. Processes emotions non-verbally.',
            'Avoid stressful media: News, violent shows, social media doom-scrolling increases inflammation.'
          ]
        },
        {
          name: '🏃 Exercise Modifications',
          priority: 'MODERATE',
          items: [
            'Exercise intolerance common: Lyme damages mitochondria. Body can\'t produce energy for exercise.',
            'Listen to your body: If exercise makes you feel worse, it\'s too much. Back off immediately.',
            'Start very low: 5-10 minutes gentle walking if that\'s all you can tolerate.',
            'Build extremely gradually: Add 1-2 minutes per week only if no post-exertional crashes.',
            'Post-exertional malaise is sign: Of overdoing it. May not feel it for 24-48 hours after activity.',
            'Gentle movement helpful: When tolerated. Supports lymphatic drainage (carries away toxins).',
            'Stretching and yoga: Restorative, not power yoga. Maintains flexibility without overexertion.',
            'Swimming if tolerated: Low-impact, full body. But chlorine may bother some.',
            'Don\'t push through: "No pain, no gain" doesn\'t apply. Pain = damage with Lyme.',
            'Exercise capacity returns: As you heal. May take many months to years. Be patient.',
            'Some never return to previous level: That\'s okay. Adjust expectations.'
          ]
        },
        {
          name: '🧠 Cognitive Support',
          priority: 'MODERATE',
          items: [
            'Brain fog is debilitating: Difficulty thinking, remembering, processing. Affects work, school, daily life.',
            'This is organic brain dysfunction: Not psychological. Bacteria in brain, neuroinflammation, neurotoxins.',
            'Will improve with treatment: But takes time. Brain heals slowly.',
            'External supports help: Lists, calendars, phone reminders, notes everywhere.',
            'Simplify when possible: Routines reduce cognitive load. Put things in same place always.',
            'Avoid multitasking: Can\'t process multiple things. Do one thing at a time.',
            'Take breaks: Cognitive work exhausting. 15-20 min work, 5-10 min rest.',
            'Reduce stimulation: Noise, visual clutter worsen brain fog. Quiet, simple environment better.',
            'School/work accommodations: Reduced schedule, extra time, written instructions, quiet workspace.',
            'Don\'t fight it: Accept current limitations. Fighting causes more stress.',
            'Brain exercises later: Once treatment progressing. Not during acute phase (too exhausting).'
          ]
        },
        {
          name: '💢 Pain Management',
          priority: 'MODERATE-HIGH',
          items: [
            'Joint and muscle pain common: Lyme causes arthritis, muscle aches, nerve pain.',
            'Pain is real: Not "all in your head." Inflammation, infection, nerve damage cause real pain.',
            'Treat the infection: Pain improves as infection treated. But takes time.',
            'NSAIDs with caution: Ibuprofen, naproxen reduce inflammation but hard on gut. Already compromised by antibiotics.',
            'Tylenol alternative: Doesn\'t reduce inflammation but helps pain. Easier on gut.',
            'Curcumin very effective: Natural anti-inflammatory. May reduce pain significantly.',
            'Omega-3s reduce inflammation: High dose fish oil helps joint pain over time.',
            'Magnesium for muscle pain: Especially cramping. 400-600mg daily.',
            'Heat or cold: Heating pad, ice packs. Whatever feels better.',
            'Gentle stretching: When able. Keeps joints/muscles from getting stiff.',
            'Physical therapy: If recommended. Gentle, understanding PT can help.',
            'Pain medication if needed: Discuss with doctor. Quality of life important.',
            'Pain clinic if severe: Chronic pain specialists can help with severe, persistent pain.'
          ]
        },
        {
          name: '👨‍👩‍👧‍👦 Relationship & Family Support',
          priority: 'MODERATE',
          items: [
            'Chronic illness strains relationships: Financial stress, role changes, social isolation affect whole family.',
            'Communicate openly: About needs, limitations, feelings. Don\'t expect others to read your mind.',
            'Educate family: About Lyme, why you can\'t do things you used to, that this is real illness.',
            'Some won\'t believe you: Lyme controversial. Some people, even family, may doubt. Protect yourself from doubters.',
            'Find understanding friends: Other Lyme patients "get it." Online communities helpful.',
            'Accept help: Let people help with meals, errands, childcare. You can\'t do it all.',
            'Family therapy if needed: Everyone affected by chronic illness. Professional support helps.',
            'Children need support too: If parent has Lyme. Age-appropriate explanations. Their own therapy if struggling.',
            'Maintain connection: Even if can\'t do what you used to. Quality time looks different now.',
            'Set boundaries: With people who drain you or don\'t support healing. Protect energy.',
            'Grief is normal: For life before Lyme. Allow yourself and family to grieve losses.'
          ]
        },
        {
          name: '💰 Financial Survival',
          priority: 'MODERATE-HIGH',
          items: [
            'Lyme treatment is expensive: Long-term antibiotics, supplements, specialist appointments often not covered.',
            'Loss of income common: Too sick to work or work full-time. Financial stress adds to illness burden.',
            'Prioritize treatment: Health first. Everything else second.',
            'Look for financial assistance: LymeLight Foundation (children), local organizations, churches, GoFundMe.',
            'Disability if unable to work: Apply for SSDI (long process). Social Security doesn\'t easily approve Lyme but possible.',
            'GoodRx for prescriptions: Often cheaper than insurance copays.',
            'Communicate with providers: Some offer payment plans or sliding scale.',
            'Simplify life: Reduce expenses where possible. Minimalism becomes necessity.',
            'Insurance appeals: If treatments denied. Persistence sometimes works.',
            'Focus on essential supplements: Can\'t afford everything. Prioritize: Vitamin D, omega-3, probiotics, B vitamins.',
            'This strain is temporary: As you recover, work capacity often returns. Financial recovery possible.'
          ]
        }
      ],
      hardTruth: {
        title: 'Hope & Reality',
        items: [
          'Treatment for tick-borne infections is long and difficult - but people DO recover',
          'Recovery isn\'t linear - ups and downs, setbacks normal',
          'Patience essential - treatment typically 6 months to 2+ years',
          'Some residual symptoms may persist but quality of life can be regained',
          'Early treatment gives better outcomes - but late treatment still helps',
          'You will have to fight - for diagnosis, for treatment, for understanding',
          'But you\'re worth fighting for - keep going, even when it\'s hard'
        ]
      }
    },
    diet: {
      intro: 'Diet plays important role in managing Lyme and co-infections. Goals: Reduce inflammation, support immune function, starve pathogens, support detoxification, maintain stable blood sugar, heal gut damage from antibiotics.',
      foodsToEmphasize: {
        title: '✅ Foods to Emphasize',
        categories: [
          {
            name: 'Vegetables (7-9 servings daily)',
            items: [
              'Leafy greens: Spinach, kale, collards, Swiss chard, arugula, lettuce',
              'Cruciferous (support detox): Broccoli, cauliflower, Brussels sprouts, cabbage',
              'Sulfur-rich (support glutathione): Onions, garlic, leeks',
              'Colorful veggies: Bell peppers, carrots, beets, sweet potatoes, squash',
              'Others: Celery, cucumber, zucchini, asparagus, green beans'
            ]
          },
          {
            name: 'Clean Proteins',
            items: [
              'Wild-caught fish (omega-3s): Salmon, sardines, mackerel, anchovies - 2-3x/week minimum',
              'Organic, pasture-raised poultry: Chicken, turkey',
              'Grass-fed beef and lamb: In moderation',
              'Pasture-raised eggs: Great source of choline, protein',
              'Bone broth: Healing for gut (damaged by antibiotics), immune support, minerals'
            ]
          },
          {
            name: 'Healthy Fats (Essential)',
            items: [
              'Extra virgin olive oil: Primary cooking oil, anti-inflammatory',
              'Coconut oil: Antimicrobial properties',
              'Avocados: If tolerated (some have histamine issues)',
              'Nuts and seeds: Walnuts (omega-3), almonds, pumpkin seeds, chia, flax, hemp',
              'Grass-fed ghee/butter: If dairy tolerated'
            ]
          },
          {
            name: 'Fruits (Lower-Sugar)',
            items: [
              'Berries: Blueberries, blackberries, raspberries (high antioxidants, lower sugar)',
              'Green apples, pears: Lower glycemic than other fruits',
              'Lemon and lime: In water, supports detox'
            ],
            note: 'Limit to 1-2 servings daily. Sugar feeds pathogens.'
          },
          {
            name: 'Gluten-Free Grains (If Tolerated)',
            items: [
              'Quinoa, rice (brown and white), millet, buckwheat, certified GF oats'
            ],
            note: 'Many do better avoiding all grains initially. Test tolerance after symptoms improve.'
          },
          {
            name: 'Anti-Inflammatory Herbs & Spices',
            items: [
              'Turmeric: Most powerful anti-inflammatory. Use daily with black pepper.',
              'Ginger: Anti-inflammatory, digestive aid, nausea relief',
              'Garlic: Antimicrobial, immune support',
              'Others: Oregano, thyme, rosemary, basil, cilantro, parsley, cinnamon'
            ]
          },
          {
            name: 'Beverages',
            items: [
              'Water: Plenty - half body weight in ounces. Essential for detox.',
              'Bone broth: Sip throughout day',
              'Green tea: Antioxidants (if caffeine tolerated)',
              'Herbal teas: Ginger, turmeric, chamomile, peppermint'
            ]
          }
        ]
      },
      foodsToAvoid: {
        title: '🔴 Foods to Avoid (Pro-Inflammatory, Feed Pathogens)',
        categories: [
          {
            name: 'Sugar & Refined Carbs (CRITICAL TO AVOID)',
            reason: 'Feed bacteria, yeast, parasites. Suppress immune function. Increase inflammation.',
            items: [
              'White sugar, brown sugar, high fructose corn syrup, honey, maple syrup, agave',
              'Candy, cookies, cakes, pastries, ice cream',
              'Soda and sugary drinks',
              'Fruit juice (pure sugar without fiber)',
              'White bread, white pasta, white rice',
              'Most packaged/processed foods (hidden sugars)',
              'Artificial sweeteners (toxic)'
            ]
          },
          {
            name: 'Gluten',
            reason: 'Increases intestinal permeability (leaky gut), inflammatory, often cross-reactive',
            items: [
              'Wheat, barley, rye, conventional oats',
              'Bread, pasta, baked goods',
              'Many Lyme patients have celiac-like symptoms',
              'Molecular mimicry between gluten and Borrelia proteins'
            ]
          },
          {
            name: 'Dairy (Consider Eliminating)',
            reason: 'Inflammatory, mucus-producing, casein problematic for many',
            items: [
              'Milk, cheese, yogurt, ice cream',
              'Often inflammatory in Lyme patients',
              'Exception: Grass-fed ghee or butter often tolerated',
              'Try elimination for 4-6 weeks, see if symptoms improve'
            ]
          },
          {
            name: 'Alcohol',
            reason: 'Liver already working hard. Sugar feeds pathogens. Worsens sleep. Immune suppression.',
            items: [
              'All alcoholic beverages',
              'Especially problematic during active treatment'
            ]
          },
          {
            name: 'Processed Foods',
            reason: 'Inflammatory oils, additives, preservatives, lack nutrients, burden detox systems',
            items: [
              'Fast food, fried foods',
              'Packaged snacks, frozen dinners',
              'Processed meats (deli meat, hot dogs, bacon, sausage)',
              'Anything with ingredient list you can\'t pronounce'
            ]
          },
          {
            name: 'Inflammatory Oils',
            reason: 'High omega-6, promote inflammation, oxidize easily',
            items: [
              'Vegetable oil, canola oil, soybean oil, corn oil',
              'Margarine, shortening',
              'Anything fried in these oils'
            ]
          },
          {
            name: 'Food Additives',
            reason: 'Toxic burden, inflammatory, neurotoxic',
            items: [
              'Artificial colors and dyes',
              'MSG and hidden MSG (natural flavors, yeast extract)',
              'Preservatives (BHT, BHA, nitrates)',
              'Artificial flavors'
            ]
          }
        ]
      },
      sampleDay: {
        title: 'Sample Day of Anti-Lyme Eating',
        meals: [
          { name: 'Upon Waking', description: 'Large glass of water with lemon. Binders if using (away from meds).' },
          { name: 'Breakfast', description: 'Scrambled eggs with sautéed spinach, onions, mushrooms in coconut oil. Side of berries. Green or herbal tea.' },
          { name: 'Mid-Morning', description: 'Apple with almond butter, or celery with guacamole' },
          { name: 'Lunch', description: 'Large salad with mixed greens, grilled chicken or salmon, colorful vegetables, pumpkin seeds, olive oil and lemon dressing. Side of quinoa or sweet potato.' },
          { name: 'Afternoon', description: 'Carrot sticks with hummus, or handful of walnuts and berries' },
          { name: 'Dinner', description: 'Grass-fed beef or wild salmon with roasted broccoli, cauliflower, and Brussels sprouts (drizzled with olive oil, turmeric, garlic). Roasted sweet potato. Large side salad.' },
          { name: 'Evening', description: 'Bone broth or herbal tea (ginger, turmeric, chamomile)' }
        ]
      },
      tips: {
        title: 'Practical Diet Tips',
        items: [
          'Organic when possible: Reduce pesticide burden on already overwhelmed detox systems',
          'Meal prep when able: Cook in batches on good days. Having food ready prevents poor choices.',
          'Keep it simple: Don\'t need fancy recipes. Simple, whole foods work.',
          'Hydration critical: Especially during treatment and Herx reactions',
          'Blood sugar stability: Eat protein with every meal/snack. Prevents crashes.',
          'Chew thoroughly: Supports digestion (already compromised)',
          'Elimination diet if needed: Remove common triggers for 4-6 weeks, reintroduce one at a time',
          'Work with nutritionist: Familiar with Lyme if possible',
          'This gets easier: Initially restrictive, but becomes habit. Worth it for reduced symptoms.',
          'Food is medicine: One of few things you can control. Use it.'
        ]
      }
    }
  },

  // ==================== PARASITES ====================
  'parasites': {
    name: 'Parasites',
    color: 'bg-amber-600',
    lightColor: 'bg-amber-100',
    textColor: 'text-amber-700',
    description: 'Parasitic infections affecting children with immune dysfunction - an often overlooked contributor to chronic symptoms',
    about: {
      isExtended: true,
      whatIs: {
        title: 'Parasites: The Hidden Epidemic',
        description: 'Parasitic infections are FAR more common than most people (and doctors) realize—especially in children with chronic illness like PANDAS/PANS. Parasites are organisms that live in or on a host, feeding off the host while contributing nothing in return. They range from microscopic single-celled protozoa to large worms visible to the naked eye.'
      },
      whyItMatters: {
        title: 'Why Parasites Are Underdiagnosed',
        description: 'Parasite infections are frequently missed for several critical reasons:'
      },
      uniqueCharacteristics: {
        title: 'Why Parasites Are Underdiagnosed',
        points: [
          { name: 'Doctors don\'t think about them', description: 'Medical training in the US teaches that parasites are a "third world problem." Many doctors never consider them.' },
          { name: 'Standard testing misses most', description: 'Conventional stool O&P test misses 70-80% of infections. Requires fresh, warm stool examined by trained parasitologist—rarely happens.' },
          { name: 'Mimic other conditions', description: 'Parasite symptoms overlap with IBS, anxiety, ADHD, autoimmunity, allergies. Easy to misdiagnose.' },
          { name: 'Come and go', description: 'Parasites have life cycles. May shed intermittently. Negative test doesn\'t rule them out.' },
          { name: 'Hide in tissues', description: 'Some live in organs, muscles, brain—not in digestive tract. Stool test won\'t find them.' }
        ]
      },
      prevalence: {
        title: 'How Common Are Parasites Really?',
        points: [
          'CDC estimates: At least 60 million Americans infected (likely massive underestimate)',
          'WHO estimates: 3.5 billion people worldwide infected with intestinal parasites',
          'Children at highest risk: Play in dirt, put things in mouth, poor handwashing, close contact with others',
          'Some experts believe: 80-90% of chronic illness involves parasites as trigger or contributor',
          'You don\'t need to travel: Many parasites endemic in US. Contaminated water, food, pets, other people all sources.'
        ]
      },
      howItTriggersPans: {
        title: 'The PANDAS/PANS Connection',
        steps: [
          'Trigger autoimmunity: Chronic immune activation and molecular mimicry can trigger antibodies against brain tissue',
          'Create leaky gut: Intestinal damage allows LPS and inflammatory molecules into bloodstream, worsening neuroinflammation',
          'Toxin production: Ammonia and other neurotoxins directly affect brain function, worsen OCD, anxiety, brain fog',
          'Nutrient depletion: Steal vitamins, minerals needed for neurotransmitter production and methylation',
          'Block healing: Even with antibiotics, IVIG, other treatments—if parasites present, child may not fully improve',
          'Cause flares: Parasite life cycles can cause waxing/waning of symptoms. Mystery flares may be parasite-related.'
        ]
      },
      testingChallenges: {
        title: 'Types of Parasites',
        description: 'Parasites fall into several categories:',
        challenges: [
          { name: 'Protozoa (Single-Celled)', description: 'Blastocystis, Giardia, Entamoeba, Dientamoeba, Cryptosporidium, Toxoplasma. Microscopic, often intracellular.' },
          { name: 'Helminths (Worms)', description: 'Pinworms (up to 50% of US children!), Roundworms, Hookworms, Tapeworms, Flukes. Can range from tiny to 30+ feet.' },
          { name: 'Key Insight', description: 'Many PANS practitioners find that children who don\'t respond fully to standard treatment often have undiagnosed parasites.' }
        ]
      },
      exposureRoutes: {
        title: 'How You Get Parasites',
        items: [
          'Contaminated water: Drinking, swimming, even showering in contaminated water',
          'Contaminated food: Unwashed produce, undercooked meat, raw fish (sushi), imported foods',
          'Person-to-person: Poor handwashing, changing diapers, close contact (pinworms highly contagious)',
          'Pets: Dogs, cats carry parasites. Licking, close contact, contaminated soil from feces',
          'Soil: Walking barefoot, gardening without gloves, children playing in dirt/sandbox',
          'Travel: International travel increases exposure but NOT required'
        ]
      },
      clinicalObservations: {
        title: 'Clinical Observations',
        intro: 'When parasites are addressed in PANS children, practitioners often see:',
        items: [
          'Reduction in baseline anxiety and OCD symptoms',
          'Improved gut function (digestion, regularity, less pain)',
          'Better sleep quality',
          'Increased energy',
          'Clearer thinking / reduced brain fog',
          'Fewer allergic reactions',
          'Better response to other PANS treatments'
        ]
      }
    },
    keyPoints: [
      'At least 60 million Americans infected - likely a massive underestimate',
      'Standard O&P stool tests miss 70-80% of infections',
      'Pinworms affect up to 50% of US children',
      'Toxoplasma infects 11-30% of US population and directly affects the brain',
      'Parasites trigger autoimmunity, create leaky gut, and produce neurotoxins',
      'Children who don\'t respond to standard PANS treatment often have undiagnosed parasites'
    ],
    symptoms: {
      title: 'Parasite Symptoms',
      intro: 'Parasite symptoms are diverse and often vague, mimicking many other conditions. This is why they\'re so often missed. The key is looking at the constellation of symptoms together.',
      categories: [
        {
          name: 'Digestive Symptoms (Most Common but Not Always Present)',
          icon: '🤢',
          severity: 'COMMON',
          items: [
            'Chronic diarrhea or loose stools',
            'Constipation (especially with worms that cause blockages)',
            'Alternating diarrhea and constipation',
            'IBS-like symptoms (diagnosed IBS often undiagnosed parasites)',
            'Abdominal pain or cramping (can be severe)',
            'Bloating and gas (especially after eating)',
            'Nausea',
            'Vomiting (less common)',
            'Mucus in stool',
            'Blood in stool (hookworms, some protozoa)',
            'Foul-smelling stools',
            'Undigested food in stool',
            'Rectal itching (especially pinworms - worse at night)'
          ]
        },
        {
          name: 'Neurological & Behavioral Symptoms (Critical for PANS)',
          icon: '🧠',
          severity: 'CRITICAL',
          items: [
            'Brain fog and difficulty concentrating',
            'Memory problems',
            'Anxiety (can be severe)',
            'Depression',
            'Irritability and mood swings',
            'Rage episodes (toxins affect brain)',
            'OCD-like behaviors',
            'Tics',
            'Hyperactivity or ADHD symptoms',
            'Sleep disturbances - insomnia, nightmares, night terrors',
            'Restlessness especially at night',
            'Teeth grinding (bruxism) - classic parasite symptom',
            'Personality changes',
            'Decreased motivation'
          ]
        },
        {
          name: 'Sleep Issues',
          icon: '🌙',
          severity: 'COMMON',
          items: [
            'Difficulty falling asleep',
            'Waking between 1-3 AM (liver cleansing time - parasite toxins burden liver)',
            'Multiple night wakings',
            'Restless legs at night',
            'Teeth grinding during sleep',
            'Talking, screaming, or moving during sleep',
            'Nightmares',
            'Bedwetting (in children who were previously dry)'
          ]
        },
        {
          name: 'Skin Problems',
          icon: '🔴',
          severity: 'COMMON',
          items: [
            'Itching (especially anal/rectal area, but can be anywhere)',
            'Rashes or hives',
            'Eczema or psoriasis flares',
            'Acne or skin breakouts',
            'Dry, flaky skin',
            'Sores that won\'t heal',
            'Crawling sensations on or under skin'
          ]
        },
        {
          name: 'Appetite & Weight Changes',
          icon: '🍽️',
          severity: 'VARIABLE',
          items: [
            'Increased hunger (especially for sweets/carbs - parasites love sugar)',
            'Never feeling satisfied after eating',
            'Weight loss despite eating well',
            'Weight gain (metabolic disruption)',
            'Food cravings (especially sugar, carbs)',
            'Loss of appetite',
            'Nausea after eating'
          ]
        },
        {
          name: 'Immune & Allergy Symptoms',
          icon: '🤧',
          severity: 'COMMON',
          items: [
            'Frequent infections',
            'Chronic sinusitis',
            'New or worsening allergies (food and environmental)',
            'Asthma or wheezing',
            'Chronic cough',
            'Recurring ear infections',
            'Post-nasal drip',
            'Swollen lymph nodes',
            'Low-grade fever',
            'Autoimmune conditions'
          ]
        },
        {
          name: 'Energy & Fatigue',
          icon: '😴',
          severity: 'COMMON',
          items: [
            'Chronic fatigue (parasites steal nutrients and produce toxins)',
            'Weakness',
            'Exhaustion despite rest',
            'Lack of energy or motivation',
            'Worsening fatigue after eating (especially sugar/carbs)',
            'Exercise intolerance'
          ]
        },
        {
          name: 'Pain Symptoms',
          icon: '⚡',
          severity: 'VARIABLE',
          items: [
            'Abdominal/stomach pain (can migrate, come and go)',
            'Joint pain and muscle aches',
            'Headaches or migraines',
            'Back pain',
            'Chest pain (lung flukes, migrating larvae)'
          ]
        },
        {
          name: 'Nutritional Deficiencies',
          icon: '💊',
          severity: 'SIGNIFICANT',
          items: [
            'Anemia (hookworms cause blood loss, others cause B12/iron malabsorption)',
            'B12 deficiency (fish tapeworm)',
            'Iron deficiency',
            'Other vitamin/mineral deficiencies despite supplementation',
            'Pale skin',
            'Hair loss',
            'Brittle nails'
          ]
        },
        {
          name: 'Other Symptoms',
          icon: '🔍',
          severity: 'VARIABLE',
          items: [
            'Bad breath (toxins from parasites)',
            'Body odor changes',
            'Sensitivity to smells',
            'Blurred vision',
            'Eye floaters',
            'Dizziness',
            'Heart palpitations',
            'Reproductive issues or menstrual irregularities',
            'Sensory sensitivities',
            'Temperature regulation problems'
          ]
        },
        {
          name: 'Red Flags Suggesting Parasites',
          icon: '🚨',
          severity: 'INVESTIGATE',
          items: [
            'Multiple seemingly unrelated symptoms that don\'t fit one diagnosis',
            'Symptoms that come and go mysteriously (parasite life cycles)',
            'Symptoms worse on full moon (controversial but many observe this)',
            'Anal itching, especially at night in children',
            'Teeth grinding during sleep',
            'Chronic digestive issues without clear cause',
            'Multiple food sensitivities that keep increasing',
            'Not responding to treatments that should work',
            'Chronic illness started after travel or stomach bug',
            'Strong sugar/carb cravings'
          ]
        }
      ],
      importantNote: 'You don\'t need ALL these symptoms. Even a few in combination warrant investigation. Many people with parasites have NO digestive symptoms—neurological and fatigue symptoms may be primary presentation.'
    },
    triggers: {
      title: 'Testing for Parasites',
      intro: 'Parasite testing is notoriously unreliable. Many tests miss infections. Multiple methods and repeat testing often necessary.',
      sections: [
        {
          name: 'Why Testing Is So Difficult',
          description: 'Understanding the challenges of parasite detection',
          items: [
            'Intermittent shedding: Parasites don\'t shed eggs/cysts every day. Negative test doesn\'t rule them out.',
            'Life cycles: May be in tissue stage, not shedding at time of test.',
            'Standard O&P terrible: Conventional stool test misses 70-80% of parasites. Sample sits too long, wrong preservatives, tech not trained.',
            'Hidden in biofilms: Parasites hide in protective biofilm matrix. Don\'t show up in stool.',
            'Tissue parasites: Toxoplasma, some tapeworm larvae live in organs/brain. Won\'t appear in stool.',
            'Small numbers: May have small parasite load but still symptomatic. Below detection threshold.'
          ]
        },
        {
          name: 'Comprehensive Stool Testing (Best Option)',
          description: 'Specialized labs only—NOT standard hospital/LabCorp/Quest O&P',
          items: [
            'Doctor\'s Data - Comprehensive Stool Analysis: PCR testing (DNA-based), microscopy, tests for wide range of parasites, bacteria, yeast. $300-400. Requires provider order.',
            'Genova Diagnostics - GI Effects: Comprehensive - parasites, bacteria, yeast, inflammation markers. PCR and microscopy. Digestive function markers. $400-500.',
            'Diagnostic Solutions - GI-MAP: PCR-based (DNA testing). Tests for parasites, bacteria, viruses, fungi. Very sensitive. $300-400.',
            'Parasitology Center (Tempe, AZ): Dr. Omar Amin - world expert. Most comprehensive parasite testing available. Fresh stool samples examined immediately by experts. Tests for obscure parasites others miss. More expensive but worth it if high suspicion.',
            'Protocol: Collect 3 samples on different days (increases detection). Follow collection instructions exactly.'
          ]
        },
        {
          name: 'Blood Tests',
          description: 'Useful for certain parasites and screening',
          items: [
            'Serology (Antibody Testing): Tests for antibodies to specific parasites (Toxoplasma, Trichinella, etc.). Doesn\'t show active infection necessarily. Useful for tissue parasites.',
            'Eosinophils (CBC with Differential): Elevated eosinophils suggest parasites (body\'s response to worms). Not all parasites cause elevation. Inexpensive screening tool.',
            'IgE (Total): Elevated IgE suggests allergic response or parasites. Non-specific but raises suspicion.'
          ]
        },
        {
          name: 'Pinworm Tape Test',
          description: 'For pinworms (most common US parasite in kids)',
          items: [
            'Do at home: Press clear tape to anal area first thing in morning (before bathing/wiping)',
            'Stick tape to glass slide, take to lab for microscopy',
            'Repeat 3 mornings for best detection',
            'Stool tests miss pinworms (eggs laid outside anus)'
          ]
        },
        {
          name: 'Imaging (for Suspected Tissue Parasites)',
          description: 'Only if high suspicion based on symptoms/exposure history',
          items: [
            'CT or MRI: Can show cysts in brain (Toxoplasma, tapeworm cysts), liver (liver flukes, amoeba)',
            'Ultrasound: Liver, intestinal parasites'
          ]
        },
        {
          name: 'Other Markers',
          description: 'Additional tests to consider',
          items: [
            'Anemia workup: If unexplained anemia (hookworms)',
            'B12 levels: Low B12 can indicate fish tapeworm',
            'Stool microscopy at home: Some people see worms or segments in stool (tapeworm segments look like rice grains)'
          ]
        },
        {
          name: 'Testing Strategy',
          description: 'Recommended approach',
          items: [
            '1. Start with comprehensive stool test (GI-MAP, GI Effects, or Doctor\'s Data). Collect 3 samples on different days.',
            '2. Blood work: CBC with differential (check eosinophils), total IgE',
            '3. Pinworm tape test if child has anal itching or teeth grinding (do at home, 3 mornings)',
            '4. If negative but high suspicion: Repeat testing with different lab. Consider Parasitology Center.',
            '5. Toxoplasma antibody test if neuropsychiatric symptoms severe (especially with cat exposure)',
            '6. Empirical treatment may be warranted even with negative tests if clinical picture fits'
          ]
        },
        {
          name: 'When to Suspect Parasites Without Testing',
          description: 'If testing negative or unavailable but clinical picture strongly suggests parasites, consider empirical treatment trial',
          items: [
            'Child has many classic symptoms (especially anal itching, teeth grinding, digestive issues)',
            'Recent travel to endemic areas',
            'Exposure history (contaminated water, undercooked meat, pets)',
            'Not responding to standard PANS treatment',
            'Visible worms/segments in stool (don\'t need test—you saw them!)',
            'Work with knowledgeable practitioner for empirical treatment. Track symptoms carefully during treatment to assess response.'
          ]
        },
        {
          name: 'Important Testing Limitations',
          description: 'Critical points to remember',
          items: [
            'Negative test doesn\'t rule out parasites: Many false negatives',
            'Treat clinical picture, not just test results: If looks like parasites, acts like parasites—probably parasites',
            'Repeat testing if needed: May take multiple tests to catch them',
            'Some parasites essentially untestable: Live in tissues, don\'t shed, no antibody test available',
            'Empirical treatment reasonable: Safe anti-parasitic herbs/meds can be tried even without positive test'
          ]
        }
      ]
    },
    supplements: {
      title: 'Anti-Parasitic Supplements & Herbs',
      intro: 'Both pharmaceutical medications and herbal protocols can eliminate parasites. Many practitioners use combination approach. Herbal protocols take longer but often better tolerated and address broader range of parasites.',
      dieOffWarning: {
        title: 'CRITICAL: Die-Off (Herxheimer) Reactions',
        description: 'When parasites killed, they release toxins. Can feel MUCH worse temporarily:',
        symptoms: [
          'Worsening of all symptoms',
          'Flu-like feeling, fatigue, headache',
          'Skin breakouts, rashes',
          'Mood changes, irritability',
          'Digestive upset'
        ],
        management: 'Management: Start low dose, increase slowly. Excellent binder support (see below). Hydration. Support detoxification. If severe, back off dose. Herx = treatment working but may need to slow down.'
      },
      categories: [
        {
          name: 'Anti-Parasitic Herbs & Supplements',
          importance: 'FOUNDATIONAL',
          importanceNote: 'Core herbs for eliminating parasites',
          supplements: [
            {
              name: 'Black Walnut Hull (Green)',
              dose: 'Per product instructions, typically 500-1000mg 2-3x daily',
              evidence: 'Traditional use, clinical evidence',
              benefits: [
                'Effective against wide range of parasites',
                'Contains juglone (antiparasitic compound)',
                'Works on protozoa and worms',
                'Also antifungal (Candida)',
                'Part of classic "Trinity" protocol with wormwood and cloves'
              ],
              notes: 'Use tincture or capsules. Green (unripe) hull most potent. Can stain, use carefully. Start low dose. Part of most parasite protocols.'
            },
            {
              name: 'Wormwood (Artemisia absinthium)',
              dose: '200-500mg 2-3x daily, or per product instructions',
              evidence: 'Strong traditional and clinical evidence',
              benefits: [
                'Powerful against protozoa (Giardia, amoebas)',
                'Also effective against worms',
                'Contains artemisinin (antimalarial compound)',
                'Anti-inflammatory',
                'Supports digestion',
                'Part of classic Trinity protocol'
              ],
              notes: 'Very bitter. Use capsules if can\'t tolerate taste. Don\'t use if pregnant. Short-term use (4-6 weeks max, then break). Highly effective.'
            },
            {
              name: 'Cloves',
              dose: '500mg 3x daily, or per protocol',
              evidence: 'Traditional use',
              benefits: [
                'Kills parasite EGGS (this is key!)',
                'Prevents reinfection from eggs hatching',
                'Most antiparasitics kill adults but not eggs',
                'Also antimicrobial and antifungal',
                'Part of classic Trinity protocol'
              ],
              notes: 'Essential part of parasite protocol. Without killing eggs, reinfestation certain. Can use ground cloves in food too.'
            },
            {
              name: 'Oregano Oil',
              dose: '500mg 2-3x daily (emulsified form best)',
              evidence: 'Strong antimicrobial evidence',
              benefits: [
                'Broad-spectrum antiparasitic',
                'Antibacterial, antiviral, antifungal',
                'Disrupts biofilms',
                'Supports gut health',
                'Anti-inflammatory'
              ],
              notes: 'Very strong. Can cause die-off. Take with food. Emulsified forms (ADP) easier on stomach. Rotate with other herbs.'
            },
            {
              name: 'Garlic (Allicin)',
              dose: 'Raw garlic (1-2 cloves daily) or aged garlic extract (600-1200mg)',
              evidence: 'Well-studied antimicrobial',
              benefits: [
                'Antiparasitic (especially protozoa, pinworms)',
                'Antibacterial, antiviral, antifungal',
                'Immune support',
                'Prebiotic (feeds good gut bacteria)',
                'Anti-inflammatory',
                'Cardiovascular benefits'
              ],
              notes: 'Raw garlic most potent (must crush/chop to activate allicin). Aged garlic extract (Kyolic) gentler, still effective. Can add to food.'
            },
            {
              name: 'Berberine',
              dose: '500mg 2-3x daily',
              evidence: 'Extensive research',
              benefits: [
                'Powerful against protozoa (Giardia, Entamoeba, Blastocystis)',
                'Antibacterial (especially for SIBO)',
                'Antifungal',
                'Blood sugar regulation',
                'Anti-inflammatory',
                'Gut health support'
              ],
              notes: 'From goldenseal, barberry, Oregon grape. Very effective. Can cause die-off. Take with meals. Yellow color (can stain).'
            },
            {
              name: 'Grapefruit Seed Extract (GSE)',
              dose: '100-200mg 2-3x daily, or 10-15 drops in water',
              evidence: 'Clinical use',
              benefits: [
                'Broad-spectrum antimicrobial',
                'Effective against many parasites',
                'Antibacterial, antiviral, antifungal',
                'Relatively gentle',
                'Can use long-term'
              ],
              notes: 'Liquid or capsules. Bitter taste (use capsules or mix in juice). Quality varies by brand. NutriBiotic reputable.'
            },
            {
              name: 'Neem',
              dose: '500-1000mg 2x daily',
              evidence: 'Traditional Ayurvedic medicine',
              benefits: [
                'Broad antiparasitic',
                'Antiviral, antibacterial, antifungal',
                'Immune modulation',
                'Skin health (topical and internal)',
                'Detoxification support'
              ],
              notes: 'Used for centuries in India. Bitter taste. Capsules easiest. Safe for longer-term use.'
            },
            {
              name: 'Mimosa Pudica Seed',
              dose: '1000-2000mg 2x daily on empty stomach',
              evidence: 'Emerging clinical evidence',
              benefits: [
                'Mechanically removes parasites (becomes gel in gut, traps parasites)',
                'Visible worms in stool after taking (can be disturbing but confirms working)',
                'Also binds toxins',
                'Gut healing properties',
                'Different mechanism than herbs (doesn\'t kill, removes)'
              ],
              notes: 'Take on empty stomach with water (2 hours before/after food). May see worms in stool. Excellent addition to protocol. Microbe Formulas brand popular.'
            },
            {
              name: 'Diatomaceous Earth (Food Grade)',
              dose: '1-2 tsp in water daily, start with 1/2 tsp',
              evidence: 'Traditional use, mechanical action',
              benefits: [
                'Mechanically kills parasites (sharp edges cut exoskeletons)',
                'Removes parasites from gut',
                'Binds toxins',
                'Source of silica (gut healing)',
                'Very inexpensive'
              ],
              notes: 'MUST be food grade only. Mix in water, drink quickly (gritty texture). Take on empty stomach. Can dry out, drink extra water.'
            },
            {
              name: 'Pumpkin Seeds',
              dose: '1/4 cup raw seeds daily, or 1000-2000mg extract',
              evidence: 'Traditional remedy',
              benefits: [
                'Contains cucurbitacin (paralyzes worms)',
                'Gentle, safe, nutritious',
                'High in zinc (immune support)',
                'Good for prostate health',
                'Can eat as food'
              ],
              notes: 'Raw, organic seeds best. Grind in blender or food processor. Mix with food. Gentle option for children.'
            },
            {
              name: 'Papaya Seeds',
              dose: '1 tbsp seeds daily, or extract per instructions',
              evidence: 'Traditional use, some studies',
              benefits: [
                'Contains papain (enzyme that digests parasite protein)',
                'Effective against worms',
                'Digestive support',
                'Anti-inflammatory',
                'Nutritious'
              ],
              notes: 'From fresh papaya. Dry and grind, or blend fresh. Spicy/peppery taste. Can add to smoothies.'
            },
            {
              name: 'Sweet Wormwood (Artemisia annua)',
              dose: '200-400mg artemisinin 1-2x daily',
              evidence: 'Strong evidence (antimalarial)',
              benefits: [
                'Powerful antiparasitic',
                'Used for malaria treatment worldwide',
                'Effective against protozoa',
                'Anti-cancer properties being studied',
                'Anti-inflammatory'
              ],
              notes: 'Different from regular wormwood. Contains artemisinin. Very effective but more expensive. Short-term use.'
            },
            {
              name: 'Cayenne Pepper',
              dose: 'Start very small (pinch), build to 1/4-1/2 tsp 2-3x daily',
              evidence: 'Traditional use',
              benefits: [
                'Creates hostile environment for parasites',
                'Increases circulation (helps herbs reach tissues)',
                'Digestive support',
                'Metabolism boost',
                'Anti-inflammatory'
              ],
              notes: 'Very hot. Start tiny amounts. Can add to food or take in capsules. Not for everyone.'
            }
          ]
        },
        {
          name: 'Binders (CRITICAL)',
          importance: 'ESSENTIAL',
          importanceNote: 'Take 2 hours away from everything else. Mop up toxins released when parasites die. Prevent reabsorption. ESSENTIAL for managing die-off.',
          supplements: [
            {
              name: 'Activated Charcoal',
              dose: '1000mg 2-3x daily',
              notes: 'Binds wide range of toxins. Can cause constipation—increase water.'
            },
            {
              name: 'Bentonite Clay',
              dose: '1 tsp in water 1-2x daily',
              notes: 'Excellent binder, supports gut lining.'
            },
            {
              name: 'Chlorella',
              dose: '3-5 grams daily',
              notes: 'Binds heavy metals, supports detox, nutritious.'
            },
            {
              name: 'Zeolite',
              dose: 'Per product instructions',
              notes: 'Binds heavy metals and toxins.'
            },
            {
              name: 'GI Detox (Biocidin)',
              dose: 'Per product instructions',
              notes: 'Combination binder product. Very effective.'
            }
          ],
          binderNote: 'Rotate binders (use different ones different days) for broad spectrum binding. Take 2 hours away from food, supplements, medications.'
        },
        {
          name: 'Liver Support',
          importance: 'HIGH',
          importanceNote: 'Support detoxification during treatment',
          supplements: [
            {
              name: 'Milk Thistle',
              dose: '300mg 2-3x daily',
              notes: 'Protects and regenerates liver.'
            },
            {
              name: 'NAC (N-Acetyl Cysteine)',
              dose: '600-1200mg daily',
              notes: 'Glutathione precursor, detox support.'
            },
            {
              name: 'Dandelion Root',
              dose: 'Tea or extract',
              notes: 'Supports bile flow (carries toxins out).'
            },
            {
              name: 'Beet Root',
              dose: 'Per product or dietary intake',
              notes: 'Supports liver detoxification, bile production.'
            }
          ]
        },
        {
          name: 'Digestive Support',
          importance: 'HIGH',
          importanceNote: 'Support gut healing and function',
          supplements: [
            {
              name: 'Digestive Enzymes',
              dose: 'With meals',
              notes: 'Helps break down parasite biofilms and supports digestion.'
            },
            {
              name: 'HCL (if low stomach acid)',
              dose: 'Titrate to tolerance',
              notes: 'Stomach acid kills parasites. Many with parasites have low acid.'
            },
            {
              name: 'Probiotics',
              dose: '50-100 billion CFU',
              notes: 'Crowd out parasites, support immune function.'
            },
            {
              name: 'L-Glutamine',
              dose: '3-5 grams daily',
              notes: 'Heals gut lining damaged by parasites.'
            }
          ]
        },
        {
          name: 'Immune Support',
          importance: 'SUPPORTIVE',
          importanceNote: 'Strengthen immune function',
          supplements: [
            {
              name: 'Vitamin C',
              dose: '1000mg 2-3x daily',
              notes: 'Immune support, antioxidant.'
            },
            {
              name: 'Vitamin D',
              dose: '4000-5000 IU daily',
              notes: 'Critical for immune function.'
            },
            {
              name: 'Zinc',
              dose: '30mg daily',
              notes: 'Immune support, often depleted by parasites.'
            }
          ]
        }
      ],
      sampleProtocol: {
        title: 'Sample Parasite Protocol (Herbal Approach)',
        phases: [
          {
            name: 'Weeks 1-2: Prep Phase',
            items: [
              'Open detox pathways: Binders, liver support, hydration, daily bowel movements',
              'Support gut: Probiotics, L-glutamine',
              'Start very low dose antiparasitics to test tolerance'
            ]
          },
          {
            name: 'Weeks 3-8: Kill Phase',
            items: [
              'Black walnut, wormwood, cloves (Trinity protocol)',
              'Add oregano oil OR berberine',
              'Mimosa pudica seed',
              'Continue all support supplements',
              'Increase binders if die-off severe'
            ]
          },
          {
            name: 'Weeks 9-12: Continue + Rotate',
            items: [
              'Rotate herbs (use different antiparasitics)',
              'Prevents parasite adaptation',
              'Continue support'
            ]
          },
          {
            name: 'After Protocol',
            items: [
              'Retest if possible',
              'May need to repeat cycles',
              'Maintenance dose of garlic, pumpkin seeds ongoing',
              'Focus on gut healing'
            ]
          }
        ]
      },
      warnings: [
        'Work with knowledgeable practitioner: Especially for children, complex cases',
        'Start low, go slow: Die-off can be intense',
        'Binders essential: Not optional. Prevent reabsorption of toxins.',
        'Hydration critical: Half body weight in ounces water minimum',
        'Support detox pathways: Daily bowel movements, sweating, rest',
        'May feel worse before better: Die-off normal but uncomfortable',
        'Treat whole family + pets: Prevents reinfection',
        'Multiple rounds often needed: Parasites have life cycles. Kill adults, eggs hatch, need another round.',
        'Pharmaceutical options exist: Albendazole, mebendazole, ivermectin, nitazoxanide. Discuss with doctor.'
      ]
    },
    lifestyle: {
      title: 'Lifestyle Strategies for Parasite Prevention & Treatment',
      intro: 'Prevention and treatment support strategies for optimal outcomes.',
      categories: [
        {
          name: 'Hygiene Practices (Prevention #1)',
          icon: '🧼',
          importance: 'CRITICAL',
          importanceNote: 'Foundation of parasite prevention',
          items: [
            'Handwashing obsessively: Before eating, after bathroom, after touching pets, after outdoor play. Soap and water 20 seconds minimum. Hand sanitizer doesn\'t kill parasite eggs.',
            'Nail hygiene: Keep nails short and clean. Parasite eggs hide under nails. Children put fingers in mouth constantly.',
            'Don\'t share personal items: Towels, washcloths, toothbrushes. Pinworms spread through fomites.',
            'Shower in morning: Especially during treatment. Pinworms lay eggs at night—morning shower removes them before spread.',
            'Change underwear daily: Wash in hot water. Pinworm eggs can survive on fabric.',
            'Wash hands after changing diapers: Immediately. Parasites in stool highly contagious.',
            'No nail biting or thumb sucking: Direct route for parasites.',
            'Clean doorknobs, light switches: High-touch surfaces. Pinworm eggs can survive 2-3 weeks on surfaces.',
            'Individual bathrooms if possible during treatment: Prevents spread to family members.'
          ]
        },
        {
          name: 'Food Safety (Prevention #2)',
          icon: '🍽️',
          importance: 'CRITICAL',
          importanceNote: 'Prevent reinfection through contaminated food',
          items: [
            'Wash ALL produce thoroughly: Even organic. Soil contains parasite eggs. Use produce wash or vinegar solution.',
            'Cook meat completely: Use meat thermometer. 145°F minimum for whole cuts, 160°F ground meat, 165°F poultry. No pink.',
            'Avoid raw/undercooked: No rare meat, raw fish (sushi), raw eggs, unpasteurized dairy.',
            'Freeze fish: If eating raw (sushi), fish should be frozen to -4°F for 7 days or -31°F for 15 hours. Kills parasites.',
            'Separate cutting boards: One for raw meat, one for produce. Prevents cross-contamination.',
            'Clean cutting boards thoroughly: Hot soapy water after each use. Better yet, dishwasher.',
            'Don\'t eat at questionable restaurants: Food handling practices matter.',
            'Avoid buffets: Food sits out at improper temperatures. High contamination risk.',
            'Wash hands before cooking: Don\'t contaminate food you\'re preparing.',
            'Be careful with imported foods: Especially from countries with high parasite rates.'
          ]
        },
        {
          name: 'Water Safety',
          icon: '💧',
          importance: 'HIGH',
          importanceNote: 'Waterborne parasites are common',
          items: [
            'Filter drinking water: Reverse osmosis or high-quality filter. Municipal water can contain Giardia, Cryptosporidium (chlorine-resistant).',
            'Don\'t drink from lakes, streams, rivers: Even if looks clean. Giardia, Crypto very common in natural water.',
            'Boil questionable water: Rolling boil for 1 minute (3 minutes at high altitude). Kills parasites.',
            'Careful in swimming pools, lakes: Don\'t swallow water. Crypto in pools common (chlorine-resistant).',
            'Filter shower water if possible: Or at least don\'t drink shower water.',
            'Ice from filtered water only: Parasites survive freezing.',
            'Travel precautions: Bottled water in endemic countries. Brush teeth with bottled water. No ice.'
          ]
        },
        {
          name: 'Pet Management',
          icon: '🐕',
          importance: 'HIGH',
          importanceNote: 'Pets can be significant parasite source',
          items: [
            'Deworm pets regularly: Every 3-6 months minimum. More often if parasite issues in household.',
            'Test pets for parasites: Annual fecal tests. Pets often asymptomatic carriers.',
            'Wash hands after petting: Especially before eating.',
            'No face licking: Dogs/cats can have parasite eggs on fur and in saliva.',
            'Don\'t let pets sleep in bed during treatment: Can transfer parasites to bedding.',
            'Keep litter box away from kitchen/dining: Toxoplasma in cat feces.',
            'Pregnant women avoid litter boxes: Toxoplasma risk to fetus.',
            'Cover sandboxes: Cats use as litter box. Children playing in contaminated sand = parasite exposure.',
            'Pick up pet waste immediately: Don\'t let sit in yard. Eggs become infectious in days.',
            'Treat all pets in household: Even if only one symptomatic.'
          ]
        },
        {
          name: 'During Treatment: Family-Wide Approach',
          icon: '👨‍👩‍👧‍👦',
          importance: 'CRITICAL',
          importanceNote: 'Prevent ping-pong reinfection',
          items: [
            'Treat everyone in household simultaneously: Even if only one person symptomatic. Prevents ping-pong reinfection.',
            'Treat pets at same time: Deworm all household pets.',
            'Wash all bedding, towels, clothes in hot water: At start of treatment and weekly during. Hot dryer too.',
            'Vacuum daily: HEPA filter. Pinworm eggs airborne. Dispose of bag/empty canister outside immediately.',
            'Wipe down surfaces daily: Doorknobs, toilet handles, light switches, counters. Use disinfectant or dilute bleach.',
            'Clean bathrooms thoroughly: Daily during treatment. Parasites concentrate here.',
            'No sharing food: During treatment, everyone uses own utensils, plates, cups.',
            'Individual towels: Color-coded so not confused.',
            'Change toothbrushes: At start of treatment, end of treatment. Eggs can survive on bristles.',
            'Morning shower routine: Everyone showers first thing to remove eggs laid overnight (pinworms).',
            'Wear tight-fitting underwear at night: Prevents scratching, reduces egg spread.',
            'Trim nails very short: Everyone. Less space for eggs to hide.',
            'Consider sleeping in separate rooms: During treatment if possible, reduces spread.'
          ]
        },
        {
          name: 'Environmental Controls',
          icon: '🏠',
          importance: 'MODERATE-HIGH',
          importanceNote: 'Reduce environmental exposure',
          items: [
            'Shoes off at door: Don\'t track parasite eggs from outside into house.',
            'Outdoor play precautions: No playing in dirt in areas where pets defecate. Wear shoes outside.',
            'Garden gloves: Soil contains parasite eggs. Always glove for gardening.',
            'Wash outdoor toys: If played with in dirt/grass.',
            'Public playgrounds: Sandboxes high-risk (cats use as litter). Wash hands thoroughly after playing.',
            'Swimming precautions: Shower before entering pool (reduces contamination). Don\'t swim if have diarrhea.',
            'Hot yoga studios, gyms: Wipe equipment, wash hands. Warm, moist environment promotes parasite survival on surfaces.'
          ]
        },
        {
          name: 'Support Detoxification',
          icon: '🔄',
          importance: 'HIGH',
          importanceNote: 'During treatment - essential for managing die-off',
          items: [
            'Hydration essential: 1/2 body weight in ounces minimum. Flushes toxins through kidneys.',
            'Daily bowel movements mandatory: Parasites and toxins eliminated through stool. Constipation = reabsorption. Magnesium, vitamin C, fiber as needed.',
            'Coffee enemas (if tolerated): Support liver detoxification, remove toxins. Controversial but effective for some. (Discuss with practitioner.)',
            'Castor oil packs: Over liver 3x week during treatment. Increases lymphatic drainage.',
            'Sauna or hot baths: Sweat out toxins. Epsom salt baths (2 cups, 20 min). If CBS issues, start with 1/4 cup.',
            'Dry brushing: Before shower, toward heart. Stimulates lymphatic drainage.',
            'Gentle exercise: Walking, yoga. Moves lymph, supports elimination. Don\'t overdo during treatment.',
            'Deep breathing: Lungs eliminate toxins. 10 deep breaths several times daily.',
            'Adequate sleep: 9-11 hours children, 8-9 adults. Detoxification and healing happen during sleep.',
            'Minimize additional toxin exposure: No alcohol, minimize processed foods, avoid environmental chemicals.'
          ]
        },
        {
          name: 'Stress Management',
          icon: '🧘',
          importance: 'MODERATE',
          importanceNote: 'Stress impacts immune function and healing',
          items: [
            'Treatment is stressful: Die-off, dietary changes, protocol complexity. Acknowledge difficulty.',
            'Lower expectations temporarily: School, work, activities. Healing priority.',
            'Rest when needed: Fatigue during treatment common. Listen to body.',
            'Stress worsens symptoms: Reduces immune function, slows healing.',
            'Gentle stress management: Deep breathing, meditation, time in nature, gentle yoga.',
            'Support for whole family: Treatment affects everyone. Make sure everyone has support.',
            'Celebrate wins: Protocol completion, symptom improvements. Acknowledge hard work.',
            'Connect with others: Online support groups for parasite cleansing. You\'re not alone.'
          ]
        },
        {
          name: 'Prevention After Treatment',
          icon: '🛡️',
          importance: 'HIGH',
          importanceNote: 'Maintain parasite-free status',
          items: [
            'Maintain good hygiene: Habits learned during treatment.',
            'Periodic maintenance cleanses: Some do gentle herbal protocol 1-2x year as prevention.',
            'Garlic and pumpkin seeds regularly: Food-based parasite prevention.',
            'Probiotics ongoing: Healthy gut resistant to parasites.',
            'Strong digestion: Adequate stomach acid kills many parasites. HCL supplements if needed.',
            'Avoid high-risk exposures: Be mindful of food/water safety.',
            'Test/treat if suspicious symptoms return: Reinfection possible. Don\'t ignore symptoms.',
            'Keep immune system strong: Good nutrition, adequate sleep, stress management, vitamin D.'
          ]
        }
      ]
    },
    diet: {
      title: 'Anti-Parasitic Diet',
      intro: 'Diet is foundation of parasite treatment. Starve parasites, support immune system, optimize gut environment.',
      categories: [
        {
          name: 'Foods That Fight Parasites',
          icon: '✅',
          items: [
            {
              food: 'Raw Garlic (Most Powerful)',
              reason: '1-2 cloves daily, crushed and raw. Antiparasitic, antibacterial, antifungal.',
              examples: 'Mix with honey if can\'t tolerate taste. Most effective raw (cooking destroys allicin). Can add to salad dressing, guacamole.'
            },
            {
              food: 'Pumpkin Seeds',
              reason: '1/4 cup daily, raw and organic. Contains cucurbitacin (paralyzes worms).',
              examples: 'Snack on them, add to smoothies, grind into butter.'
            },
            {
              food: 'Papaya & Papaya Seeds',
              reason: 'Papain enzyme digests parasite protein.',
              examples: 'Eat fresh papaya, save seeds. Dry seeds and grind, or blend fresh. 1 tablespoon seeds daily (spicy/peppery taste—add to smoothies).'
            },
            {
              food: 'Coconut & Coconut Oil',
              reason: 'Medium-chain fatty acids (especially lauric acid) antiparasitic.',
              examples: '2-3 tablespoons coconut oil daily. Use for cooking, add to coffee, eat coconut meat.'
            },
            {
              food: 'Fermented Foods (High Quality)',
              reason: 'Probiotics create hostile environment for parasites.',
              examples: 'Sauerkraut, kimchi, kefir (if tolerate dairy), kombucha (low sugar). Start small if not used to fermented foods.'
            },
            {
              food: 'Onions & Leeks',
              reason: 'Sulfur-containing foods antiparasitic.',
              examples: 'Raw more effective but cooked still beneficial. Add liberally to meals.'
            },
            {
              food: 'Pineapple',
              reason: 'Contains bromelain enzyme. Antiparasitic, anti-inflammatory.',
              examples: 'Fresh pineapple (not canned). Core especially high in bromelain.'
            },
            {
              food: 'Carrots',
              reason: 'High fiber scrubs intestinal walls, removes parasites mechanically. Vitamin A supports immune system.',
              examples: 'Raw carrot salad daily helpful (grate carrots, add coconut oil, apple cider vinegar, salt).'
            },
            {
              food: 'Bitter Greens',
              reason: 'Stimulate bile production (bile antiparasitic). Support liver detoxification.',
              examples: 'Dandelion greens, arugula, radicchio.'
            },
            {
              food: 'Turmeric & Ginger',
              reason: 'Anti-inflammatory, antiparasitic, support digestion.',
              examples: 'Add to meals, make golden milk, ginger tea.'
            },
            {
              food: 'Pomegranate',
              reason: 'Antiparasitic properties.',
              examples: 'Fresh pomegranate or pure juice (no added sugar). Seeds edible.'
            },
            {
              food: 'Bone Broth',
              reason: 'Heals gut lining damaged by parasites. Provides amino acids for immune function.',
              examples: 'Drink daily if possible.'
            },
            {
              food: 'High-Fiber Vegetables',
              reason: 'Fiber sweeps parasites out mechanically.',
              examples: 'Leafy greens, cruciferous vegetables, celery, cucumber. Aim for 7-9 servings vegetables daily.'
            },
            {
              food: 'Herbs & Spices',
              reason: 'All have antiparasitic properties.',
              examples: 'Oregano, thyme, cloves, cinnamon, cayenne (if tolerate). Use liberally in cooking.'
            }
          ]
        },
        {
          name: 'Foods to Avoid (Feed Parasites)',
          icon: '🔴',
          items: [
            {
              food: 'Sugar & Sweeteners (CRITICAL TO ELIMINATE)',
              reason: 'Parasites LOVE sugar. Feed on glucose. Sugar also suppresses immune system.',
              examples: 'White sugar, brown sugar, honey, maple syrup, agave. Candy, cookies, cakes, pastries. Sweetened beverages. Even natural sugars during treatment. Removing sugar often causes die-off on its own.'
            },
            {
              food: 'Refined Carbohydrates',
              reason: 'Quickly convert to sugar in body. Feed parasites.',
              examples: 'White bread, white pasta, white rice, crackers, pretzels, chips, pastries, donuts, muffins, most cereals.'
            },
            {
              food: 'Alcohol',
              reason: 'Converts to sugar, impairs immune function, burdens liver, disrupts gut bacteria.',
              examples: 'Avoid completely during treatment.'
            },
            {
              food: 'Processed Foods',
              reason: 'Additives, preservatives, inflammatory oils. Burden immune system and detoxification.',
              examples: 'Eat whole foods only during treatment.'
            },
            {
              food: 'Pork',
              reason: 'High risk for parasites (trichinosis, tapeworms).',
              examples: 'Avoid during treatment and be very careful always (must be well-cooked).'
            },
            {
              food: 'Raw/Undercooked Meat & Fish',
              reason: 'Reinfection risk.',
              examples: 'No sushi, rare steak, raw eggs during treatment. Cook all animal products thoroughly.'
            },
            {
              food: 'Dairy (for many)',
              reason: 'Increases mucus production (parasites hide in mucus). Inflammatory for many.',
              examples: 'Trial elimination during treatment. Some tolerate fermented dairy (kefir, yogurt).'
            },
            {
              food: 'High-Sugar Fruits (minimize)',
              reason: 'While fruit is healthy, parasites love sugar.',
              examples: 'Bananas, grapes, mangoes, dried fruit. Stick to berries, green apples, papaya, pineapple during treatment. Small portions.'
            },
            {
              food: 'Glutenous Grains',
              reason: 'Inflammatory, can worsen leaky gut.',
              examples: 'Wheat, barley, rye. Many do better avoiding during treatment.'
            }
          ]
        }
      ],
      sampleDay: {
        title: 'Sample Day of Anti-Parasitic Eating',
        meals: [
          { name: 'Upon Waking', description: 'Large glass of filtered water with fresh lemon juice. 1-2 cloves crushed raw garlic with honey (if can tolerate).' },
          { name: 'Breakfast', description: 'Smoothie: Spinach, coconut milk, small amount berries, papaya seeds (1 tbsp), coconut oil (1-2 tbsp), pinch cayenne. OR Scrambled eggs with sautéed vegetables (onions, peppers, spinach) in coconut oil.' },
          { name: 'Mid-Morning', description: '1/4 cup raw pumpkin seeds, small amount berries.' },
          { name: 'Lunch', description: 'Large salad: Mixed greens, arugula, raw carrots (grated), cucumber, bell peppers, pumpkin seeds, grilled chicken or wild salmon. Dressing: Olive oil, apple cider vinegar, crushed garlic, herbs.' },
          { name: 'Afternoon', description: 'Raw vegetable sticks (carrots, celery, cucumber) with guacamole (made with lots of garlic), or small amount fresh pineapple.' },
          { name: 'Dinner', description: 'Grass-fed beef or wild fish, roasted vegetables (broccoli, Brussels sprouts, onions) with coconut oil and turmeric, side salad. Bone broth as soup or to sip.' },
          { name: 'Evening', description: 'Herbal tea (ginger, peppermint). Small serving sauerkraut or kimchi (probiotic).' }
        ]
      },
      tips: [
        'Zero tolerance for sugar during treatment: Non-negotiable. Feeds parasites.',
        'Eat antiparasitic foods daily: Garlic, pumpkin seeds, coconut oil, papaya.',
        'High fiber essential: 7-9 servings vegetables. Sweeps parasites out.',
        'Stay hydrated: Water helps flush dead parasites and toxins.',
        'Organic critical: Pesticides impair immune system and detoxification.',
        'Quality protein: Grass-fed meat, wild fish, pasture-raised eggs. Immune system needs protein.',
        'Healthy fats: Coconut oil, olive oil, avocado. Don\'t fear fat.',
        'Fermented foods: If tolerate, excellent for crowding out parasites with good bacteria.',
        'Bone broth daily if possible: Heals gut, supports immune system.',
        'May need strict for 8-12 weeks: During active treatment. Can slowly reintroduce some foods after.',
        'Track responses: Note which foods make symptoms better/worse.',
        'Meal prep helps: Having safe foods ready prevents "emergency" bad choices when hungry.'
      ]
    }
  }
};
